// DispatchDeck - Accounts Payable (AP) Module
// Vendor invoice management with costEntry auto-sync for BI P&L
console.log('[AP] Module loaded — v1.14.944');

import {
  db,
  storage,
  functions,
  httpsCallable,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from '../firebase-config.js?v=1868';

// runTransaction not (yet) re-exported from firebase-config.js; pull it
// directly so this module doesn't force a firebase-config cache-buster
// rebump across the entire app.
import { runTransaction } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';

import { processPhoto, generateThumbnail, validateImageFile } from '../utils/imageProcessor.js?v=1955';
import { toDrawablePhoto, photoErrorMessage, createPhotoPrepTracker, settlePhotoPrepBeforeSave } from '../utils/heicConvert.js?v=3';
import { previewAndPrintCheck } from '../utils/checkPrinter.js?v=3';

// ============================================
// STATE
// ============================================
const APState = {
  tenantId: null,
  invoices: [],
  vendors: [],        // CRM customers with type='vendor'|'subcontractor'
  jobs: [],
  assets: [],         // trucks + trailers + assets
  people: [],         // roster people for person-linking on line items
  categories: [],     // from settings.customTypes.apCategories
  filter: 'all',      // 'all' | 'open' | 'paid' | 'overdue' | 'checks' | 'driverExpenses' | 'companyExpenses'
  // TASK-604: admin-recorded expenses (source:'admin', already approved). Kept in
  // a SEPARATE array + subscription from driverExpenses so the driver reimbursement
  // review queue is untouched (mixing pre-approved admin costs in there buries
  // pending driver claims — UX/Domain/Code reviewers unanimous). Surfaced via the
  // "Company Expenses" pill as a flat read-only ledger.
  companyExpenses: [],
  _companyExpensesLoaded: false,
  searchQuery: '',
  expandedVendors: {},
  unsubscribers: [],
  // Modal state
  editingInvoice: null,
  // TASK-398: stale-async-write guard. saveNewVendor, after `await addDoc(...)`,
  // schedules a setTimeout(500) that writes the new vendor id into the bill form's
  // #ap-vendor <select>. If the user closes the bill + opens a DIFFERENT bill within
  // that 500ms window, the stale write stamps the new vendor onto the OTHER bill's
  // vendor field; saving then routes the bill to the wrong vendor (financial impact).
  // invoiceFormGen is bumped on every bill-form open/close; saveNewVendor captures it
  // before the await and aborts the delayed select-write if it moved.
  invoiceFormGen: 0,
  _vendorSelectTimer: null,
  pendingDocFiles: [],
  deletedDocIds: [],
  lineItems: [],      // current modal line items
  dirty: false,
  // Driver expense state (subset of costEntries with source:'driver')
  driverExpenses: [],
  expandedDriverGroups: {},
  // Selection set for bulk Approve / etc. (set of costEntryIds)
  driverExpenseSelection: new Set(),
  // Cost allocations — used for empty-state "Setup needed" CTA in driver expenses panel
  costAllocations: [],
  // Fleet counts — lazily fetched once for the "Setup needed" CTA copy.
  _fleetCounts: { trucks: 0, trailers: 0, assets: 0, _loaded: false, _loading: false },
  // TASK-368 pattern (2026-05-28) — distinguish "loading" from "empty" so we
  // don't flash "No invoices found" / "Setup needed" / "No driver expenses yet"
  // before the first onSnapshot lands. Flipped true in both success + error
  // callbacks; reset to false on view-init so subsequent opens re-show the skeleton.
  _invoicesLoaded: false,
  _driverExpensesLoaded: false,
  _costAllocationsLoaded: false,
};

const DEFAULT_AP_CATEGORIES = [
  // TASK-10172-A: Equipment Purchase / Loan Payment take SYSTEM_AP_CATEGORIES' slugs
  // (equipment_purchase / loan_payment) in _defaultApCats; the rest = first word.
  // TASK-9724 S3: 'Supplies / PPE' — slug = first word ('supplies'; BI label "Supplies / PPE"; opex, not COGS)
  'Fuel', 'Tolls', 'Lodging', 'Materials', 'Supplies / PPE', 'Permits', 'Insurance', 'Shop', 'Other',
  'Equipment Purchase', 'Loan Payment'
];
// TASK-10172-A — buying a unit / paying its loan is never an operating expense
// (PnlStatement routes these two slugs below the statement). Offered on every
// tenant's list, including a custom one saved before these existed.
const SYSTEM_AP_CATEGORIES = [{ label: 'Equipment Purchase', slug: 'equipment_purchase' }, { label: 'Loan Payment', slug: 'loan_payment' }];
const _apCatKey = (c) => String(typeof c === 'string' ? c : (c && (c.slug || c.label)) || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
function _withSystemApCategories(list) {
  const have = new Set((list || []).map(_apCatKey));
  return (list || []).concat(SYSTEM_AP_CATEGORIES.filter(c => !have.has(c.slug)));
}

function _defaultApCats() {
  return DEFAULT_AP_CATEGORIES.map(c => ({ label: c, slug: /^(Equipment Purchase|Loan Payment)$/.test(c) ? c.toLowerCase().replace(' ', '_') : c.toLowerCase().split(' ')[0] })); // TASK-10172-A: = SYSTEM_AP_CATEGORIES slugs
}

// TASK-9724 S3 — "Receive into stock" (lazy views/apStockIn.js). Categories that
// are never goods on a shelf; MIRROR of NOT_GOODS in
// functions/src/services/supplyCostReceive.js (tests/task9724-s3-*.unit.cjs pins
// they match). The server decides; this copy only hides the button.
const _STOCKIN_NOT_GOODS = /^(fuel|def|tolls|meals|lodging|per_diem|labor|permits|insurance|subcontractor|interest|taxes|bank_fees|benefits|penalties|fines|draws|contributions|other_income|dot_compliance|equipment_purchase|loan_payment)$/; // + TASK-10172-A capital (integ/2129)
const _stockInCategoryOk = (c) => { const x = String(c || '').trim().toLowerCase().replace(/\s+/g, '_'); return !!x && !_STOCKIN_NOT_GOODS.test(x); };
// Receiving moves stock (roster | hr) AND reads what the purchase cost (owner /
// bi / billing — every AP login already passes that). The server re-checks both.
function _canStockIn() {
  const cm = window.checkModuleAccess;
  return !APState.receiptsOnly && window.canEditMoney?.() !== false && !!cm && (cm('roster', true) === true || cm('hr', true) === true);
}
// The lookup + sheet live in the lazy module (cold-load budget).
function openStockIn(kind, sourceId, lineId) {
  __ddChunkWrap(import('./apStockIn.js?v=9724303')).then(m => m.openFromAp(APState, kind, sourceId, lineId))
    .catch(() => window.showToast?.('Could not open Receive into stock. Try again.', 'error'));
}
function _stockInExpenseBtn(e, jsId) {
  if (!e || e.status !== 'approved' || _personalOf(e) || !_stockInCategoryOk(e.category) || !_canStockIn()) return '';
  return _stockInBtn(`'expense','${jsId}'`, 'Receive into stock', 'de-btn');
}
// COD r4 — the bill line keeps a compact btn-icon chip (a .de-btn is flex:1 on phones and crowds the nowrap line row).
const _stockInBtn = (args, label, cls) => `<button type="button" class="${cls}" onclick="window.APModule.openStockIn(${args})" title="Put it on the shelf (no second cost)"><span class="material-icons">inventory_2</span>${label}</button>`;
function _stockInLineBtn(li) {
  const inv = APState.editingInvoice;
  // COD r1 — never on a Draft (not a cost yet) or a statement bill paying crew
  // receipts (each receipt is received from its expense). The server refuses both.
  if (!inv || !inv.id || !li || !li.id || inv.draft === true || (Array.isArray(inv.chargeReceiptIds) && inv.chargeReceiptIds.length)) return '';
  const saved = (inv.lineItems || []).find(l => l && String(l.id) === String(li.id));
  if (!saved || !(saved.total > 0) || !_stockInCategoryOk(saved.category) || !_canStockIn()) return '';
  const j = window.escJsAttr || String;
  return _stockInBtn(`'ap_line','${j(inv.id)}','${j(String(li.id))}'`, 'Stock', 'btn-icon" style="width:auto;min-height:44px;padding:0 6px;font-size:0.8rem;font-weight:600;color:var(--text-primary)');
}

// ============================================
// TASK-9367 — vendor refunds / credits ("Refund / Credit" — never "credit memo"
// on screen). A credit is a costEntry with a NEGATIVE amount + entryType:'credit'
// written only by the createVendorCreditAdmin callable. When it reverses an
// UNPAID AP bill the callable also raises apInvoices.creditApplied, so:
//   owed (open)      = grandTotal − paidAmount − creditApplied   (never < 0)
//   a printed check  = the open balance at print time, stamped as checkAmount
//   cash paid        = paidAmount (legacy paid bill without it: net of credits)
// These helpers are the ONE place the AP screen does that arithmetic (in cents).
// They mirror functions/src/services/vendorCredits.js (openBalance / netOwed /
// recordedPayment / recordedCheckAmount) and tests/task9367-vendor-credits.unit.cjs
// runs both against the same hand-worked bills so they cannot drift.
// ============================================
// TASK-9537 — bill already PUSHED to QuickBooks (qboPushBill refuses a second push).
// Mirrors pushedQboBillId in functions/src/services/qboInvoicePushGuard.js: a real
// qboBillId, or synced outside the sandbox stub. A STUB_ id / qboSandbox is NOT a push.
function _apQboPushedBillId(inv) {
  if (!inv || typeof inv !== 'object') return null;
  const id = inv.qboBillId == null ? '' : String(inv.qboBillId).trim();
  if (id && !id.startsWith('STUB_')) return id;
  if (inv.qboSynced === true && inv.qboSandbox !== true && !id.startsWith('STUB_')) return '(unknown id)';
  return null;
}
// TASK-9589 S1 residue — the bill card's QuickBooks push button follows Billing's
// LIVE answer (getQBOConnectionTruth: true / false / null), not the cached
// tenantData.qboConnected a stale tab can still hold. With no live answer yet it
// asks for one (once per load) and falls back to the cache for this ACTION button
// only — the server refuses a push when QuickBooks is not connected. Same rule as
// the ticket view's Sync button in billing.js.
let _apQboLiveKicked = false;
function _apQboConnected() {
  const bm = window.BillingModule;
  const live = bm && typeof bm.getQBOConnectionTruth === 'function' ? bm.getQBOConnectionTruth() : null;
  if (live === true || live === false) return live;
  const cached = !!window.AppState?.tenantData?.qboConnected;
  if (!_apQboLiveKicked && bm && typeof bm.refreshQBOStatus === 'function') {
    _apQboLiveKicked = true;
    // COD r1 (Code/Domain/Blast): Billing's refresh repaints only its own panel —
    // repaint the bill list when the live answer lands and differs from the cache
    // this paint used, same tenant only. A failed check keeps the cache (the
    // server still refuses a push when QuickBooks is not connected).
    const tid = APState.tenantId;
    try {
      Promise.resolve(bm.refreshQBOStatus()).then(() => {
        if (APState.tenantId !== tid) return;
        const now = typeof bm.getQBOConnectionTruth === 'function' ? bm.getQBOConnectionTruth() : null;
        if ((now === true || now === false) && now !== cached) renderInvoiceList();
      }).catch(() => {});
    } catch (_) { /* hint only */ }
  }
  return cached;
}
function _apNum(v) { return typeof v === 'number' && Number.isFinite(v) ? v : 0; }
function _apCents(n) { return Math.round(_apNum(n) * 100); }
function _apCreditApplied(inv) { return _apNum(inv?.creditApplied); }
function _apOwed(inv) {
  return Math.max(0, _apCents(inv?.grandTotal) - _apCents(inv?.paidAmount) - _apCents(inv?.creditApplied)) / 100;
}
function _apNetOfCredits(inv) {
  return Math.max(0, _apCents(inv?.grandTotal) - _apCents(inv?.creditApplied)) / 100;
}
// Cash actually paid on the bill (what the .iif payment / QBO payment / Paid
// summary report). Never grandTotal: a credit applied to the bill was not paid.
function _apPaidAmount(inv) {
  if (_apNum(inv?.paidAmount) > 0) return _apCents(inv.paidAmount) / 100;
  return inv?.paymentStatus === 'paid' ? _apNetOfCredits(inv) : 0;
}
// The amount ON the check (stamped at print time). A check that predates the
// stamp keeps its HISTORICAL amount — what the printer of its day printed:
// grandTotal (− creditApplied, frozen once paid) — never re-derived from
// paidAmount, so a later partial edit can't change what a reprint says.
function _apCheckAmount(inv) {
  const c = inv?.checkAmount;
  if (typeof c === 'number' && Number.isFinite(c) && c >= 0) return _apCents(c) / 100;
  return _apNetOfCredits(inv);
}
// TASK-9367 r2 — every row of the check register: the bill's ACTIVE check (if
// any) plus every VOIDED check filed in its checkHistory by the server void,
// each with its own number and amount. A pre-r2 client-side void (checkNumber
// still on the bill + checkVoided:true) is a VOID row until the next print
// files it into history.
//
// One row per PHYSICAL check: a batch check ($150 paying $100 + $50) carries
// one checkAllocationId across its bills, so those bills fold into ONE row
// with the summed amount and every bill's reference. A check without an
// allocation id (hand-typed, or printed before TASK-9338) stays one row per
// bill — never grouped by number + vendor, the rule reprint follows too (an
// admin can reset the counter, so two real checks can share a number).
function _apCheckRows(invoices) {
  const groups = new Map();
  const add = (key, seed, inv, cents) => {
    let g = groups.get(key);
    if (!g) { g = { ...seed, inv, invoices: [], _cents: 0 }; groups.set(key, g); }
    g.invoices.push(inv);
    g._cents += cents;
  };
  for (const inv of invoices || []) {
    if (inv.checkNumber) {
      const voided = inv.checkVoided === true;
      const num = String(inv.checkNumber);
      const key = inv.checkAllocationId && !voided ? `a|${inv.checkAllocationId}|${num}` : `i|${inv.id}|${num}|${voided ? 'v' : 'a'}`;
      add(key, {
        checkNumber: num, voided, active: !voided,
        date: inv.checkPrintedAt?.toDate ? inv.checkPrintedAt.toDate() : (inv.paidAt?.toDate ? inv.paidAt.toDate() : null),
      }, inv, _apCents(_apCheckAmount(inv)));
    }
    (Array.isArray(inv.checkHistory) ? inv.checkHistory : []).forEach((h, idx) => {
      if (!h || !h.checkNumber) return;
      const num = String(h.checkNumber);
      const key = h.checkAllocationId ? `h|${h.checkAllocationId}|${num}` : `hi|${inv.id}|${num}|${idx}`;
      add(key, {
        checkNumber: num, voided: true, active: false,
        date: h.printedAt?.toDate ? h.printedAt.toDate() : (h.voidedAt?.toDate ? h.voidedAt.toDate() : null),
      }, inv, _apCents(h.amount));
    });
  }
  return [...groups.values()].map(g => {
    const refs = g.invoices.map(i => i.vendorInvoiceNumber).filter(Boolean);
    return {
      inv: g.inv, invoices: g.invoices, checkNumber: g.checkNumber, voided: g.voided, active: g.active, date: g.date,
      amount: g._cents / 100,
      ref: g.invoices.length > 1 ? (refs.length ? refs.join(', ') : `${g.invoices.length} bills`) : (refs[0] || ''),
    };
  });
}
// An unpaid bill that credits have fully covered — nothing left to pay.
function _apSettledByCredit(inv) {
  return inv?.paymentStatus !== 'paid' && _apCreditApplied(inv) > 0 && _apOwed(inv) <= 0;
}
function _isCreditEntry(e) { return e?.entryType === 'credit'; }
function _fmtSignedMoney(n) {
  const v = Number(n) || 0;
  return (v < 0 ? '−$' : '$') + Math.abs(v).toFixed(2);
}
// Σ already credited against a costEntry id, from the Company Expenses snapshot
// (every credit is source:'admin', so all of them are in that list).
// TASK-9947: a REJECTED refund never happened (the server's cap skips it too).
function _creditedAgainst(costEntryId) {
  return _creditsOf(costEntryId)
    .filter(e => e.status !== 'rejected')
    .reduce((s, e) => s + Math.abs(_apCents(e.amount)), 0) / 100;
}
// TASK-9947 — refund-aware credits. A credit written since refund approvals
// carries `refundTo` and is PENDING until a second billing login approves it
// (decideExpenseRefund). These mirror services/vendorCredits.js
// (isRecognizedCredit / summarizeCredits / reimbursementNetCents) — one rule
// for AP, BI and the payout.
function _creditsOf(costEntryId) {
  return (APState.companyExpenses || []).filter(e => _isCreditEntry(e) && e.creditOf === costEntryId);
}
function _isNewRefund(c) { return !!c && Object.prototype.hasOwnProperty.call(c, 'refundTo'); }
function _refundCounts(c) {
  if (!_isCreditEntry(c)) return true;
  if (!_isNewRefund(c)) return c.status !== 'rejected';
  return c.status === 'approved' && c.originalApproved !== false;
}
function _employeeRefundCents(costEntryId, status) {
  return _creditsOf(costEntryId)
    .filter(c => c.refundTo === 'employee' && c.status === status)
    .reduce((s, c) => s + Math.abs(_apCents(c.amount)), 0);
}
// What the reimbursement still pays: the expense less APPROVED refunds that
// went back to the employee (never below 0).
function _reimbNet(e) {
  if (!e) return 0;
  return Math.max(0, _apCents(e.amount) - _employeeRefundCents(e.id, 'approved')) / 100;
}
const _REFUND_TO_LABEL = { employee: 'Back to the employee', company_card: 'Back to the card', company: 'To the company', store_credit: 'Store credit with the vendor' }; // store_credit: TASK-9948

// ============================================
// INIT / CLEANUP
// ============================================
export function initAP(container, tenantId) {
  console.log('[AP] Initializing for tenant:', tenantId);

  if (!tenantId) {
    container.innerHTML = `
      <div class="empty-state">
        <span class="material-icons empty-state-icon">warning</span>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">Unable to load AP. Please try logging out and back in.</div>
      </div>`;
    return () => {};
  }

  APState.tenantId = tenantId;
  APState.receiptsOnly = false; // TASK-9778 — the full AP view
  APState.personalExtra = []; // TASK-9917 s4 — personal charges AP's two lists don't carry (imports, BI costs)
  APState.ownerDrawRequests = []; // TASK-9917 — owner-draw requests waiting for the owner (ownerDrawRequest list)
  APState._odrRemindedFor = null; // TASK-9917 expiry follow-up — the overdue toast, once per AP visit
  APState.filter = 'all';
  APState.searchQuery = '';
  APState.expandedVendors = {};
  // TASK-368 pattern — reset _loaded flags so the skeleton shows during the
  // first-snapshot window instead of flashing a misleading empty-state.
  APState._invoicesLoaded = false;
  APState._driverExpensesLoaded = false;
  APState._costAllocationsLoaded = false;
  // TASK-4606 — the fleet cache is TENANT-SCOPED and was never cleared here.
  // On an admin/overlord tenant switch without a full reload, tenant A's asset
  // names would have been diffed against tenant B's allocations and named in
  // B's drift banner — one customer's equipment listed on another's screen
  // (COD Domain lane). Cleared with the rest of the per-tenant state.
  APState._fleetCounts = { trucks: 0, trailers: 0, assets: 0, assetRefs: [], _loaded: false, _loading: false };

  container.innerHTML = getAPHTML();
  subscribeToData(tenantId);
  renderAPView();
  fetchBankBalance();

  return () => {
    APState.unsubscribers.forEach(u => u());
    APState.unsubscribers = [];
    // TASK-9793 — the CRM vendor form we may have mounted on <body> leaves
    // with the view (window.CRMModule.* only, never an import — Pitfall #1).
    window.CRMModule?.removeVendorEditorHost?.();
  };
}

// ============================================
// TASK-9778 — RECEIPTS & EXPENSES VIEW (#/receipts)
// ============================================
// The narrow "Receipts & Expenses" module: the receipt inbox (receipts.js) and
// the submitted driver / staff expense review queue, and NOTHING else of AP —
// no bills, vendors, jobs, bank balance, checks, company expenses or credits.
// It subscribes ONLY to what the firestore.rules `receipts` arms allow:
//   costEntries where source in ['driver','staff'] (document-scoped read arm),
//   costAllocations, trucks, trailers (canReadTenant — every member).
// Approve / reject / ask driver / re-allocate go through the callables, which
// admit the receipts module (_expenseReviewerGate / reallocateExpense).
function _receiptsCanChange() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  const ma = st.memberAccess;
  if (!ma) return true; // legacy single-login owner
  return ma.viewUploadOnly !== true && ma.readOnly !== true;
}

export function initReceiptsView(container, tenantId) {
  if (!tenantId) {
    container.innerHTML = `
      <div class="empty-state">
        <span class="material-icons empty-state-icon">warning</span>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">Unable to load receipts. Please try logging out and back in.</div>
      </div>`;
    return () => {};
  }
  APState.unsubscribers.forEach(u => { try { u(); } catch (_) { /* ignore */ } });
  APState.unsubscribers = [];
  APState.tenantId = tenantId;
  APState.receiptsOnly = true;
  APState.filter = 'driverExpenses';
  APState.searchQuery = '';
  APState.invoices = [];
  APState.vendors = [];
  APState.jobs = [];
  APState.people = [];
  APState.assets = [];
  APState.companyExpenses = [];
  APState.personalExtra = []; // TASK-9917 s4 — never subscribed in the receipts view (rules deny the list)
  APState.ownerDrawRequests = APState.ownerDrawNotices = [];
  APState._odrAt = 0; // an owner-draw list still loading for the full AP view is dropped
  APState.driverExpenses = [];
  APState.costAllocations = [];
  APState.driverExpenseSelection = new Set();
  APState._driverExpensesLoaded = false;
  APState._costAllocationsLoaded = false;
  APState._fleetCounts = { trucks: 0, trailers: 0, assets: 0, assetRefs: [], _loaded: false, _loading: false };

  container.innerHTML = `
    <div class="ap-view">
      <div style="text-align:center;margin-bottom:var(--spacing-sm);">
        <h1 style="font-size:1.4rem;margin:0;font-weight:700;color:var(--text-primary);">Receipts &amp; Expenses</h1>
        <div style="font-size:0.9rem;color:var(--text-secondary);margin-top:4px;">Upload receipts, check what the reader filled in, match them to a truck, work order or job, and review the expenses your team turned in.</div>
      </div>
      <div class="ap-nav-bar">
        <div class="ap-nav-inner">
          <div class="ap-nav-actions">
            <div class="ap-search-wrap">
              <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">search</span>
              <input type="text" id="ap-search" placeholder="Search expenses..." autocomplete="off"
                oninput="window.APModule.onSearch(this.value)">
            </div>
            <button class="btn" type="button" onclick="window.APModule.openReceiptInbox()" title="Drop a stack of receipt photos or PDFs — one record each, you confirm the values. Search every receipt by PO, VIN, truck, vendor or part number here too." style="background:#D97706;color:#fff;border:none;white-space:nowrap;padding:9px 16px;min-height:44px;font-size:14px;font-weight:600;border-radius:8px;display:inline-flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">upload_file</span> Upload receipts
            </button>
            <button class="btn btn-secondary" type="button" onclick="window.APModule.openReceiptInbox({ search: true })" title="Find any receipt by PO, VIN, truck, vendor or part number, and see the warranty printed on a part" style="white-space:nowrap;min-height:44px;display:inline-flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">manage_search</span> Find a receipt
            </button>
          </div>
        </div>
      </div>
      <div id="ap-content-area">
        <div class="loading-state"><div class="loading-spinner"></div><p>Loading...</p></div>
      </div>
    </div>`;
  _subscribeReceiptsViewData(tenantId);
  renderAPView();

  return () => {
    APState.unsubscribers.forEach(u => { try { u(); } catch (_) { /* ignore */ } });
    APState.unsubscribers = [];
    APState.receiptsOnly = false;
  };
}

function _subscribeReceiptsViewData(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;
  // The source filter is REQUIRED: the receipts read arm is document-scoped,
  // and a list query the rules cannot prove in-scope is denied whole.
  const expQ = query(collection(db, 'tenants', tenant, 'costEntries'), where('source', 'in', ['driver', 'staff']));
  APState.unsubscribers.push(onSnapshot(expQ, snap => {
    APState.driverExpenses = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const ta = a.submittedAt?.toMillis ? a.submittedAt.toMillis() : 0;
        const tb = b.submittedAt?.toMillis ? b.submittedAt.toMillis() : 0;
        return tb - ta;
      });
    APState._driverExpensesLoaded = true;
    renderAPView();
  }, err => {
    console.warn('[Receipts] expense subscription failed:', err?.message);
    APState.driverExpenses = [];
    APState._driverExpensesLoaded = true;
    renderAPView();
  }));
  APState.unsubscribers.push(onSnapshot(query(collection(db, 'tenants', tenant, 'costAllocations')), snap => {
    APState.costAllocations = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    APState._costAllocationsLoaded = true;
    renderAPView();
  }, err => {
    console.warn('[Receipts] cost allocations subscription failed:', err?.message);
    APState.costAllocations = [];
    APState._costAllocationsLoaded = true;
    renderAPView();
  }));
  // Trucks + trailers for the Re-allocate vehicle picker (canReadTenant).
  APState.unsubscribers.push(onSnapshot(query(collection(db, 'tenants', tenant, 'trucks')), snap => {
    const trucks = snap.docs.map(d => ({ id: d.id, ...d.data(), _assetType: 'truck' }));
    APState.assets = [...trucks, ...APState.assets.filter(a => a._assetType !== 'truck')];
  }, err => console.warn('[Receipts] trucks subscription failed:', err?.message)));
  APState.unsubscribers.push(onSnapshot(query(collection(db, 'tenants', tenant, 'trailers')), snap => {
    const trailers = snap.docs.map(d => ({ id: d.id, ...d.data(), _assetType: 'trailer' }));
    APState.assets = [...APState.assets.filter(a => a._assetType !== 'trailer'), ...trailers];
  }, err => console.warn('[Receipts] trailers subscription failed:', err?.message)));
}

// ============================================
// DATA SUBSCRIPTIONS
// ============================================
function subscribeToData(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;

  // AP Invoices
  const invQ = query(collection(db, 'tenants', tenant, 'apInvoices'), orderBy('invoiceDate', 'desc'));
  const invUnsub = onSnapshot(invQ, snap => {
    APState.invoices = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // TASK-368 pattern — flip _invoicesLoaded so the skeleton clears even when
    // the snapshot is genuinely empty (new tenant, no invoices yet).
    APState._invoicesLoaded = true;
    renderAPView();
  }, err => {
    // TASK-368 pattern — flip _invoicesLoaded on error too so the skeleton
    // doesn't stick forever on permission-denied / network-permanent failure.
    console.error('[AP] Invoice error:', err);
    APState.invoices = [];
    APState._invoicesLoaded = true;
    if (typeof window.showToast === 'function') {
      window.showToast('Error loading invoices — check your connection and refresh.', 'error');
    }
    renderAPView();
  });
  APState.unsubscribers.push(invUnsub);

  // Vendors from CRM customers
  const custQ = query(collection(db, 'tenants', tenant, 'customers'));
  const custUnsub = onSnapshot(custQ, snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // TASK-9684 — the ONE vendor-like rule (case/space-insensitive), not a local copy.
    APState.vendors = all.filter(c => window.BillableCustomer.isVendorLikeCustomer(c));
  }, err => { console.error('[AP] Vendor error:', err); APState.vendors = []; });
  APState.unsubscribers.push(custUnsub);

  // Jobs
  const jobQ = query(collection(db, 'tenants', tenant, 'jobs'), orderBy('createdAt', 'desc'));
  const jobUnsub = onSnapshot(jobQ, snap => {
    APState.jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }, err => { console.error('[AP] Jobs error:', err); APState.jobs = []; });
  APState.unsubscribers.push(jobUnsub);

  // Trucks + trailers + assets (for assignment dropdown)
  const truckQ = query(collection(db, 'tenants', tenant, 'trucks'));
  const truckUnsub = onSnapshot(truckQ, snap => {
    const trucks = snap.docs.map(d => ({ id: d.id, ...d.data(), _assetType: 'truck' }));
    const trailers = APState.assets.filter(a => a._assetType !== 'truck');
    APState.assets = [...trucks, ...trailers];
  });
  APState.unsubscribers.push(truckUnsub);

  const trailerQ = query(collection(db, 'tenants', tenant, 'trailers'));
  const trailerUnsub = onSnapshot(trailerQ, snap => {
    const trailers = snap.docs.map(d => ({ id: d.id, ...d.data(), _assetType: 'trailer' }));
    const rest = APState.assets.filter(a => a._assetType !== 'trailer');
    APState.assets = [...rest, ...trailers];
  });
  APState.unsubscribers.push(trailerUnsub);

  // People from roster (for person-linking on line items)
  const peopleQ = query(collection(db, 'tenants', tenant, 'people'));
  const peopleUnsub = onSnapshot(peopleQ, snap => {
    APState.people = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(p => p.active !== false)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, err => { console.error('[AP] People error:', err); APState.people = []; });
  APState.unsubscribers.push(peopleUnsub);

  // Driver-submitted expenses — driver expense submission v1 (2026-05-12).
  // Pulls driver- AND staff-source costEntries; client filters status for the pill.
  // TASK-527: staff source = signed-in non-admin members who submitted via
  // createMemberExpense (status:'pending'). They must be visible + approvable in
  // this same queue, so the subscription now matches source IN ['driver','staff'].
  // costEntries collection rules at firestore.rules:613 allow admin read,
  // so this subscription only runs in admin context (AP is admin-only).
  const expQ = query(
    collection(db, 'tenants', tenant, 'costEntries'),
    where('source', 'in', ['driver', 'staff']),
    orderBy('submittedAt', 'desc')
  );
  const expUnsub = onSnapshot(expQ, snap => {
    APState.driverExpenses = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // TASK-368 pattern — flip _driverExpensesLoaded so "No driver expenses yet"
    // empty-state only renders AFTER first snapshot lands.
    APState._driverExpensesLoaded = true;
    renderAPView();
  }, err => {
    // Most likely cause: composite index missing (the source IN [..] + orderBy
    // submittedAt query may need a new index vs the old single-equality one).
    // Refetch WITHOUT orderBy and sort client-side so staff/driver entries still
    // surface instead of silently vanishing on a fresh/un-indexed tenant.
    console.warn('[AP] Expense subscription warning, falling back to client sort:', err?.message);
    const fbQ = query(
      collection(db, 'tenants', tenant, 'costEntries'),
      where('source', 'in', ['driver', 'staff'])
    );
    const fbUnsub = onSnapshot(fbQ, fbSnap => {
      APState.driverExpenses = fbSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.submittedAt?.toMillis ? a.submittedAt.toMillis() : 0;
          const tb = b.submittedAt?.toMillis ? b.submittedAt.toMillis() : 0;
          return tb - ta;
        });
      APState._driverExpensesLoaded = true;
      renderAPView();
    }, fbErr => {
      console.warn('[AP] Expense fallback subscription failed:', fbErr?.message);
      APState.driverExpenses = [];
      APState._driverExpensesLoaded = true;
      renderAPView();
    });
    APState.unsubscribers.push(fbUnsub);
  });
  APState.unsubscribers.push(expUnsub);

  // Cost allocations — drives the Driver Expenses empty-state "Setup needed" CTA.
  // Live snapshot so that after a Seed, the panel auto-flips from setup-CTA to normal.
  const allocQ = query(collection(db, 'tenants', tenant, 'costAllocations'));
  const allocUnsub = onSnapshot(allocQ, snap => {
    APState.costAllocations = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // TASK-368 pattern — flip _costAllocationsLoaded so "Setup needed" CTA only
    // renders after we confirm costAllocations is genuinely empty.
    APState._costAllocationsLoaded = true;
    // Only re-render if the driver expenses panel is currently open
    if (APState.filter === 'driverExpenses') renderAPView();
  }, err => {
    console.warn('[AP] Cost allocations subscription warning:', err?.message);
    APState.costAllocations = [];
    // TASK-368 pattern — flip on error too.
    APState._costAllocationsLoaded = true;
    if (APState.filter === 'driverExpenses') renderAPView();
  });
  APState.unsubscribers.push(allocUnsub);

  // TASK-604: admin-recorded ("Company Expenses") subscription — source:'admin'
  // costEntries written by the AP Record Expense button (createExpenseAdmin),
  // already status:'approved'. SEPARATE from the driver/staff subscription above
  // so the reimbursement review queue stays clean. Equality-only filter + client
  // sort (no orderBy) so it never needs a composite index / can't silently miss.
  const companyQ = query(
    collection(db, 'tenants', tenant, 'costEntries'),
    where('source', '==', 'admin')
  );
  const companyUnsub = onSnapshot(companyQ, snap => {
    APState.companyExpenses = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const ta = a.date?.toMillis ? a.date.toMillis() : (a.submittedAt?.toMillis ? a.submittedAt.toMillis() : 0);
        const tb = b.date?.toMillis ? b.date.toMillis() : (b.submittedAt?.toMillis ? b.submittedAt.toMillis() : 0);
        return tb - ta;
      });
    APState._companyExpensesLoaded = true;
    // TASK-9947 — refunds live in this list and show under driver rows too (net owed, Approve / Reject).
    if (APState.filter === 'companyExpenses' || APState.filter === 'personalCharges' || APState.filter === 'driverExpenses') renderAPView(); // TASK-9917 (COD r3)
    else renderFilterPills(); // keep the pill count live even when panel closed
  }, err => {
    console.warn('[AP] Company expenses subscription warning:', err?.message);
    APState.companyExpenses = [];
    APState._companyExpensesLoaded = true;
    if (APState.filter === 'companyExpenses') renderAPView();
  });
  APState.unsubscribers.push(companyUnsub);

  // TASK-9917 s4 (a) — personal charges the two lists above never carry:
  // statement / fuel-card imports and costs added in BI, marked Personal from
  // BI → P&L → Charges. They need the Personal Charges panel (repayments, undo)
  // like any other. Equality filter on the server-only marker (single-field
  // index, no composite). The receipts-only view never runs this (its read arm
  // is driver/staff only and a list it can't prove in-scope is denied whole).
  const personalQ = query(
    collection(db, 'tenants', tenant, 'costEntries'),
    where('personalCharge.payer', 'in', ['owner', 'employee'])
  );
  const personalUnsub = onSnapshot(personalQ, snap => {
    APState.personalExtra = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => e.source !== 'admin' && e.source !== 'driver' && e.source !== 'staff');
    if (APState.filter === 'personalCharges') renderAPView();
    else renderFilterPills();
  }, err => {
    console.warn('[AP] Personal charges subscription warning:', err?.message);
    APState.personalExtra = [];
  });
  APState.unsubscribers.push(personalUnsub);
  _loadOwnerDrawRequests(); // TASK-9917 — the owner path (server-only docs, so a callable, not a listener)

  // Load categories from settings
  loadCategories(tenant);
}

async function loadCategories(tenant) {
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant));
    if (snap.exists()) {
      const settings = snap.data().settings || {};
      APState.categories = settings?.customTypes?.apCategories || _defaultApCats();
    }
    if (!APState.categories.length) {
      APState.categories = _defaultApCats();
    }
  } catch (e) {
    console.error('[AP] Load categories error:', e);
    APState.categories = _defaultApCats();
  }
  APState.categories = _withSystemApCategories(APState.categories);
}

// ============================================
// RENDER INTO EXTERNAL CONTAINER (called from billing.js)
// ============================================
function renderAPViewInContainer(container, tenantId) {
  if (!tenantId) return;

  // Initialize data subscriptions if not already done for this tenant
  if (APState.tenantId !== tenantId || !APState.unsubscribers.length) {
    // Cleanup old subs
    APState.unsubscribers.forEach(u => u());
    APState.unsubscribers = [];
    APState.tenantId = tenantId;
    APState.receiptsOnly = false; // TASK-9778
    APState.filter = 'all';
    APState.searchQuery = '';
    APState.expandedVendors = {};
    // TASK-368 pattern — reset _loaded flags before re-subscribing.
    APState._invoicesLoaded = false;
    APState._driverExpensesLoaded = false;
    APState._costAllocationsLoaded = false;
    subscribeToData(tenantId);
  }

  container.innerHTML = getAPContentHTML();
  renderAPView();
}

// ============================================
// MAIN HTML SHELL
// ============================================
function getAPHTML() {
  return `
    <div class="ap-view">
      ${getAPHeaderHTML()}
      ${getAPContentHTML()}
    </div>
    ${getAPModalHTML()}
  `;
}

function getAPHeaderHTML() {
  return `
    <div style="text-align:center;margin-bottom:var(--spacing-sm);">
      <h1 style="font-size:1.4rem;margin:0;font-weight:700;">Accounts Payable</h1>
      <div id="ap-bank-balance-badge" style="margin-top:4px;"></div>
    </div>
    <div class="view-header" style="justify-content:center;">
      <div class="view-actions">
        <button class="btn btn-primary" onclick="window.APModule.showNewInvoice()" style="padding:10px 20px;font-size:15px;font-weight:600;">
          <span class="material-icons" style="font-size:20px;">add</span> New Invoice
        </button>
      </div>
    </div>`;
}

function getAPContentHTML() {
  return `
    <!-- AP centered nav: filter pills + search + new invoice -->
    <div class="ap-nav-bar">
      <div class="ap-nav-inner">
        <div class="view-toggle view-toggle--labeled" id="ap-filter-pills"></div>
        <div class="ap-nav-actions">
          <div class="ap-search-wrap">
            <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">search</span>
            <input type="text" id="ap-search" placeholder="Search..." autocomplete="off"
              oninput="window.APModule.onSearch(this.value)">
          </div>
          <button class="btn btn-primary" onclick="window.APModule.showNewInvoice()" style="white-space:nowrap;padding:9px 16px;font-size:14px;font-weight:600;border-radius:8px;display:inline-flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">add</span> New Invoice
          </button>
          <button class="btn btn-secondary" onclick="window.DocIntake.open({ surface: 'ap' })" style="white-space:nowrap;display:inline-flex;align-items:center;gap:6px;">
            <span class="material-icons">note_add</span> From a bill
          </button>
          <div class="ap-record-split" style="position:relative;display:inline-flex;">
            <button class="btn" onclick="window.APModule.openAdminExpense()" title="Record an expense you (or the company) paid — receipt photo, cost allocation, optional reimbursement flag" style="background:#D97706;color:#fff;border:none;white-space:nowrap;padding:9px 14px 9px 16px;font-size:14px;font-weight:600;border-radius:8px 0 0 8px;display:inline-flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">receipt_long</span> Record Expense
            </button>
            <button class="btn" type="button" aria-label="More ways to record" aria-haspopup="true" onclick="window.APModule.toggleRecordMenu(event)" style="background:#D97706;color:#fff;border:none;border-left:1px solid rgba(255,255,255,0.45);padding:9px 8px;min-width:36px;border-radius:0 8px 8px 0;display:inline-flex;align-items:center;justify-content:center;">
              <span class="material-icons" style="font-size:18px;">expand_more</span>
            </button>
            <div id="ap-record-menu" hidden style="position:absolute;top:calc(100% + 4px);right:0;z-index:50;min-width:220px;background:var(--bg-card);border:1px solid var(--border-light);border-radius:8px;box-shadow:0 8px 24px rgba(15,23,42,0.15);padding:4px;">
              <button type="button" onclick="window.APModule.openVendorCredit()" style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 12px;border:none;background:none;text-align:left;font-size:14px;font-weight:500;color:var(--text-primary);border-radius:6px;cursor:pointer;min-height:44px;">
                <span class="material-icons" style="font-size:18px;color:#059669;">undo</span> Record refund / credit
              </button>
              <button type="button" onclick="window.APModule.openReceiptInbox()" title="Drop a stack of receipt photos or PDFs — one record each, you confirm the values. Search every receipt by PO, VIN, truck, vendor or part number here too." style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 12px;border:none;background:none;text-align:left;font-size:14px;font-weight:500;color:var(--text-primary);border-radius:6px;cursor:pointer;min-height:44px;">
                <span class="material-icons" style="font-size:18px;color:#D97706;">upload_file</span> Upload receipts
              </button>
            </div>
          </div>
          <button class="btn" style="background:#0F172A;color:#fff;border:none;white-space:nowrap;padding:9px 16px;font-size:14px;font-weight:600;border-radius:8px;display:inline-flex;align-items:center;gap:6px;" onclick="window.APModule.exportAPBillsIIF()" title="Export to QuickBooks Desktop">
            <span class="material-icons" style="font-size:18px;">download</span> QB (.iif)
          </button>
          <button class="btn" onclick="window.APModule.showBankSetup()" title="Bank Account Setup" style="background:#0891B2;color:#fff;border:none;white-space:nowrap;padding:9px 16px;font-size:14px;font-weight:600;border-radius:8px;display:inline-flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">account_balance</span> Bank
          </button>
        </div>
      </div>
    </div>

    <!-- Invoice list -->
    <div id="ap-content-area">
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    </div>

    ${getAPModalHTML()}
  `;
}

function getAPModalHTML() {
  return `
    <div class="modal hidden" id="modal-ap-invoice">
      <div class="modal-content modal-large ap-invoice-modal-content" id="ap-invoice-modal-body"></div>
    </div>
    <div class="modal hidden" id="modal-bank-setup">
      <div class="modal-content" style="max-width:520px;" id="bank-setup-modal-body"></div>
    </div>`;
}

// ============================================
// FILTER PILLS + SEARCH
// ============================================
function renderFilterPills() {
  const pillsEl = document.getElementById('ap-filter-pills');
  if (!pillsEl) return;

  const now = new Date();
  // Driver-expense pill count = pending OR clarification-requested driver-submitted
  // expenses; reimbursement-pending count surfaced in sub-badge.
  const pendingDriverCount = (APState.driverExpenses || []).filter(e =>
    e.status === 'pending' || e.status === 'clarification_requested'
  ).length;
  // TASK-1543 — count only APPROVED unpaid reimbursements (_reimbOwed): a
  // rejected claim is not money owed and must not keep the red badge alive.
  const reimbBadge = (APState.driverExpenses || []).filter(_reimbOwed).length;

  const filters = [
    { key: 'all', icon: 'list', label: 'All', count: APState.invoices.length },
    // TASK-9367 — a bill fully covered by refunds / credits counts as settled.
    { key: 'open', icon: 'pending', label: 'Open', count: APState.invoices.filter(i => (i.paymentStatus === 'open' || i.paymentStatus === 'partial') && !_apSettledByCredit(i)).length },
    { key: 'paid', icon: 'payments', label: 'Paid', count: APState.invoices.filter(i => i.paymentStatus === 'paid' || _apSettledByCredit(i)).length },
    { key: 'overdue', icon: 'warning', label: 'Overdue', count: APState.invoices.filter(i => {
      if (i.paymentStatus === 'paid' || _apSettledByCredit(i)) return false;
      const due = i.dueDate?.toDate ? i.dueDate.toDate() : new Date(i.dueDate);
      return due < now;
    }).length },
    { key: 'checks', icon: 'receipt_long', label: 'Checks', count: _apCheckRows(APState.invoices).length },
    { key: 'driverExpenses', icon: 'request_quote', label: 'Driver Expenses', count: pendingDriverCount, accent: reimbBadge > 0 ? `+${reimbBadge} reimburse` : null },
    // TASK-604: admin-recorded company expenses (already approved). Separate pill
    // so the AP Record Expense button's output is findable, not just a BI total.
    { key: 'companyExpenses', icon: 'store', label: 'Company Expenses', count: (APState.companyExpenses || []).length },
    // TASK-9917 — company-card charges marked personal. Shown once there is one.
    // Expiry follow-up: the owner's in-app reminder — the pill turns into a bell once an ask has waited 3+ days.
    // + the asker's lapsed-ask notices (APState.ownerDrawNotices).
    ...((_personalEntries().length || _ownerDrawWaiting().length || _odrNotices().length) ? [{ key: 'personalCharges', icon: (_ownerDrawOverdue().length || _odrNotices().length) ? 'notification_important' : 'person_off', label: 'Personal Charges', count: (_personalEntries().filter(e => _personalOutstanding(e) > 0).length || _personalEntries().length) + _ownerDrawWaiting().length + _odrNotices().length }] : []),
    // TASK-4866 — the company's cards, where the person paying the bills works.
    // The registry has always existed on the tenant doc, but its only editor was
    // BI → Financing → "Debt Accounts" — accounting jargon an office manager
    // never opens — and it had ZERO rows in all 95 tenants as a result.
    { key: 'cards', icon: 'credit_card', label: 'Cards', count: _apCardRegistry().length },
  ];

  pillsEl.innerHTML = filters.map(f => `
    <button class="view-btn ${APState.filter === f.key ? 'active' : ''}"
      onclick="window.APModule.switchFilter('${f.key}')">
      <span class="material-icons">${f.icon}</span>
      <span class="view-btn-label">${f.label}</span>
      <span class="ap-filter-count">${f.count}</span>
      ${f.accent ? `<span class="ap-filter-accent" style="background:#FEE2E2;color:#991B1B;border-radius:999px;padding:2px 8px;font-size:0.7rem;font-weight:700;margin-left:4px;">${f.accent}</span>` : ''}
    </button>
  `).join('');
}

function switchFilter(key) {
  APState.filter = key;
  renderAPView();
}

function onSearch(val) {
  APState.searchQuery = val.trim().toLowerCase();
  renderInvoiceList();
}

// ============================================
// RENDER MAIN VIEW
// ============================================
function renderAPView() {
  // TASK-9778 — the Receipts & Expenses view has no AP pills (bills, checks, cards).
  if (!APState.receiptsOnly) renderFilterPills();
  renderInvoiceList();
}

function renderInvoiceList() {
  const area = document.getElementById('ap-content-area');
  if (!area) return;

  let invoices = getFilteredInvoices();

  // Search
  if (APState.searchQuery) {
    const q = APState.searchQuery;
    invoices = invoices.filter(i =>
      (i.vendorName || '').toLowerCase().includes(q) ||
      (i.vendorInvoiceNumber || '').toLowerCase().includes(q) ||
      (i.poNumber || '').toLowerCase().includes(q) ||
      (i.jobName || '').toLowerCase().includes(q) ||
      (i.notes || '').toLowerCase().includes(q)
    );
  }

  // Check register view
  if (APState.filter === 'checks') {
    renderCheckRegister(area, invoices);
    return;
  }

  // Driver Expenses — grouped-by-driver review surface (2026-05-12)
  if (APState.filter === 'driverExpenses') {
    renderDriverExpensesPanel(area);
    return;
  }

  // TASK-604: Company Expenses — admin-recorded, already-approved costs (flat ledger)
  if (APState.filter === 'companyExpenses') {
    renderCompanyExpensesPanel(area);
    return;
  }

  // TASK-9917: Personal Charges — owed back by employees, and owner draws
  if (APState.filter === 'personalCharges') {
    renderPersonalChargesPanel(area);
    return;
  }

  // TASK-4866: Cards — the tenant's credit / fuel card registry, managed here
  // instead of only under BI → Financing.
  if (APState.filter === 'cards') {
    renderCardsPanel(area);
    return;
  }

  // TASK-368 pattern (2026-05-28) — distinguish "loading" from "no invoices".
  // Pre-fix this rendered "No invoices found" before the first onSnapshot
  // landed, indistinguishable from a real empty pipeline. Same shape as the
  // HR Hiring + billing tickets + roster fixes shipped this morning.
  if (!APState._invoicesLoaded) {
    area.innerHTML = `
      <div class="ap-loading-skeleton" style="margin-top:var(--spacing-lg);display:flex;flex-direction:column;gap:8px;opacity:0.55;">
        ${[1,2,3,4].map(() => `
          <div style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:14px 16px;display:flex;align-items:center;gap:12px;">
            <div style="width:36px;height:36px;border-radius:6px;background:var(--bg-elevated);flex-shrink:0;"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
              <div style="height:14px;width:50%;background:var(--bg-elevated);border-radius:4px;"></div>
              <div style="height:12px;width:30%;background:var(--bg-elevated);border-radius:4px;"></div>
            </div>
          </div>
        `).join('')}
        <div style="text-align:center;color:var(--text-secondary);font-size:0.85rem;margin-top:6px;">Loading invoices…</div>
      </div>
    `;
    return;
  }

  if (!invoices.length) {
    area.innerHTML = `
      <div class="empty-state" style="padding:60px 20px;">
        <span class="material-icons empty-state-icon" style="font-size:56px;color:var(--text-muted);">receipt_long</span>
        <div class="empty-state-title" style="margin-top:12px;">No invoices found</div>
        <div class="empty-state-text">${APState.filter === 'all' && !APState.searchQuery
          ? 'Add your first vendor invoice to start tracking payables.'
          : 'Try adjusting your filters or search.'}</div>
        ${APState.filter === 'all' && !APState.searchQuery ? `
          <button class="btn btn-primary" style="margin-top:16px;padding:10px 20px;font-size:15px;font-weight:600;" onclick="window.APModule.showNewInvoice()">
            <span class="material-icons" style="font-size:20px;">add</span> New Invoice
          </button>` : ''}
      </div>`;
    return;
  }

  // Group by vendor
  const byVendor = {};
  for (const inv of invoices) {
    const vKey = inv.vendorId || '_unknown';
    if (!byVendor[vKey]) byVendor[vKey] = { name: inv.vendorName || 'Unknown Vendor', invoices: [] };
    byVendor[vKey].invoices.push(inv);
  }

  // Sort vendors alphabetically
  const sortedVendors = Object.entries(byVendor).sort((a, b) => a[1].name.localeCompare(b[1].name));

  // Summary bar
  // TASK-9367 — outstanding is net of refunds / credits applied to each bill.
  // TASK-9624 — a Draft bill (Smart Tools, not yet saved) is out of every total.
  const totalOpen = invoices.filter(i => i.paymentStatus !== 'paid' && !i.draft).reduce((s, i) => s + _apOwed(i), 0);
  // Cash actually paid (paidAmount), not the bill total: a credit applied to a
  // bill, or a partial payment, is not money that left the bank.
  const totalPaid = invoices.filter(i => i.paymentStatus === 'paid' && !i.draft).reduce((s, i) => s + _apPaidAmount(i), 0);

  let html = `
    <div class="ap-summary-bar">
      <div class="ap-summary-item">
        <span class="ap-summary-label">Outstanding</span>
        <span class="ap-summary-value ap-summary-open">$${totalOpen.toFixed(2)}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Paid (filtered)</span>
        <span class="ap-summary-value ap-summary-paid">$${totalPaid.toFixed(2)}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Invoices</span>
        <span class="ap-summary-value">${invoices.length}</span>
      </div>
    </div>
  `;

  // Vendor groups
  const canEditVendors = _apCanEditVendors(); // TASK-9793
  for (const [vendorId, group] of sortedVendors) {
    const expanded = APState.expandedVendors[vendorId] !== false; // default expanded
    // TASK-9367 — billed to this vendor NET of refunds / credits applied, plus
    // what is still owed (what a vendor balance actually means to the office).
    const vendorTotal = group.invoices.filter(i => !i.draft).reduce((s, i) => s + _apNetOfCredits(i), 0);
    const vendorOwed = group.invoices.filter(i => i.paymentStatus !== 'paid' && !i.draft).reduce((s, i) => s + _apOwed(i), 0);
    const vendorCredits = group.invoices.reduce((s, i) => s + _apCreditApplied(i), 0);

    html += `
      <div class="ap-vendor-group">
        <div class="ap-vendor-header" onclick="window.APModule.toggleVendor('${vendorId}')">
          <div class="ap-vendor-header-left">
            <span class="material-icons ap-vendor-chevron">${expanded ? 'expand_more' : 'chevron_right'}</span>
            <span class="material-icons" style="color:var(--brand-blue);font-size:20px;">storefront</span>
            <strong>${escapeHtml(group.name)}</strong>
            <span class="ap-vendor-badge">${group.invoices.length}</span>
            ${canEditVendors && vendorId !== '_unknown' && typeof window.escJsAttr === 'function' ? `<button type="button" class="btn-icon ap-vendor-edit-btn" onclick="event.stopPropagation();window.APModule.editVendor('${window.escJsAttr(vendorId)}')" title="Edit vendor" aria-label="Edit vendor ${escapeHtml(group.name)}" style="margin-left:4px;"><span class="material-icons" style="font-size:18px;">edit</span></button>` : ''}
          </div>
          <div class="ap-vendor-total" title="Billed, net of refunds / credits">$${vendorTotal.toFixed(2)}${vendorCredits > 0
            ? `<div style="font-size:0.72rem;font-weight:500;color:var(--text-secondary);">Owed $${vendorOwed.toFixed(2)} · <span style="color:#166534;">credits −$${vendorCredits.toFixed(2)}</span></div>`
            : ''}</div>
        </div>
        ${expanded ? `<div class="ap-vendor-invoices">
          ${group.invoices.map(inv => renderInvoiceCard(inv)).join('')}
        </div>` : ''}
      </div>`;
  }

  area.innerHTML = html;
}

function getFilteredInvoices() {
  const now = new Date();
  switch (APState.filter) {
    // TASK-9367 — same settled-by-credit rule as the pill counts.
    case 'open':
      return APState.invoices.filter(i => (i.paymentStatus === 'open' || i.paymentStatus === 'partial') && !_apSettledByCredit(i));
    case 'paid':
      return APState.invoices.filter(i => i.paymentStatus === 'paid' || _apSettledByCredit(i));
    case 'overdue':
      return APState.invoices.filter(i => {
        if (i.paymentStatus === 'paid' || _apSettledByCredit(i)) return false;
        const due = i.dueDate?.toDate ? i.dueDate.toDate() : new Date(i.dueDate);
        return due < now;
      });
    case 'checks':
      return APState.invoices.filter(i => i.checkNumber || (Array.isArray(i.checkHistory) && i.checkHistory.length));
    default:
      return [...APState.invoices];
  }
}

function renderInvoiceCard(inv) {
  const date = inv.invoiceDate?.toDate ? inv.invoiceDate.toDate() : new Date(inv.invoiceDate);
  const dueDate = inv.dueDate?.toDate ? inv.dueDate.toDate() : (inv.dueDate ? new Date(inv.dueDate) : null);
  const now = new Date();
  const settledByCredit = _apSettledByCredit(inv); // TASK-9367
  const creditApplied = _apCreditApplied(inv);
  const isOverdue = dueDate && inv.paymentStatus !== 'paid' && !settledByCredit && dueDate < now;
  const lineCount = inv.lineItems?.length || 0;

  let statusBadge = '';
  const methodIcons = { check: 'receipt_long', ach: 'account_balance', credit_card: 'credit_card', cash: 'payments', wire: 'swap_horiz' };
  const methodLabels = { check: 'Check', ach: 'ACH', credit_card: 'Card', cash: 'Cash', wire: 'Wire' };

  if (inv.paymentStatus === 'paid') {
    const mIcon = methodIcons[inv.paymentMethod] || 'check_circle';
    const mLabel = inv.paymentMethod ? (inv.checkNumber ? `Check #${escapeHtml(inv.checkNumber)}` : methodLabels[inv.paymentMethod] || 'Paid') : 'Paid';
    statusBadge = `<span class="badge badge-paid"><span class="material-icons" style="font-size:11px;vertical-align:middle;margin-right:2px;">${mIcon}</span>${mLabel}</span>`;
  } else if (settledByCredit) {
    statusBadge = '<span class="badge badge-paid"><span class="material-icons" style="font-size:11px;vertical-align:middle;margin-right:2px;">undo</span>Covered by credit</span>';
  } else if (inv.paymentStatus === 'partial') {
    statusBadge = '<span class="badge badge-partial">Partial</span>';
  } else if (isOverdue) {
    statusBadge = '<span class="badge badge-overdue"><span class="material-icons" style="font-size:11px;vertical-align:middle;margin-right:2px;">warning</span>Overdue</span>';
  } else {
    statusBadge = '<span class="badge badge-open">Open</span>';
  }

  return `
    <div class="ap-invoice-card ${isOverdue ? 'ap-overdue' : ''}" onclick="window.APModule.editInvoice('${inv.id}')">
      <div class="ap-card-header">
        <div class="ap-card-ref">
          ${inv.vendorInvoiceNumber ? `<span class="ap-card-invnum">#${escapeHtml(inv.vendorInvoiceNumber)}</span>` : ''}
          ${inv.poNumber ? `<span class="ap-card-po">${escapeHtml(inv.poNumber)}</span>` : ''}
          ${inv.draft === true ? '<span class="badge badge-partial" title="Open, check and Save to clear Draft">Draft</span>' : ''}
        </div>
        ${statusBadge}
      </div>
      <div class="ap-card-body">
        <div class="ap-card-date">
          <span class="material-icons" style="font-size:14px;">calendar_today</span>
          ${formatDate(date)}
          ${dueDate ? `<span style="color:${isOverdue ? '#DC2626' : 'var(--text-secondary)'};margin-left:8px;">Due ${formatDate(dueDate)}</span>` : ''}
        </div>
        <div class="ap-card-meta">
          ${inv.jobName ? `<span><span class="material-icons" style="font-size:14px;">work</span> ${escapeHtml(inv.jobName)}</span>` : ''}
          ${inv.assetName ? `<span><span class="material-icons" style="font-size:14px;">local_shipping</span> ${escapeHtml(inv.assetName)}</span>` : ''}
          <span>${lineCount} line${lineCount !== 1 ? 's' : ''}</span>
          ${_apChargeLinkedIds(inv).length ? `<span title="Crew charge receipts on this statement are already expenses; this bill's cost counts only the rest."><span class="material-icons" style="font-size:14px;">link</span> Covers ${_apChargeLinkedIds(inv).length} crew receipt${_apChargeLinkedIds(inv).length === 1 ? '' : 's'}, $${((Number(inv.chargeNettedCents) || 0) / 100).toFixed(2)}</span>` : ''}
          ${Number(inv.chargeOverCents) > 0 ? `<span style="color:#B45309;font-weight:600;" title="The crew receipts linked to this statement (plus refunds on it) add up to more than the bill, so this much counts twice. Check the bill total and the receipts.">Check: linked receipts are $${(Number(inv.chargeOverCents) / 100).toFixed(2)} over this bill</span>` : ''}
        </div>
      </div>
      <div class="ap-card-amount">
        <strong>$${(inv.grandTotal || 0).toFixed(2)}</strong>
        ${inv.paymentStatus === 'partial' ? `<span class="ap-card-paid-note">($${(inv.paidAmount || 0).toFixed(2)} paid)</span>` : ''}
        ${creditApplied > 0 ? `<span class="ap-card-paid-note" style="color:#166534;">Credit −$${creditApplied.toFixed(2)}${inv.paymentStatus !== 'paid' ? ` · owes $${_apOwed(inv).toFixed(2)}` : ''}</span>` : ''}
        ${(() => {
          const isPaidByCheck = inv.paymentStatus === 'paid' && inv.paymentMethod === 'check';
          const isUnpaid = inv.paymentStatus !== 'paid' && !settledByCredit;
          if (inv.draft || (!isPaidByCheck && !isUnpaid)) return '';
          const label = isPaidByCheck ? 'Reprint' : 'Print Check';
          const title = isPaidByCheck ? 'Reprint Check' : 'Print check and mark invoice paid';
          return `
            <button class="ap-card-print-btn" onclick="event.stopPropagation();window.APModule.printCheck('${inv.id}')" title="${title}">
              <span class="material-icons" style="font-size:14px;">print</span>
              ${label}
            </button>`;
        })()}
        ${_apQboConnected() && !inv.draft ? (_apQboPushedBillId(inv) ? `
          <span class="ap-card-print-btn ap-qb-pushed" title="In QuickBooks${_apQboPushedBillId(inv) !== '(unknown id)' ? ` (bill id ${escapeHtml(_apQboPushedBillId(inv))})` : ''}. Make changes to that bill in QuickBooks." style="color:#10B981;cursor:default;">
            <span class="material-icons" style="font-size:14px;">cloud_done</span>
            In QB
          </span>
          <button type="button" class="ap-card-print-btn ap-qb-unlock" onclick="event.stopPropagation();window.BillingModule?.unlockVoidedQboPush('bill','${window.escJsAttr ? window.escJsAttr(inv.id) : inv.id}')" title="If this bill was voided or deleted in QuickBooks, DispatchDeck checks QuickBooks and lets you send it again." style="color:#1D4ED8;">
            Voided in QuickBooks? Check &amp; unlock
          </button>` : `
          <button class="ap-card-print-btn" onclick="event.stopPropagation();window.BillingModule?.syncAPToQBO('${inv.id}')" title="Sync to QuickBooks" style="color:#2CA01C;">
            <span class="material-icons" style="font-size:14px;">cloud_upload</span>
            QB
          </button>`) : ''}
        ${(inv.grandTotal || 0) > 0 && !inv.draft ? `
          <button class="ap-card-print-btn" onclick="event.stopPropagation();window.APModule.openVendorCredit('ap_${window.escJsAttr ? window.escJsAttr(inv.id) : inv.id}')" title="Record a refund or credit from this vendor against this bill" style="color:#166534;">
            <span class="material-icons" style="font-size:14px;">undo</span>
            Refund / Credit
          </button>` : ''}
      </div>
    </div>`;
}

// ============================================
// VENDOR ACCORDION
// ============================================
function toggleVendor(vendorId) {
  APState.expandedVendors[vendorId] = APState.expandedVendors[vendorId] === false ? true : false;
  renderInvoiceList();
}

// ============================================
// INVOICE MODAL — NEW / EDIT
// ============================================
function showNewInvoice() {
  // TASK-398: new bill-form generation — invalidate any pending vendor-select write.
  APState.invoiceFormGen++;
  if (APState._vendorSelectTimer) { clearTimeout(APState._vendorSelectTimer); APState._vendorSelectTimer = null; }
  APState.editingInvoice = null;
  APState.pendingDocFiles = [];
  APState.deletedDocIds = [];
  APState.lineItems = [createBlankLineItem()];
  APState.dirty = false;
  renderInvoiceModal();
  document.getElementById('modal-ap-invoice')?.classList.remove('hidden');
}

function editInvoice(invoiceId) {
  const inv = APState.invoices.find(i => i.id === invoiceId);
  if (!inv) { window.showToast?.('Invoice not found', 'error'); return; }

  // TASK-398: new bill-form generation — invalidate any pending vendor-select write
  // queued for a different bill so it can't stamp a vendor onto THIS bill.
  APState.invoiceFormGen++;
  if (APState._vendorSelectTimer) { clearTimeout(APState._vendorSelectTimer); APState._vendorSelectTimer = null; }
  APState.editingInvoice = inv;
  APState.pendingDocFiles = [];
  APState.deletedDocIds = [];
  APState.lineItems = (inv.lineItems || []).map(li => ({ ...li }));
  if (!APState.lineItems.length) APState.lineItems = [createBlankLineItem()];
  APState.dirty = false;
  renderInvoiceModal();
  document.getElementById('modal-ap-invoice')?.classList.remove('hidden');
}

function closeInvoiceModal() {
  // TASK-398: closing the bill form invalidates any pending vendor-select write.
  APState.invoiceFormGen++;
  if (APState._vendorSelectTimer) { clearTimeout(APState._vendorSelectTimer); APState._vendorSelectTimer = null; }
  // Clean up preview URLs
  APState.pendingDocFiles.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
  APState.pendingDocFiles = [];
  APState.editingInvoice = null;
  document.getElementById('modal-ap-invoice')?.classList.add('hidden');
}

function createBlankLineItem() {
  return {
    id: `li_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    category: '',
    description: '',
    quantity: 1,
    rate: 0,
    total: 0,
    personId: '',
    personName: ''
  };
}

function renderInvoiceModal() {
  const body = document.getElementById('ap-invoice-modal-body');
  if (!body) return;

  const inv = APState.editingInvoice;
  const isEdit = !!inv;
  const title = isEdit ? 'Edit AP Invoice' : 'New AP Invoice';

  // Pre-fill values
  const vendorId = inv?.vendorId || '';
  const vendorInvoiceNumber = inv?.vendorInvoiceNumber || '';
  const poNumber = inv?.poNumber || '';
  const invoiceDate = inv?.invoiceDate ? formatDateInput(inv.invoiceDate) : formatDateInput(new Date());
  const dueDate = inv?.dueDate ? formatDateInput(inv.dueDate) : '';
  const jobId = inv?.jobId || '';
  const assetId = inv?.assetId || '';
  const paymentStatus = inv?.paymentStatus || 'open';
  const paidAmount = inv?.paidAmount || 0;
  const paymentMethod = inv?.paymentMethod || '';
  const paymentReference = inv?.paymentReference || '';
  const checkNumber = inv?.checkNumber || '';
  const notes = inv?.notes || '';

  const categories = getCategories();

  body.innerHTML = `
    <div class="modal-header">
      <h3>${title}</h3>
      <button class="btn-icon" onclick="window.APModule.closeInvoiceModal()">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body" style="padding:20px 24px;max-height:75vh;overflow-y:auto;">

      <!-- Vendor -->
      <div class="form-group">
        <label>Vendor *</label>
        <div style="display:flex;gap:8px;">
          <select id="ap-vendor" style="flex:1;" onchange="window.APModule.onVendorChange()">
            <option value="">Select vendor...</option>
            ${_apVendorSplit(vendorId).vendors.map(v => `<option value="${escapeHtml(v.id)}" ${v.id === vendorId ? 'selected' : ''}>${escapeHtml(v.name)}</option>`).join('')}
          </select>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.APModule.showAddVendorInline()" title="Add Vendor" style="padding:6px 10px;">
            <span class="material-icons" style="font-size:18px;">add</span>
          </button>
          ${_apCanEditVendors() ? `<button type="button" class="btn btn-secondary btn-sm" onclick="window.APModule.editVendor(document.getElementById('ap-vendor')?.value)" title="Edit the selected vendor" style="padding:6px 10px;">
            <span class="material-icons" style="font-size:18px;">edit</span>
          </button>` : ''}
        </div>
        ${_apPayeeFinderHtml(vendorId)}
        <div id="ap-charge-notice">${_apChargeNoticeHtml(vendorId)}</div>
        <div id="ap-add-vendor-inline"></div>
      </div>

      <!-- Invoice Details -->
      <div class="form-row">
        <div class="form-group">
          <label>Vendor Invoice #</label>
          <input type="text" id="ap-vendor-inv-num" value="${escapeHtml(vendorInvoiceNumber)}" placeholder="INV-001">
        </div>
        <div class="form-group">
          <label>PO Number</label>
          <div style="display:flex;gap:6px;">
            <input type="text" id="ap-po-number" value="${escapeHtml(poNumber)}" placeholder="PO-202603-001" style="flex:1;">
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.APModule.autoGenPO()" title="Auto-generate PO#" style="padding:6px 10px;">
              <span class="material-icons" style="font-size:16px;">autorenew</span>
            </button>
          </div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Invoice Date *</label>
          <input type="date" id="ap-invoice-date" value="${invoiceDate}" required onchange="window.APModule.onInvoiceDateChange()">
        </div>
        <div class="form-group">
          <label>Due Date</label>
          <input type="date" id="ap-due-date" value="${dueDate}">
        </div>
      </div>

      <!-- Assignment -->
      <div class="form-row">
        <div class="form-group">
          <label>Job (optional)</label>
          <select id="ap-job">
            <option value="">None</option>
            ${APState.jobs.filter(j => j.status !== 'finished').map(j => {
              const custName = j.customerName ? ` — ${escapeHtml(j.customerName)}` : '';
              return `<option value="${j.id}" ${j.id === jobId ? 'selected' : ''}>${escapeHtml(j.name || j.jobName || 'Unnamed')}${custName}</option>`;
            }).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>Vehicle/Asset (optional)</label>
          <select id="ap-asset">
            <option value="">None</option>
            ${APState.assets.map(a => {
              const label = a.unit ? `${a.unit}${a.nickname ? ' — ' + a.nickname : ''}` : (a.name || a.nickname || 'Unnamed');
              return `<option value="${a.id}" data-name="${escapeHtml(label)}" ${a.id === assetId ? 'selected' : ''}>${escapeHtml(label)} (${a._assetType || 'asset'})</option>`;
            }).join('')}
          </select>
        </div>
      </div>

      <!-- Line Items -->
      <div class="ap-line-items-section">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <label style="font-weight:600;font-size:0.9375rem;">Line Items</label>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.APModule.addLineItem()">
            <span class="material-icons" style="font-size:16px;">add</span> Add Line
          </button>
        </div>
        <div id="ap-line-items-container">
          ${renderLineItemsHTML()}
        </div>
        <div class="ap-grand-total" id="ap-grand-total">
          Grand Total: $${calcGrandTotal().toFixed(2)}
        </div>
        ${_apCreditApplied(inv) > 0 ? `
        <div style="text-align:right;font-size:0.85rem;color:#166534;margin-top:4px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">undo</span>
          Refunds / credits applied: −$${_apCreditApplied(inv).toFixed(2)}
        </div>` : ''}
      </div>

      <!-- Payment Status -->
      <div class="form-group" style="margin-top:16px;">
        <label>Payment Status</label>
        <div class="ap-payment-toggle" id="ap-payment-toggle">
          <button type="button" class="ap-pay-btn ${paymentStatus === 'open' ? 'active' : ''}" onclick="window.APModule.setPaymentStatus('open')">Open</button>
          <button type="button" class="ap-pay-btn ${paymentStatus === 'partial' ? 'active' : ''}" onclick="window.APModule.setPaymentStatus('partial')">Accrue</button>
          <button type="button" class="ap-pay-btn ${paymentStatus === 'paid' ? 'active' : ''}" onclick="window.APModule.setPaymentStatus('paid')">Paid</button>
        </div>
        <div id="ap-accrue-wrap" style="${paymentStatus === 'partial' ? '' : 'display:none;'}margin-top:8px;">
          <label style="font-size:0.8125rem;color:var(--text-secondary);">Amount Paid</label>
          <input type="number" id="ap-paid-amount" value="${paidAmount}" step="0.01" min="0" placeholder="0.00" style="max-width:160px;">
        </div>
        <div id="ap-payment-method-wrap" style="${paymentStatus === 'paid' ? '' : 'display:none;'}margin-top:12px;">
          <label style="font-size:0.8125rem;font-weight:600;color:var(--text-secondary);margin-bottom:4px;display:block;">Payment Method</label>
          <div class="ap-payment-method-grid">
            <button type="button" class="ap-method-btn ${paymentMethod === 'check' ? 'active' : ''}" data-method="check" onclick="window.APModule.setPaymentMethod('check')">
              <span class="material-icons">receipt_long</span> Check
            </button>
            <button type="button" class="ap-method-btn ${paymentMethod === 'ach' ? 'active' : ''}" data-method="ach" onclick="window.APModule.setPaymentMethod('ach')">
              <span class="material-icons">account_balance</span> ACH
            </button>
            <button type="button" class="ap-method-btn ${paymentMethod === 'credit_card' ? 'active' : ''}" data-method="credit_card" onclick="window.APModule.setPaymentMethod('credit_card')">
              <span class="material-icons">credit_card</span> Card
            </button>
            <button type="button" class="ap-method-btn ${paymentMethod === 'cash' ? 'active' : ''}" data-method="cash" onclick="window.APModule.setPaymentMethod('cash')">
              <span class="material-icons">payments</span> Cash
            </button>
            <button type="button" class="ap-method-btn ${paymentMethod === 'wire' ? 'active' : ''}" data-method="wire" onclick="window.APModule.setPaymentMethod('wire')">
              <span class="material-icons">swap_horiz</span> Wire
            </button>
          </div>
          <div id="ap-check-number-wrap" style="${paymentMethod === 'check' ? '' : 'display:none;'}margin-top:8px;">
            <label style="font-size:0.8125rem;color:var(--text-secondary);">Check Number</label>
            <input type="text" id="ap-check-number" value="${escapeHtml(checkNumber)}" placeholder="Auto-assigned on print" style="max-width:160px;">
          </div>
          <div id="ap-payment-ref-wrap" style="${paymentMethod === 'ach' || paymentMethod === 'wire' ? '' : 'display:none;'}margin-top:8px;">
            <label style="font-size:0.8125rem;color:var(--text-secondary);">Reference Number</label>
            <input type="text" id="ap-payment-ref" value="${escapeHtml(paymentReference)}" placeholder="Transaction reference" style="max-width:240px;">
          </div>
        </div>
      </div>

      <!-- Documents -->
      <div class="form-group" style="margin-top:16px;">
        <label>Documents</label>
        <div id="ap-documents-section">
          ${renderDocumentsHTML()}
        </div>
        <label class="btn btn-secondary btn-sm" style="margin-top:8px;cursor:pointer;">
          <span class="material-icons" style="font-size:16px;">attach_file</span> Attach File
          <input type="file" accept="image/*,.heic,.heif,application/pdf" multiple style="display:none;" onchange="window.APModule.handleDocUpload(event)">
        </label>
      </div>

      <!-- Notes -->
      <div class="form-group" style="margin-top:16px;">
        <label>Notes</label>
        <textarea id="ap-notes" rows="3" placeholder="Optional notes...">${escapeHtml(notes)}</textarea>
      </div>
    </div>

    <!-- Footer -->
    <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom, #F8FAFC, #fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);display:flex;gap:12px;justify-content:flex-end;flex-shrink:0;">
      ${isEdit ? `
        <button type="button" class="btn btn-danger" onclick="window.APModule.deleteInvoice('${inv.id}')" style="margin-right:auto;">
          <span class="material-icons" style="font-size:16px;">delete</span> Delete
        </button>` : ''}
      <button type="button" class="btn btn-secondary" onclick="window.APModule.closeInvoiceModal()">Cancel</button>
      <button type="button" class="btn btn-primary" id="ap-save-btn" onclick="window.APModule.saveInvoice()">
        <span class="material-icons" style="font-size:16px;">save</span> Save Invoice
      </button>
    </div>
  `;
}

// ============================================
// LINE ITEMS
// ============================================
function renderLineItemsHTML() {
  const categories = getCategories();
  return APState.lineItems.map((li, idx) => `
    <div class="ap-line-item" data-li-id="${li.id}">
      <div class="ap-li-row">
        <div class="form-group ap-li-cat">
          <select onchange="window.APModule.updateLineItem('${li.id}','category',this.value)">
            <option value="">Category...</option>
            ${categories.map(c => `<option value="${typeof c === 'string' ? c : c.slug}" ${li.category === (typeof c === 'string' ? c : c.slug) ? 'selected' : ''}>${typeof c === 'string' ? c : c.label}</option>`).join('')}
          </select>
        </div>
        <div class="form-group ap-li-desc">
          <input type="text" value="${escapeHtml(li.description)}" placeholder="Description" onchange="window.APModule.updateLineItem('${li.id}','description',this.value)">
        </div>
        <div class="form-group ap-li-qty">
          <input type="number" value="${li.quantity}" min="0" step="0.01" placeholder="Qty" onchange="window.APModule.updateLineItem('${li.id}','quantity',this.value)">
        </div>
        <div class="form-group ap-li-rate">
          <input type="number" value="${li.rate}" min="0" step="0.01" placeholder="Rate" onchange="window.APModule.updateLineItem('${li.id}','rate',this.value)">
        </div>
        <div class="form-group ap-li-person">
          <select onchange="window.APModule.updateLineItem('${li.id}','personId',this.value)">
            <option value="">Person...</option>
            ${APState.people.map(p => `<option value="${p.id}" ${li.personId === p.id ? 'selected' : ''}>${escapeHtml(p.name)}</option>`).join('')}
          </select>
        </div>
        ${_stockInLineBtn(li)}
        <div class="ap-li-total">$${(li.total || 0).toFixed(2)}</div>
        <button type="button" class="btn-icon ap-li-remove" onclick="window.APModule.removeLineItem('${li.id}')" title="Remove line"${APState.lineItems.length === 1 ? ' disabled' : ''}>
          <span class="material-icons">close</span>
        </button>
      </div>
    </div>
  `).join('');
}

function addLineItem() {
  APState.lineItems.push(createBlankLineItem());
  refreshLineItems();
}

function removeLineItem(liId) {
  if (APState.lineItems.length <= 1) return;
  APState.lineItems = APState.lineItems.filter(li => li.id !== liId);
  refreshLineItems();
}

function updateLineItem(liId, field, value) {
  const li = APState.lineItems.find(l => l.id === liId);
  if (!li) return;

  if (field === 'quantity' || field === 'rate') {
    li[field] = parseFloat(value) || 0;
    li.total = li.quantity * li.rate;
  } else {
    li[field] = value;
    if (field === 'personId') {
      const person = APState.people.find(p => p.id === value);
      li.personName = person?.name || '';
    }
  }

  APState.dirty = true;
  // Update total display
  const totalEl = document.querySelector(`.ap-line-item[data-li-id="${liId}"] .ap-li-total`);
  if (totalEl) totalEl.textContent = `$${(li.total || 0).toFixed(2)}`;
  updateGrandTotal();
}

function refreshLineItems() {
  const container = document.getElementById('ap-line-items-container');
  if (container) container.innerHTML = renderLineItemsHTML();
  updateGrandTotal();
}

function calcGrandTotal() {
  return APState.lineItems.reduce((s, li) => s + (li.total || 0), 0);
}

function updateGrandTotal() {
  const el = document.getElementById('ap-grand-total');
  if (el) el.textContent = `Grand Total: $${calcGrandTotal().toFixed(2)}`;
}

// ============================================
// CATEGORIES
// ============================================
function getCategories() {
  if (APState.categories.length) return APState.categories;
  return _defaultApCats();
}

async function addCategory(label) {
  if (!label?.trim()) return;
  const slug = label.trim().toLowerCase().replace(/\s+/g, '_');
  const existing = APState.categories.find(c => (typeof c === 'string' ? c : c.slug) === slug);
  if (existing) { window.showToast?.('Category already exists', 'warning'); return; }

  const newCat = { label: label.trim(), slug };
  APState.categories.push(newCat);

  // Persist to tenant settings
  try {
    const tenant = APState.tenantId === 'demo-tenant' ? 'demo' : APState.tenantId;
    const tenantRef = doc(db, 'tenants', tenant);
    const snap = await getDoc(tenantRef);
    const settings = snap.exists() ? (snap.data().settings || {}) : {};
    const customTypes = settings.customTypes || {};
    customTypes.apCategories = APState.categories;
    await updateDoc(tenantRef, { 'settings.customTypes.apCategories': APState.categories });
    window.showToast?.(`Category "${label.trim()}" added`, 'success');
  } catch (e) {
    console.error('[AP] Save category error:', e);
  }

  refreshLineItems();
}

// ============================================
// TASK-9684 — ACCOUNT VENDORS vs OTHER PAYEES in the bill's vendor picker
// ============================================
// Chris 2026-10-05: the picker lists ACCOUNT vendors (they send bills); card /
// bank-only payees fold into "Other payees (N)" — still one search away, so the
// FIRST bill from a payee can be entered (and that bill moves it up for good).
// The rule is window.BillableCustomer.vendorPayeeKind (shared with CRM and the
// server); bill activity comes from the bills this view already subscribes to.
// The tenant view setting settings.payeeView { showAll, pins } is honoured; a
// bill's CURRENT vendor is always kept so a Save never re-points it.
function _apVendorSplit(keepId) {
  const pv = window.AppState?.settings?.payeeView || {};
  return window.BillableCustomer.splitVendorsAndPayees(APState.vendors, {
    billVendorIds: window.BillableCustomer.billVendorIdsFromInvoices(APState.invoices),
    showAll: pv.showAll === true,
    pins: Array.isArray(pv.pins) ? pv.pins : [],
    keepId: keepId || null,
  });
}

function _apPayeeFinderHtml(keepId) {
  const n = _apVendorSplit(keepId).payees.length;
  if (!n) return '';
  return `
    <div id="ap-payee-finder" style="margin-top:6px;">
      <button type="button" id="ap-payee-toggle" onclick="window.APModule.togglePayeeFinder()" aria-expanded="false" aria-controls="ap-payee-panel"
        style="background:none;border:none;padding:6px 0;min-height:36px;color:var(--brand-blue-dark, #2563EB);font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:4px;">
        <span class="material-icons" style="font-size:16px;">search</span> Other payees (${n}) — find one
      </button>
      <div id="ap-payee-panel" style="display:none;margin-top:4px;">
        <input type="search" id="ap-payee-search" placeholder="Search other payees (card / bank only)" autocomplete="off"
          oninput="window.APModule.renderPayeeResults()" style="width:100%;font-size:16px;">
        <div id="ap-payee-results" role="listbox" aria-label="Other payees" style="margin-top:6px;max-height:220px;overflow-y:auto;border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-card);"></div>
        <div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Picking one and saving this bill makes it an account vendor.</div>
      </div>
    </div>`;
}

function togglePayeeFinder() {
  const panel = document.getElementById('ap-payee-panel');
  const btn = document.getElementById('ap-payee-toggle');
  if (!panel) return;
  const open = panel.style.display === 'none';
  panel.style.display = open ? 'block' : 'none';
  if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) { renderPayeeResults(); document.getElementById('ap-payee-search')?.focus(); }
}

const AP_PAYEE_RESULTS_MAX = 50;
function renderPayeeResults() {
  const box = document.getElementById('ap-payee-results');
  if (!box) return;
  const q = (document.getElementById('ap-payee-search')?.value || '').toLowerCase().trim();
  const payees = _apVendorSplit(document.getElementById('ap-vendor')?.value || '').payees
    .filter(v => !q || String(v.name || '').toLowerCase().includes(q))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  if (!payees.length) {
    box.innerHTML = `<div style="padding:10px;font-size:13px;color:var(--text-secondary);">${q ? 'No other payee matches. Use + to add a new vendor.' : 'No other payees.'}</div>`;
    return;
  }
  const shown = payees.slice(0, AP_PAYEE_RESULTS_MAX);
  box.innerHTML = shown.map(v => {
    const jsId = window.escJsAttr ? window.escJsAttr(v.id) : String(v.id).replace(/[^A-Za-z0-9_-]/g, '');
    return `<button type="button" role="option" onclick="window.APModule.pickPayee('${jsId}')"
      style="display:block;width:100%;text-align:left;padding:10px 12px;min-height:44px;border:none;border-bottom:1px solid var(--border-light);background:none;color:var(--text-primary);font-size:14px;font-family:inherit;cursor:pointer;">${escapeHtml(v.name || '(no name)')}</button>`;
  }).join('') + (payees.length > shown.length
    ? `<div style="padding:8px 12px;font-size:12px;color:var(--text-secondary);">${payees.length - shown.length} more — keep typing to narrow it down.</div>` : '');
}

// Put a vendor into the bill's select (adding its option if the short list
// does not carry it) and select it.
function _apSelectVendorOption(id, name) {
  const sel = document.getElementById('ap-vendor');
  if (!sel || !id) return false;
  let opt = Array.from(sel.options).find(o => o.value === id);
  if (!opt) {
    opt = document.createElement('option');
    opt.value = id;
    opt.textContent = name || id;
    sel.appendChild(opt);
  }
  sel.value = id;
  return sel.value === id;
}

function pickPayee(vendorId) {
  const v = APState.vendors.find(x => x.id === vendorId);
  if (!v) return;
  if (_apSelectVendorOption(v.id, v.name)) onVendorChange();
  const panel = document.getElementById('ap-payee-panel');
  if (panel) panel.style.display = 'none';
  document.getElementById('ap-payee-toggle')?.setAttribute('aria-expanded', 'false');
}

// ============================================
// VENDOR INLINE ADD
// ============================================
// TASK-9793 (Chris 2026-10-12: "they will have to be able to edit vendors") —
// may THIS login edit a vendor record? Advisory client mirror of the
// firestore.rules customers/{id} update arms a vendor doc can pass through:
// canEditMoney (owner by tenants.ownerId, or the billing module, never
// view-only / read-only) or the crm module. ACCESS = MODULES, NOT ROLES: no
// role arm here. Enforcement is the rules file; this only decides whether the
// pencil is drawn, so a member the rules will refuse never sees it.
function _apCanEditVendors() {
  try {
    const st = window.AppState || {};
    if (st.isDemo || st.tenant === 'demo') return true;
    const ma = st.memberAccess;
    if (ma == null) return true; // legacy single-login owner — no member doc
    if (ma.status && ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
    if (st.user?.uid && st.tenantData?.ownerId && st.user.uid === st.tenantData.ownerId) return true;
    if (ma.viewUploadOnly === true || ma.readOnly === true) return false;
    const mods = Array.isArray(ma.modules) ? ma.modules : [];
    return mods.includes('billing') || mods.includes('crm');
  } catch (_) { return false; }
}

// Open the CRM vendor edit form (the same one CRM → Vendors uses) for a
// vendor / subcontractor from APState.vendors — the AP subscription already
// holds the full doc. Customers never reach here (APState.vendors is
// type-filtered, and CRMModule.openVendorEditor refuses them again).
function editVendor(vendorId) {
  if (!vendorId) { window.showToast?.('Select a vendor first', 'info'); return; }
  if (!_apCanEditVendors()) { window.showToast?.('Your access does not include editing vendors.', 'warning'); return; }
  const vendor = APState.vendors.find(v => v.id === vendorId);
  if (!vendor) { window.showToast?.('Vendor not found — it may have been removed.', 'error'); return; }
  const open = window.CRMModule?.openVendorEditor;
  if (typeof open !== 'function') { window.showToast?.('Vendor editing is unavailable — reload and try again.', 'error'); return; }
  open(vendor, APState.tenantId === 'demo-tenant' ? 'demo' : APState.tenantId);
}

function showAddVendorInline() {
  const wrap = document.getElementById('ap-add-vendor-inline');
  if (!wrap || wrap.querySelector('.ap-vendor-inline-form')) return;

  wrap.innerHTML = `
    <div class="ap-vendor-inline-form" style="display:flex;gap:8px;margin-top:8px;align-items:center;">
      <input type="text" id="ap-new-vendor-name" placeholder="Vendor name" style="flex:1;" maxlength="100" autocomplete="off">
      <button type="button" class="btn btn-primary btn-sm" onclick="window.APModule.saveNewVendor()">
        <span class="material-icons" style="font-size:16px;">check</span>
      </button>
      <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('ap-add-vendor-inline').innerHTML=''">
        <span class="material-icons" style="font-size:16px;">close</span>
      </button>
    </div>`;
  document.getElementById('ap-new-vendor-name')?.focus();
}

async function saveNewVendor() {
  const nameEl = document.getElementById('ap-new-vendor-name');
  const name = nameEl?.value?.trim();
  if (!name) { window.showToast?.('Vendor name is required', 'error'); return; }

  // TASK-9684 — a payee folded under "Other payees" is not in the select, so a
  // biller could re-create it here. Same name (ignoring case/spacing) -> use it.
  const _norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const existing = APState.vendors.find(v => _norm(v.name) === _norm(name));
  if (existing) {
    _apSelectVendorOption(existing.id, existing.name);
    onVendorChange();
    document.getElementById('ap-add-vendor-inline').innerHTML = '';
    window.showToast?.(`Found "${existing.name}" already in your vendors — picked it for this bill instead of adding a duplicate`, 'info');
    return;
  }

  // TASK-398: capture the bill-form generation BEFORE the network await. If the
  // user closes this bill and opens a DIFFERENT one during the await + the 500ms
  // post-snapshot delay, invoiceFormGen will have advanced and we MUST NOT write
  // the new vendor into the now-different bill's #ap-vendor select.
  const myGen = APState.invoiceFormGen;
  try {
    const tenant = APState.tenantId === 'demo-tenant' ? 'demo' : APState.tenantId;
    const vendorDoc = await addDoc(collection(db, 'tenants', tenant, 'customers'), {
      name,
      type: 'vendor',
      status: 'Active',
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.uid || 'unknown'
    });
    window.showToast?.(`Vendor "${name}" created`, 'success');

    // Select the new vendor in dropdown after snapshot fires.
    // TASK-398: token-guard — abort if the bill form moved to a different bill.
    if (APState._vendorSelectTimer) clearTimeout(APState._vendorSelectTimer);
    APState._vendorSelectTimer = setTimeout(() => {
      APState._vendorSelectTimer = null;
      if (APState.invoiceFormGen !== myGen) return; // form moved on — abort
      // TASK-9684 — a brand-new vendor has no bill yet, so it is not in the
      // short list; add its option (it was never re-rendered in before, either).
      _apSelectVendorOption(vendorDoc.id, name);
    }, 500);

    document.getElementById('ap-add-vendor-inline').innerHTML = '';
  } catch (e) {
    console.error('[AP] Save vendor error:', e);
    window.showToast?.('Failed to create vendor', 'error');
  }
}

// ============================================
// PO AUTO-GENERATION
// ============================================
function autoGenPO() {
  const now = new Date();
  const ym = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const existing = APState.invoices.filter(i => (i.poNumber || '').startsWith(`PO-${ym}`));
  const nextNum = existing.length + 1;
  const po = `PO-${ym}-${String(nextNum).padStart(3, '0')}`;
  const el = document.getElementById('ap-po-number');
  if (el) el.value = po;
}

// ============================================
// PAYMENT STATUS TOGGLE
// ============================================
function setPaymentStatus(status) {
  document.querySelectorAll('.ap-pay-btn').forEach(btn => {
    btn.classList.toggle('active', btn.textContent.trim().toLowerCase() === (status === 'partial' ? 'accrue' : status));
  });
  const accrueWrap = document.getElementById('ap-accrue-wrap');
  if (accrueWrap) accrueWrap.style.display = status === 'partial' ? '' : 'none';
  const methodWrap = document.getElementById('ap-payment-method-wrap');
  if (methodWrap) methodWrap.style.display = status === 'paid' ? '' : 'none';
}

function setPaymentMethod(method) {
  document.querySelectorAll('.ap-method-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.method === method);
  });
  const checkWrap = document.getElementById('ap-check-number-wrap');
  const refWrap = document.getElementById('ap-payment-ref-wrap');
  if (checkWrap) checkWrap.style.display = method === 'check' ? '' : 'none';
  if (refWrap) refWrap.style.display = (method === 'ach' || method === 'wire') ? '' : 'none';
}

// ============================================
// DOCUMENT UPLOAD
// ============================================
// TASK-9397 — pick-time photo work saveInvoice must wait out (a HEIC
// conversion can take seconds; a Save during it would drop the photo).
const _apPhotoPrep = createPhotoPrepTracker();

function handleDocUpload(event) {
  return _apPhotoPrep.track(_handleDocUpload(event));
}

async function _handleDocUpload(event) {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  event.target.value = '';
  // A bill form closed or swapped while we convert must not receive this file.
  const formGen = APState.invoiceFormGen;
  const pendingBefore = APState.pendingDocFiles.length;

  const totalDocs = (APState.editingInvoice?.documents?.length || 0) + APState.pendingDocFiles.length;
  const remaining = 10 - totalDocs;
  if (remaining <= 0) { window.showToast?.('Maximum 10 documents per invoice', 'warning'); return; }

  const toProcess = files.slice(0, remaining);
  if (files.length > remaining) {
    window.showToast?.(`Only ${remaining} more document${remaining !== 1 ? 's' : ''} allowed`, 'warning');
  }

  for (const file of toProcess) {
    const nameLower = file.name.toLowerCase();
    const isPdf = file.type === 'application/pdf' || nameLower.endsWith('.pdf');
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif'];
    const isImage = file.type.startsWith('image/') || imageExts.some(ext => nameLower.endsWith(ext));

    if (!isPdf && !isImage) { window.showToast?.(`${file.name}: Only images and PDFs allowed`, 'error'); continue; }
    if (file.size > 10 * 1024 * 1024) { window.showToast?.(`${file.name}: File too large (max 10MB)`, 'error'); continue; }

    const id = `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      if (isPdf) {
        APState.pendingDocFiles.push({ id, blob: file, previewUrl: null, name: file.name, mimeType: 'application/pdf', isPdf: true });
      } else {
        const validation = validateImageFile(file);
        if (!validation.valid) { window.showToast?.(validation.error, 'error'); continue; }
        // TASK-9397 — HEIC used to be parked RAW here (broken image on every
        // non-Safari screen). Now: convert when this browser can't draw it,
        // then the same JPEG compression as every other photo. Never raw.
        const drawable = await toDrawablePhoto(file);
        const processed = await processPhoto(drawable, { maxSizeKB: 200 });
        if (APState.invoiceFormGen !== formGen) return;
        APState.pendingDocFiles.push({ id, blob: processed, previewUrl: URL.createObjectURL(processed), name: drawable.name || file.name, mimeType: 'image/jpeg', isPdf: false });
      }
    } catch (err) {
      console.error('[AP] Doc upload error:', err?.cause || err);
      window.showToast?.(`${file.name}: ${photoErrorMessage(err, 'Failed to process this file')}`, 'error');
    }
  }
  if (APState.invoiceFormGen !== formGen) return;

  const section = document.getElementById('ap-documents-section');
  if (section) section.innerHTML = renderDocumentsHTML();
  const added = APState.pendingDocFiles.length - pendingBefore;
  if (added > 0) window.showToast?.(`${added} document${added !== 1 ? 's' : ''} added`, 'success');
}

function renderDocumentsHTML() {
  const existingDocs = (APState.editingInvoice?.documents || []).filter(d => !APState.deletedDocIds.includes(d.id));
  const allDocs = [...existingDocs, ...APState.pendingDocFiles.map(p => ({ ...p, isPending: true }))];

  if (!allDocs.length) return '<div style="color:var(--text-secondary);font-size:0.8125rem;">No documents attached.</div>';

  return `<div class="ap-doc-grid">${allDocs.map(d => `
    <div class="ap-doc-item">
      ${d.isPdf || d.mimeType === 'application/pdf'
        ? `<span class="material-icons" style="font-size:32px;color:#DC2626;">picture_as_pdf</span>`
        : (d.isPending
          ? `<img src="${escapeHtml(d.previewUrl || '')}" alt="" style="width:48px;height:48px;object-fit:cover;border-radius:4px;">`
          // TASK-9855 — saved bill images load through the Storage SDK on their
          // path (rules-checked), never the stored token URL.
          : `<img ${window.StorageFile ? window.StorageFile.imgAttrs(d, { urlKey: d.thumbUrl ? 'thumbUrl' : 'url' }) : ''} alt="" style="width:48px;height:48px;object-fit:cover;border-radius:4px;">`)}
      <span class="ap-doc-name">${escapeHtml(d.name || 'Document')}</span>
      <button type="button" class="btn-icon" onclick="window.APModule.removeDoc('${d.id}')" title="Remove">
        <span class="material-icons" style="font-size:16px;">close</span>
      </button>
    </div>
  `).join('')}</div>`;
}

function removeDoc(docId) {
  const pendingIdx = APState.pendingDocFiles.findIndex(p => p.id === docId);
  if (pendingIdx !== -1) {
    const f = APState.pendingDocFiles[pendingIdx];
    if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    APState.pendingDocFiles.splice(pendingIdx, 1);
  } else {
    APState.deletedDocIds.push(docId);
  }
  const section = document.getElementById('ap-documents-section');
  if (section) section.innerHTML = renderDocumentsHTML();
}

// ============================================
// SAVE INVOICE
// ============================================
// TASK-9491 — firestore.rules apInvoices: CREATE is canAddMoneyRecord (a
// view-only billing member may still enter a bill — "other than adding
// expenses"), but UPDATE is canEditMoney and DELETE is the owner (ownerId).
// Refuse those here with the same plain sentence instead of a raw denial.
function _apMoneyEditBlocked() {
  if (typeof window.canEditMoney !== 'function' || window.canEditMoney() !== false) return false;
  window.showToast?.(window.MONEY_VIEW_ONLY_MSG || 'View only — ask the owner to make this change.', 'warning');
  return true;
}

// TASK-9947 (review RED) — a refund waiting for approval on this bill: paying it
// now would pay the vendor in full. The check printer refuses server-side too.
function _apPendingCreditOnBill(invoiceId) {
  return !!invoiceId && _creditsOf(`ap_${invoiceId}`).some(c => c.status === 'pending');
}
async function saveInvoice() {
  // TASK-9397 — a photo still converting would miss the upload list; wait first.
  // COD: if the bill form was closed/swapped while we waited, this Save is
  // for a form that no longer exists — don't persist the new one.
  const saveFormGen = APState.invoiceFormGen;
  if (!(await settlePhotoPrepBeforeSave(_apPhotoPrep, 'ap-save-btn'))) return;
  if (APState.invoiceFormGen !== saveFormGen) return;
  if (APState.editingInvoice && _apMoneyEditBlocked()) return; // TASK-9491 — editing an existing bill
  const vendorId = document.getElementById('ap-vendor')?.value;
  const vendorName = document.getElementById('ap-vendor')?.selectedOptions?.[0]?.textContent || '';
  const invoiceDateVal = document.getElementById('ap-invoice-date')?.value;

  if (!vendorId) { window.showToast?.('Vendor is required', 'error'); return; }
  if (!invoiceDateVal) { window.showToast?.('Invoice date is required', 'error'); return; }

  // Validate at least one line item has content
  const validLines = APState.lineItems.filter(li => li.category || li.description || li.rate > 0);
  if (!validLines.length) { window.showToast?.('Add at least one line item', 'error'); return; }

  // W-9 backup-withholding warning (TASK-550): paying a SUBCONTRACTOR with no
  // W-9 on file exposes the payer to 24% IRS backup withholding (IRC §3406).
  // Soft, non-blocking — vendors (supply houses/corps) are 1099-exempt, no warning.
  const vendorForW9 = APState.vendors.find(v => v.id === vendorId);
  if (vendorForW9 && vendorForW9.type === 'subcontractor' && !vendorForW9.w9OnFile && typeof window.showConfirm === 'function') {
    const ok = await window.showConfirm(
      'No W-9 on file',
      'No W-9 on file — you may be required to withhold 24% of this payment (IRS backup withholding). Collect a W-9 before paying. Continue anyway?',
      { confirmText: 'Continue anyway', cancelText: 'Cancel' }
    );
    if (!ok) return;
  }

  const btn = document.getElementById('ap-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const tenant = APState.tenantId === 'demo-tenant' ? 'demo' : APState.tenantId;

    // Determine payment status from toggle
    let paymentStatus = 'open';
    document.querySelectorAll('.ap-pay-btn.active').forEach(b => {
      const txt = b.textContent.trim().toLowerCase();
      if (txt === 'paid') paymentStatus = 'paid';
      else if (txt === 'accrue') paymentStatus = 'partial';
    });
    // TASK-9947 (review RED) — not paid in full while a refund on it waits for approval.
    if ((paymentStatus === 'paid' || paymentStatus === 'partial') && APState.editingInvoice && APState.editingInvoice.paymentStatus !== 'paid' && _apPendingCreditOnBill(APState.editingInvoice.id)) {
      window.showToast?.('A refund / credit on this bill is waiting for approval. Approve or reject it first, so the payment is the right amount.', 'warning');
      return;
    }

    // TASK-9367 — marking a bill Paid records the cash actually paid: the total
    // less any refunds / credits already applied to it (same as a printed check).
    const paidAmount = paymentStatus === 'partial'
      ? (parseFloat(document.getElementById('ap-paid-amount')?.value) || 0)
      : paymentStatus === 'paid'
        ? Math.max(0, Math.round(calcGrandTotal() * 100) - _apCents(_apCreditApplied(APState.editingInvoice))) / 100
        : 0;

    const jobSelect = document.getElementById('ap-job');
    const jobId = jobSelect?.value || null;
    const jobName = jobId ? jobSelect?.selectedOptions?.[0]?.textContent : null;

    const assetSelect = document.getElementById('ap-asset');
    const assetId = assetSelect?.value || null;
    const assetName = assetId ? assetSelect?.selectedOptions?.[0]?.dataset?.name || assetSelect?.selectedOptions?.[0]?.textContent : null;

    // Recalculate line totals
    const lineItems = APState.lineItems.map(li => ({
      id: li.id,
      category: li.category,
      description: li.description,
      quantity: li.quantity || 0,
      rate: li.rate || 0,
      total: (li.quantity || 0) * (li.rate || 0),
      personId: li.personId || null,
      personName: li.personName || null
    }));

    const grandTotal = lineItems.reduce((s, li) => s + li.total, 0);
    // TASK-9947 (COD r2) — refunds are capped against this bill's cost; the
    // rules keep that cost's amount once a refund exists, so a changed total
    // would save the bill but not its cost (a silent split). Say so instead.
    // COD r3: ask the SERVER summary (expenseRefundTotals), not the cached list
    // (empty until Company Expenses loads, or after its listener fails).
    if (APState.editingInvoice && Math.round(grandTotal * 100) !== Math.round(Number(APState.editingInvoice.grandTotal || 0) * 100)) {
      // integ/2108 COD r2 fold: a rejected refund counts nowhere (the server drops the summary when all are rejected).
      let hasRefunds = _creditsOf(`ap_${APState.editingInvoice.id}`).some(c => c.status !== 'rejected');
      if (!hasRefunds) {
        try {
          hasRefunds = (await getDoc(doc(db, 'tenants', tenant, 'expenseRefundTotals', `ap_${APState.editingInvoice.id}`))).exists();
        } catch (e) {
          window.showToast?.('Could not check this bill for refunds, so the total was not changed. Try again.', 'error');
          return;
        }
      }
      if (hasRefunds) {
        window.showToast?.('This bill has a refund / credit recorded against it, so its total cannot change. Keep the total, or reject the refund and record a new bill.', 'warning');
        return;
      }
    }

    // TASK-10145 — the crew charge receipts ticked as on this statement. They
    // are linked on the SERVER (apChargeReceiptsSet, one transaction); the bill
    // keeps the full statement and its cost counts only the rest.
    const _chargeLinks = _apReadChargeLinks(vendorId);
    const _linkedBefore = _apChargeLinkedIds(APState.editingInvoice);
    const _linksChanged = _chargeLinks.ids.length !== _linkedBefore.length || _chargeLinks.ids.some(id => !_linkedBefore.includes(id));
    if (_chargeLinks.cents > Math.round(grandTotal * 100)) {
      window.showToast?.('The receipts you ticked add up to more than this bill. Untick some, or check the bill total.', 'error');
      return;
    }
    if (APState.editingInvoice && _chargeLinks.ids.length && APState.editingInvoice.vendorId && vendorId !== APState.editingInvoice.vendorId) {
      window.showToast?.('This bill covers crew receipts from its vendor. Untick them first, then change the vendor.', 'warning');
      return;
    }

    // Payment method fields (only when paid)
    let paymentMethod = '';
    let paymentReference = '';
    let checkNumber = '';
    if (paymentStatus === 'paid') {
      const activeMethod = document.querySelector('.ap-method-btn.active');
      paymentMethod = activeMethod?.dataset?.method || '';
      checkNumber = document.getElementById('ap-check-number')?.value?.trim() || '';
      paymentReference = document.getElementById('ap-payment-ref')?.value?.trim() || '';
    }

    const data = {
      vendorId,
      vendorName: vendorName.trim(),
      vendorInvoiceNumber: document.getElementById('ap-vendor-inv-num')?.value?.trim() || '',
      poNumber: document.getElementById('ap-po-number')?.value?.trim() || '',
      invoiceDate: Timestamp.fromDate(new Date(invoiceDateVal + 'T12:00:00')),
      dueDate: document.getElementById('ap-due-date')?.value
        ? Timestamp.fromDate(new Date(document.getElementById('ap-due-date').value + 'T12:00:00'))
        : null,
      jobId,
      jobName: jobName?.trim() || null,
      assetId,
      assetName: assetName?.trim() || null,
      lineItems,
      grandTotal,
      paymentStatus,
      paidAmount,
      paymentMethod: paymentMethod || null,
      paymentReference: paymentReference || null,
      checkNumber: checkNumber || null,
      paidAt: paymentStatus === 'paid' ? Timestamp.now() : null,
      paidBy: paymentStatus === 'paid' ? (window.AppState?.uid || 'unknown') : null,
      notes: document.getElementById('ap-notes')?.value?.trim() || '',
      // TASK-9624 — a bill Smart Tools drafted is reviewed once someone saves it here.
      draft: false,
      updatedAt: Timestamp.now()
    };

    // TASK-9367 r2 — a check the SERVER printed (checkAllocationId) is owned by
    // apCheckPrint / apVoidCheck. Editing the bill must not rename or drop it
    // (switching to Partial used to null the number and orphan the check from
    // the register); firestore.rules refuses that write anyway. Void it instead.
    if (APState.editingInvoice?.checkAllocationId) delete data.checkNumber;

    // Upload pending documents
    const uploadedDocs = [];
    for (const pf of APState.pendingDocFiles) {
      const path = `tenants/${tenant}/apInvoices/${Date.now()}_${pf.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pf.blob, { contentType: pf.mimeType || pf.blob?.type || 'application/octet-stream' });
      const url = await getDownloadURL(storageRef);
      uploadedDocs.push({ id: pf.id, name: pf.name, url, mimeType: pf.mimeType, storagePath: path });
    }

    // Merge existing docs (minus deleted) + new uploads
    const existingDocs = (APState.editingInvoice?.documents || []).filter(d => !APState.deletedDocIds.includes(d.id));
    data.documents = [...existingDocs, ...uploadedDocs];

    // Delete removed docs from Storage
    for (const docId of APState.deletedDocIds) {
      const existing = (APState.editingInvoice?.documents || []).find(d => d.id === docId);
      if (existing?.storagePath) {
        try { await deleteObject(ref(storage, existing.storagePath)); } catch (e) { /* ignore */ }
      }
    }

    let invoiceId;
    // TASK-10145 — unlinking (or linking within the saved total) goes FIRST, so
    // a lower total / a new vendor is never refused for links being removed in
    // the same save (firestore.rules keep a linked bill's vendor and total).
    const _linkFirst = !!APState.editingInvoice && _linksChanged
      && (!_chargeLinks.ids.length || _chargeLinks.cents <= Math.round(Number(APState.editingInvoice.grandTotal || 0) * 100));
    if (_linkFirst) {
      const err = await _apSetChargeLinks(APState.editingInvoice.id, _chargeLinks.ids, _linkedBefore);
      if (err) { window.showToast?.(err, 'error'); return; }
    }
    if (APState.editingInvoice) {
      // Update existing
      invoiceId = APState.editingInvoice.id;
      await updateDoc(doc(db, 'tenants', tenant, 'apInvoices', invoiceId), data);
    } else {
      // Create new
      data.createdAt = Timestamp.now();
      data.createdBy = window.AppState?.uid || 'unknown';
      const docRef = await addDoc(collection(db, 'tenants', tenant, 'apInvoices'), data);
      invoiceId = docRef.id;
    }

    // Auto-sync costEntry for BI
    await syncCostEntry(tenant, invoiceId, data);

    if (_linksChanged && !_linkFirst) {
      const err = await _apSetChargeLinks(invoiceId, _chargeLinks.ids, _linkedBefore);
      if (err) {
        window.showToast?.('Bill saved, but the crew receipts were not linked: ' + err, 'warning');
        closeInvoiceModal();
        return;
      }
    }

    window.showToast?.('Invoice saved', 'success');
    closeInvoiceModal();
  } catch (err) {
    console.error('[AP] Save invoice error:', err);
    window.showToast?.('Failed to save invoice', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save Invoice'; }
  }
}

// ============================================
// COST ENTRY AUTO-SYNC (for BI P&L)
// ============================================
async function syncCostEntry(tenant, invoiceId, data) {
  try {
    // Use the AP invoice ID as the costEntry ID for 1:1 linking
    const costRef = doc(db, 'tenants', tenant, 'costEntries', `ap_${invoiceId}`);

    // Determine dominant category from line items
    const catTotals = {};
    for (const li of (data.lineItems || [])) {
      const cat = li.category || 'other';
      catTotals[cat] = (catTotals[cat] || 0) + (li.total || 0);
    }
    const dominantCategory = Object.entries(catTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || 'other';

    const costEntry = {
      source: 'ap_invoice',
      apInvoiceId: invoiceId,
      category: dominantCategory,
      amount: data.grandTotal || 0,
      date: data.invoiceDate,
      description: `AP: ${data.vendorName}${data.vendorInvoiceNumber ? ' #' + data.vendorInvoiceNumber : ''}`,
      vendorId: data.vendorId,
      vendorName: data.vendorName,
      jobId: data.jobId || null,
      jobName: data.jobName || null,
      vehicleId: data.assetId || null,
      vehicleDisplay: data.assetName || null,
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.uid || 'unknown'
    };

    // TASK-573: statement-imported invoices (bulkCreateStatementExpenses) sync
    // a costEntry whose source is 'statement_import', and firestore.rules pins
    // `source` immutable after create (TASK-565). Re-sending 'ap_invoice' over
    // an existing different source would put `source` in affectedKeys and the
    // rules would reject the ENTIRE update (silent costEntry drift on AP edits).
    // Preserve whatever source the existing entry carries.
    try {
      const existingSnap = await getDoc(costRef);
      const existingSource = existingSnap.exists() ? existingSnap.data()?.source : null;
      if (existingSource) costEntry.source = existingSource;
      // TASK-10145 — a bill that covers crew charge receipts: the server owns
      // this cost's amount (statement total minus those receipts) and re-syncs
      // it when the total changes; firestore.rules refuse a client amount here.
      if (existingSnap.exists() && Object.prototype.hasOwnProperty.call(existingSnap.data() || {}, 'chargeNettedCents')) delete costEntry.amount;
    } catch (e) { /* fall through with default source */ }

    await setDoc(costRef, costEntry, { merge: true });
  } catch (err) {
    console.error('[AP] Sync costEntry error:', err);
    // Non-fatal — invoice saved, cost sync failed
  }
}

// ============================================
// DELETE INVOICE
// ============================================
async function deleteInvoice(invoiceId) {
  if (_apMoneyEditBlocked()) return; // TASK-9491
  // TASK-9491 (COD Edge Case) — the rules let only the OWNER (tenants.ownerId)
  // delete a bill, but the linked `ap_` cost mirror below is deletable by any
  // billing editor. Refuse up front so a non-owner never strips the cost and
  // then fails on the bill, leaving a bill with no cost behind it.
  {
    const st = window.AppState || {};
    const isOwner = !!(st.user?.uid && st.tenantData?.ownerId && st.user.uid === st.tenantData.ownerId);
    if (!isOwner) { window.showToast?.('Only the owner can delete a bill — ask the owner.', 'warning'); return; }
  }
  if (!confirm('Delete this AP invoice? This cannot be undone.')) return;

  try {
    const tenant = APState.tenantId === 'demo-tenant' ? 'demo' : APState.tenantId;

    // TASK-9947 — a bill whose cost has a refund recorded against it keeps it
    // (firestore.rules refuse deleting that cost); say so before deleting anything.
    if (_creditsOf(`ap_${invoiceId}`).some(c => c.status !== 'rejected')) {
      window.showToast?.('This bill has a refund / credit recorded against it. Reject the refund first, then delete the bill.', 'warning');
      return;
    }
    // TASK-10145 — a statement bill that covers crew charge receipts: unlink
    // them first (server), so none is left pointing at a deleted bill.
    if (_apChargeLinkedIds(APState.invoices.find(i => i.id === invoiceId)).length) {
      // COD r1 (Edge): ask the SERVER summary for refunds before unlinking (the
      // cached credit list is empty until Company Expenses loads) — a bill whose
      // delete would then be refused must keep its links.
      try {
        if ((await getDoc(doc(db, 'tenants', tenant, 'expenseRefundTotals', `ap_${invoiceId}`))).exists()) {
          window.showToast?.('This bill has a refund / credit recorded against it. Reject the refund first, then delete the bill.', 'warning');
          return;
        }
      } catch (e) {
        window.showToast?.('Could not check this bill for refunds, so it was not deleted. Try again.', 'error');
        return;
      }
      const err = await _apSetChargeLinks(invoiceId, [], _apChargeLinkedIds(APState.invoices.find(i => i.id === invoiceId)));
      if (err) { window.showToast?.(err, 'error'); return; }
    }
    // Delete linked costEntry
    try { await deleteDoc(doc(db, 'tenants', tenant, 'costEntries', `ap_${invoiceId}`)); } catch (e) {
      // TASK-9947 — refused by rules (a refund points at it): stop, keep the bill.
      if (e?.code === 'permission-denied') { window.showToast?.('This bill\'s cost has a refund recorded against it, so the bill was not deleted.', 'warning'); return; }
    }

    // Delete documents from storage
    const inv = APState.invoices.find(i => i.id === invoiceId);
    if (inv?.documents) {
      for (const d of inv.documents) {
        if (d.storagePath) {
          try { await deleteObject(ref(storage, d.storagePath)); } catch (e) { /* ignore */ }
        }
      }
    }

    await deleteDoc(doc(db, 'tenants', tenant, 'apInvoices', invoiceId));
    window.showToast?.('Invoice deleted', 'success');
    closeInvoiceModal();
  } catch (err) {
    console.error('[AP] Delete error:', err);
    window.showToast?.('Failed to delete invoice', 'error');
  }
}

// ============================================
// CHECK REGISTER VIEW
// ============================================
function renderCheckRegister(area, checks) {
  // TASK-9367 r2 — one row per CHECK (active + voided history), not per bill.
  const sorted = _apCheckRows(checks).sort((a, b) => parseInt(b.checkNumber || 0) - parseInt(a.checkNumber || 0));

  // TASK-368 pattern (2026-05-28) — checks are derived from APState.invoices,
  // so the "No checks yet" empty-state was lying before the first onSnapshot
  // landed. Gate on the same _invoicesLoaded flag.
  if (!APState._invoicesLoaded) {
    area.innerHTML = `
      <div class="ap-loading-skeleton" style="margin-top:var(--spacing-lg);display:flex;flex-direction:column;gap:8px;opacity:0.55;">
        ${[1,2,3].map(() => `
          <div style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:14px 16px;display:flex;align-items:center;gap:12px;">
            <div style="width:36px;height:36px;border-radius:6px;background:var(--bg-elevated);flex-shrink:0;"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
              <div style="height:14px;width:50%;background:var(--bg-elevated);border-radius:4px;"></div>
              <div style="height:12px;width:30%;background:var(--bg-elevated);border-radius:4px;"></div>
            </div>
          </div>
        `).join('')}
        <div style="text-align:center;color:var(--text-secondary);font-size:0.85rem;margin-top:6px;">Loading checks…</div>
      </div>
    `;
    return;
  }

  if (!sorted.length) {
    area.innerHTML = `
      <div class="empty-state" style="padding:60px 20px;">
        <span class="material-icons empty-state-icon" style="font-size:56px;color:var(--text-muted);">receipt_long</span>
        <div class="empty-state-title" style="margin-top:12px;">No checks yet</div>
        <div class="empty-state-text">Checks will appear here when you print them from paid invoices.</div>
      </div>`;
    return;
  }

  // TASK-9367 — the RECORDED check amount (checkAmount), so the register ties
  // to the bank statement even when a check paid off a partial / credited bill.
  const totalAmount = sorted.reduce((s, r) => s + (r.voided ? 0 : r.amount), 0);
  const voidCount = sorted.filter(r => r.voided).length;

  area.innerHTML = `
    <div class="ap-summary-bar">
      <div class="ap-summary-item">
        <span class="ap-summary-label">Total Checks</span>
        <span class="ap-summary-value">${sorted.length}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Active Amount</span>
        <span class="ap-summary-value ap-summary-paid">$${totalAmount.toFixed(2)}</span>
      </div>
      ${voidCount ? `<div class="ap-summary-item">
        <span class="ap-summary-label">Voided</span>
        <span class="ap-summary-value" style="color:#DC2626;">${voidCount}</span>
      </div>` : ''}
      <div class="ap-summary-item">
        <button class="btn btn-secondary btn-sm" onclick="window.APModule.exportCheckRegister()" style="font-size:12px;">
          <span class="material-icons" style="font-size:14px;">download</span> Export CSV
        </button>
      </div>
    </div>

    <div class="ap-check-register-table">
      <div class="ap-cr-header">
        <span class="ap-cr-col-num">Check #</span>
        <span class="ap-cr-col-date">Date</span>
        <span class="ap-cr-col-payee">Payee</span>
        <span class="ap-cr-col-ref">Invoice Ref</span>
        <span class="ap-cr-col-amount">Amount</span>
        <span class="ap-cr-col-status">Status</span>
        <span class="ap-cr-col-actions"></span>
      </div>
      ${sorted.map(r => {
        const c = r.inv;
        const date = r.date;
        const isVoided = r.voided;
        const jsId = window.escJsAttr ? window.escJsAttr(c.id) : c.id;
        return `
          <div class="ap-cr-row ${isVoided ? 'ap-cr-voided' : ''}" onclick="window.APModule.editInvoice('${jsId}')">
            <span class="ap-cr-col-num" style="font-weight:600;">${escapeHtml(r.checkNumber)}</span>
            <span class="ap-cr-col-date">${date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</span>
            <span class="ap-cr-col-payee">${escapeHtml(c.vendorName || '')}</span>
            <span class="ap-cr-col-ref">${escapeHtml(r.ref)}</span>
            <span class="ap-cr-col-amount" style="${isVoided ? 'text-decoration:line-through;color:var(--text-secondary);' : ''}">$${r.amount.toFixed(2)}</span>
            <span class="ap-cr-col-status">
              ${isVoided
                ? '<span style="color:#DC2626;font-weight:600;font-size:11px;">VOID</span>'
                : '<span style="color:#10B981;font-weight:600;font-size:11px;">Printed</span>'}
            </span>
            <span class="ap-cr-col-actions" onclick="event.stopPropagation();">
              ${r.active ? `<button class="btn-icon" onclick="window.APModule.printCheck('${jsId}')" title="Reprint" style="padding:4px;">
                <span class="material-icons" style="font-size:16px;">print</span>
              </button>
              <button class="btn-icon" onclick="window.APModule.voidCheck('${jsId}')" title="Void Check" style="padding:4px;color:#DC2626;">
                <span class="material-icons" style="font-size:16px;">block</span>
              </button>` : ''}
            </span>
          </div>`;
      }).join('')}
    </div>
  `;
}

// ============================================
// CHECK PRINTING
// ============================================
// TASK-9338 — check numbers are allocated SERVER-SIDE by the `apCheckPrint`
// callable, in one transaction with the invoice stamps. The client used to
// print, stamp the invoice, then bump `tenants/{t}.settings.bankAccount
// .nextCheckNumber` — an admin-only write, so a billing user's bump failed into
// a log-only catch and the next check reused the number. Order is now:
// allocate (all-or-nothing) -> render the PDF with the number the server gave.
// Nothing prints if allocation fails; if the PDF fails after allocation the
// invoice already carries the number and Reprint re-renders it.
// Bank details come back in the callable response only; they no longer live on
// the tenant root (driver-readable) and a billing user never reads the
// admin-only financeSecure/checkPrinting doc directly.
let _checkPrintInFlight = false;

function _checkPrintTenant() {
  // APState.tenantId is only set once the AP view has rendered; the billing
  // Financial Setup wizard can call in before that, so fall back to the app's.
  const t = APState.tenantId || window.AppState?.tenant;
  return t === 'demo-tenant' ? 'demo' : t;
}

async function _checkPrintCall(payload) {
  // Page-init `functions, httpsCallable` from firebase-config.js (same SDK
  // identity as the rest of the module — see _callExpenseFn).
  const res = await httpsCallable(functions, 'apCheckPrint')({ tenantId: _checkPrintTenant(), ...payload });
  return res.data || {};
}

// 'absent' | 'invalid' | <positive integer> — the stored counter, classified.
function _counterState(v) {
  if (v === undefined || v === null) return 'absent';
  return Number.isInteger(v) && v > 0 ? v : 'invalid';
}

// Bank not set up. An admin gets the setup modal; a billing user (who can't
// read the admin-only bank doc) gets told who to ask instead of a dead modal.
async function _promptBankSetup() {
  const opened = await showBankSetup({ fromPrint: true });
  if (opened) window.showToast?.('Set up your bank account first', 'info');
}

function _checkPrintFail(e, fallback) {
  console.error('[AP] Check print error:', e);
  if (e?.details?.reason === 'bank-not-configured') {
    _promptBankSetup();
    return;
  }
  window.showToast?.(e?.message || fallback, 'error');
}

async function printCheck(invoiceId) {
  const inv = APState.invoices.find(i => i.id === invoiceId);
  if (!inv) { window.showToast?.('Invoice not found', 'error'); return; }
  if (_checkPrintInFlight) return;
  _checkPrintInFlight = true;
  try {
    const date = inv.paidAt?.toDate ? inv.paidAt.toDate() : (inv.invoiceDate?.toDate ? inv.invoiceDate.toDate() : new Date());
    // Payee + amount come from the server response (read from the stored
    // invoice inside the same transaction that recorded it), not this page's
    // possibly-stale copy.
    const checkOpts = (r) => ({
      bankAccount: r.bank,
      checkNumber: r.checkNumber,
      payee: r.payee || inv.vendorName || 'Unknown',
      amount: typeof r.amount === 'number' ? r.amount : (inv.checkNumber ? _apCheckAmount(inv) : _apOwed(inv)),
      date: date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      memo: inv.vendorInvoiceNumber ? `Inv #${inv.vendorInvoiceNumber}` : (inv.poNumber ? `PO ${inv.poNumber}` : ''),
      invoices: [{
        invoiceNumber: inv.vendorInvoiceNumber || inv.id.substring(0, 8),
        amount: typeof r.amount === 'number' ? r.amount : (inv.checkNumber ? _apCheckAmount(inv) : _apOwed(inv)),
        description: inv.notes || inv.lineItems?.[0]?.description || ''
      }]
    });

    // Reprint: the invoice already carries an ACTIVE check. Never allocates.
    // A legacy client-side void (checkVoided) is not active: print a new one
    // (the server files the dead number in checkHistory).
    if (inv.checkNumber && inv.checkVoided !== true) {
      let r;
      try { r = await _checkPrintCall({ action: 'reprint', invoiceId }); }
      catch (e) { _checkPrintFail(e, 'Could not load the check for reprint'); return; }
      const opts = checkOpts(r);
      // A batch check reprints WHOLE: every invoice the server found under this
      // number + vendor, with the server's total.
      if (Array.isArray(r.invoices) && r.invoices.length > 1) {
        opts.memo = `${r.invoices.length} invoices`;
        opts.invoices = r.invoices.map(x => {
          const local = APState.invoices.find(i => i.id === x.id) || {};
          return {
            invoiceNumber: local.vendorInvoiceNumber || x.id.substring(0, 8),
            amount: x.amount,
            description: local.notes || local.lineItems?.[0]?.description || ''
          };
        });
      }
      await previewAndPrintCheck(opts);
      return;
    }

    let status;
    try { status = await _checkPrintCall({ action: 'status' }); }
    catch (e) { _checkPrintFail(e, 'Could not check the bank setup'); return; }
    if (!status.configured) {
      await _promptBankSetup();
      return;
    }

    // First-time print on an unpaid invoice = also marks the invoice paid via
    // check. Confirm before changing financial state. The number shown is the
    // NEXT one; the server assigns the real one at print time (it can move if
    // someone else prints first), and the toast reports what was used.
    const willMarkPaid = inv.paymentStatus !== 'paid';
    if (willMarkPaid && typeof window.showConfirm === 'function') {
      const amount = _apOwed(inv).toFixed(2); // TASK-9367 — server pays the open balance (total − paid − credits)
      const vendor = inv.vendorName || 'this vendor';
      const ok = await window.showConfirm(
        'Print check + mark paid?',
        `Print the next check (#${status.nextCheckNumber}) for $${amount} to ${vendor} and mark this invoice as Paid (by Check)?`,
        { confirmText: 'Print + Mark Paid', cancelText: 'Cancel' }
      );
      if (!ok) return;
    }

    let alloc;
    try { alloc = await _checkPrintCall({ action: 'allocate', invoiceIds: [invoiceId] }); }
    catch (e) { _checkPrintFail(e, 'Check not printed — the check number could not be recorded'); return; }

    const printed = await previewAndPrintCheck(checkOpts(alloc));
    const markedPaid = !!alloc.invoices?.[0]?.markedPaid;
    if (printed) {
      window.showToast?.(markedPaid
        ? `Check #${alloc.checkNumber} printed; invoice marked paid`
        : `Check #${alloc.checkNumber} printed`, 'success');
    } else {
      window.showToast?.(`Check #${alloc.checkNumber} is recorded on this invoice but the PDF failed — use Reprint`, 'error');
    }
  } finally {
    _checkPrintInFlight = false;
  }
}

// Batch print: pay multiple invoices from same vendor with one check
async function batchPrintCheck(invoiceIds) {
  if (!invoiceIds?.length) { window.showToast?.('No invoices selected', 'error'); return; }

  const invoices = invoiceIds.map(id => APState.invoices.find(i => i.id === id)).filter(Boolean);
  if (!invoices.length) { window.showToast?.('Invoices not found', 'error'); return; }

  // Verify all same vendor (the server re-checks inside the transaction)
  const vendorId = invoices[0].vendorId;
  if (!invoices.every(i => i.vendorId === vendorId)) {
    window.showToast?.('Batch check must be for the same vendor', 'error');
    return;
  }
  if (_checkPrintInFlight) return;
  _checkPrintInFlight = true;
  try {
    let alloc;
    try { alloc = await _checkPrintCall({ action: 'allocate', invoiceIds: invoices.map(i => i.id) }); }
    catch (e) { _checkPrintFail(e, 'Check not printed — the check number could not be recorded'); return; }

    // Amounts + payee from the server (stored invoices, same transaction).
    const serverAmt = new Map((alloc.invoices || []).map(x => [x.id, x.amount]));
    const totalAmount = typeof alloc.amount === 'number' ? alloc.amount : invoices.reduce((s, i) => s + _apOwed(i), 0);
    const printed = await previewAndPrintCheck({
      bankAccount: alloc.bank,
      checkNumber: alloc.checkNumber,
      payee: alloc.payee || invoices[0].vendorName || 'Unknown',
      amount: totalAmount,
      date: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      memo: `${invoices.length} invoices`,
      invoices: invoices.map(inv => ({
        invoiceNumber: inv.vendorInvoiceNumber || inv.id.substring(0, 8),
        amount: typeof serverAmt.get(inv.id) === 'number' ? serverAmt.get(inv.id) : _apOwed(inv),
        description: inv.notes || inv.lineItems?.[0]?.description || ''
      }))
    });
    if (printed) {
      window.showToast?.(`Check #${alloc.checkNumber} printed for ${invoices.length} invoices ($${totalAmount.toFixed(2)})`, 'success');
    } else {
      window.showToast?.(`Check #${alloc.checkNumber} is recorded on ${invoices.length} invoices but the PDF failed — use Reprint`, 'error');
    }
  } finally {
    _checkPrintInFlight = false;
  }
}

// Void a check. TASK-282 MEDIUM (2026-05-18): voiding flips the invoice
// back to unpaid since voiding a check means the money never left — the
// invoice is still owed. Standard AP practice.
// TASK-9367 r2 — voiding runs SERVER-SIDE (apVoidCheck callable, one
// transaction): it checks the check is still this bill's active check,
// subtracts exactly that check from what was paid, files it in the bill's
// check history (it stays in the register as VOID with its own amount), and
// clears the bill's active check so a replacement can be printed. A batch
// check is one piece of paper: voiding it voids it on every bill it paid.
let _voidInFlight = false;
async function voidCheck(invoiceId) {
  if (_apMoneyEditBlocked()) return; // TASK-9491
  const inv = APState.invoices.find(i => i.id === invoiceId);
  if (!inv?.checkNumber || inv.checkVoided === true) { window.showToast?.('This bill has no active check to void', 'error'); return; }
  if (_voidInFlight) return;

  const amt = _apCheckAmount(inv);
  const paidByIt = inv.checkMarkedPaid === true
    || (inv.checkMarkedPaid === undefined && inv.paymentStatus === 'paid' && inv.paymentMethod === 'check');
  const remaining = paidByIt ? Math.max(0, _apCents(_apPaidAmount(inv)) - _apCents(amt)) / 100 : null;
  // The whole physical check (a batch check is one row in the register).
  const row = _apCheckRows(APState.invoices).find(r => r.active && r.invoices.some(i => i.id === inv.id));
  const batchCount = row ? row.invoices.length : 1;
  const effect = batchCount > 1
    ? `Check #${inv.checkNumber} is $${row.amount.toFixed(2)} across ${batchCount} bills; it is voided on all of them, and each bill's share comes off that bill's payments (back to Open or Partial).`
    : paidByIt
      ? (remaining > 0
          ? `The $${amt.toFixed(2)} comes off this bill's payments. $${remaining.toFixed(2)} paid earlier still counts, so the bill goes back to Partial.`
          : `The $${amt.toFixed(2)} comes off this bill's payments, so the bill goes back to Open.`)
      : `The bill's payment status is not changed (this check wasn't what paid it).`;
  const msg = `${effect} The check stays in the register marked VOID, and you can print a replacement.`;
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm(`Void check #${inv.checkNumber}?`, msg, { confirmText: 'Void check', cancelText: 'Cancel' })
    : confirm(`Void check #${inv.checkNumber}?\n\n${msg}`);
  if (!ok) return;

  _voidInFlight = true;
  try {
    const res = await httpsCallable(functions, 'apVoidCheck')({
      tenantId: _checkPrintTenant(), invoiceId, expectedCheckNumber: String(inv.checkNumber),
    });
    const d = res?.data || {};
    const reversed = (d.invoices || []).reduce((s, x) => s + (Number(x.paymentReversed) || 0), 0);
    window.showToast?.(
      reversed > 0
        ? `Check #${d.checkNumber || inv.checkNumber} voided — $${reversed.toFixed(2)} taken off what was paid. Print a replacement when ready.`
        : `Check #${d.checkNumber || inv.checkNumber} voided.`,
      'success'
    );
  } catch (e) {
    console.error('[AP] Void check error:', e);
    window.showToast?.(`Check not voided — ${e?.message || 'try again'}`, 'error');
  } finally {
    _voidInFlight = false;
  }
}

// Export check register as CSV
function exportCheckRegister() {
  // TASK-9367 r2 — one line per CHECK, voided ones included with their own
  // amount, so the export ties to the bank statement line for line.
  const sorted = _apCheckRows(APState.invoices).sort((a, b) => parseInt(a.checkNumber) - parseInt(b.checkNumber));
  if (!sorted.length) { window.showToast?.('No checks to export', 'info'); return; }
  const rows = [['Check #', 'Date', 'Payee', 'Amount', 'Invoice Ref', 'Status']];
  for (const r of sorted) {
    const c = r.inv;
    const date = r.date || new Date();
    rows.push([
      r.checkNumber,
      date.toLocaleDateString('en-US'),
      c.vendorName || '',
      r.amount.toFixed(2), // TASK-9367 — the amount ON that check (batch = summed)
      r.ref,
      r.voided ? 'VOID' : 'Printed'
    ]);
  }

  const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `check-register-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
  window.showToast?.(`Exported ${sorted.length} checks`, 'success');
}

// ============================================
// BANK ACCOUNT SETUP
// ============================================
// Returns true when the modal opened (admin), false otherwise.
async function showBankSetup(opts = {}) {
  const body = document.getElementById('bank-setup-modal-body');
  if (!body) return false;

  // Load existing bank account settings.
  // TASK-9338: bank details live in the ADMIN-ONLY financeSecure/checkPrinting
  // doc (was tenants/{t}.settings.bankAccount — driver-readable). A billing
  // user can still PRINT (via the apCheckPrint callable) but can't open this.
  let bankAccount = {};
  try {
    const snap = await getDoc(doc(db, 'tenants', _checkPrintTenant(), 'financeSecure', 'checkPrinting'));
    if (snap.exists()) {
      bankAccount = snap.data() || {};
    }
  } catch (e) {
    if (e?.code === 'permission-denied') {
      window.showToast?.(opts.fromPrint
        ? 'Check printing isn\'t set up yet — ask an owner or admin to add the bank account under AP > Bank'
        : 'Only an owner or admin can set up bank details for check printing', 'info');
      return false;
    }
    console.error('[AP] Load bank account error:', e);
    window.showToast?.('Could not load bank details', 'error');
    return false;
  }
  // The number the form shows. saveBankSetup only writes the counter when the
  // admin CHANGES this, and refuses if a check was printed since it loaded.
  // Absent -> 1001 (first setup). Present but INVALID -> null: the field shows
  // blank, so the admin must type a real number rather than a save quietly
  // restarting the sequence at 1001.
  APState._bankSetupLoadedState = _counterState(bankAccount.nextCheckNumber);
  APState._bankSetupLoadedNext = APState._bankSetupLoadedState === 'absent' ? 1001
    : (APState._bankSetupLoadedState === 'invalid' ? null : APState._bankSetupLoadedState);

  body.innerHTML = `
    <div class="modal-header">
      <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">account_balance</span>Bank Account Setup</h3>
      <button class="btn-icon" onclick="window.APModule.closeBankSetup()">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body" style="padding:20px 24px;flex:1;overflow-y:auto;min-height:0;">
      <p style="font-size:0.8125rem;color:var(--text-secondary);margin:0 0 16px;">Configure your checking account for check printing and reconciliation.</p>

      <div class="form-group">
        <label>Company Name *</label>
        <input type="text" id="bank-company-name" value="${escapeHtml(bankAccount.companyName || '')}" placeholder="Tripp In Trucking LLC" maxlength="100">
      </div>

      <div class="form-group">
        <label>Company Address</label>
        <input type="text" id="bank-address-1" value="${escapeHtml(bankAccount.address1 || '')}" placeholder="123 Main Street" maxlength="100" style="margin-bottom:6px;">
        <input type="text" id="bank-address-2" value="${escapeHtml(bankAccount.address2 || '')}" placeholder="Suite 100" maxlength="100" style="margin-bottom:6px;">
        <input type="text" id="bank-address-3" value="${escapeHtml(bankAccount.address3 || '')}" placeholder="Williston, ND 58801" maxlength="100">
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Bank Name *</label>
          <input type="text" id="bank-name" value="${escapeHtml(bankAccount.bankName || '')}" placeholder="First International Bank" maxlength="100">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Routing Number *</label>
          <input type="text" id="bank-routing" value="${escapeHtml(bankAccount.routingNumber || '')}" placeholder="091000019" maxlength="9" inputmode="numeric" pattern="[0-9]*">
        </div>
        <div class="form-group">
          <label>Account Number *</label>
          <input type="text" id="bank-account" value="${escapeHtml(bankAccount.accountNumber || '')}" placeholder="1234567890" maxlength="17" inputmode="numeric">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Starting Check Number *</label>
          <input type="number" id="bank-start-check" value="${APState._bankSetupLoadedNext ?? ''}" min="1" step="1" style="max-width:160px;">
          <span style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;display:block;">Next check will use this number</span>
        </div>
      </div>

      <div style="background:#FEF3C7;border-radius:8px;padding:12px;margin-top:12px;">
        <div style="display:flex;gap:8px;align-items:flex-start;">
          <span class="material-icons" style="font-size:18px;color:#92400E;">info</span>
          <div style="font-size:0.8125rem;color:#92400E;">
            <strong>Security note:</strong> Only owners and admins can see or change these details. Anyone with Billing access can print checks, and a printed check shows the routing and account numbers. Bank details are never sent to any AI or external service. For check printing, you'll need MICR toner in your laser printer.
          </div>
        </div>
      </div>
    </div>

    <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom, #F8FAFC, #fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);display:flex;gap:12px;justify-content:flex-end;flex-shrink:0;">
      <button type="button" class="btn btn-secondary" onclick="window.APModule.closeBankSetup()">Cancel</button>
      <button type="button" class="btn btn-primary" id="bank-save-btn" onclick="window.APModule.saveBankSetup()">
        <span class="material-icons" style="font-size:16px;">save</span> Save Bank Account
      </button>
    </div>
  `;

  document.getElementById('modal-bank-setup')?.classList.remove('hidden');
  return true;
}

function closeBankSetup() {
  document.getElementById('modal-bank-setup')?.classList.add('hidden');
}

async function saveBankSetup() {
  const companyName = document.getElementById('bank-company-name')?.value?.trim();
  const bankName = document.getElementById('bank-name')?.value?.trim();
  const routingNumber = document.getElementById('bank-routing')?.value?.trim();
  const accountNumber = document.getElementById('bank-account')?.value?.trim();
  // TASK-9338: no silent `|| 1001` — a blank or bad field used to quietly
  // restart the sequence at 1001 and reprint numbers already used.
  const nextRaw = String(document.getElementById('bank-start-check')?.value ?? '').trim();
  const nextCheckNumber = /^\d+$/.test(nextRaw) ? parseInt(nextRaw, 10) : NaN;
  if (!Number.isInteger(nextCheckNumber) || nextCheckNumber < 1 || nextCheckNumber > 99999999) {
    window.showToast?.('Next check number must be a whole number of 1 or more', 'error'); return;
  }

  if (!companyName) { window.showToast?.('Company name is required', 'error'); return; }
  if (!bankName) { window.showToast?.('Bank name is required', 'error'); return; }
  if (!routingNumber || !/^\d{9}$/.test(routingNumber)) { window.showToast?.('Routing number must be 9 digits', 'error'); return; }
  if (!accountNumber) { window.showToast?.('Account number is required', 'error'); return; }

  const btn = document.getElementById('bank-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    // TASK-9338: admin-only financeSecure/checkPrinting, never the tenant root.
    // One transaction so the COUNTER is only written when the admin changed the
    // number on the form — a plain overwrite would roll the counter back over
    // any check a billing user printed while this form was open (= duplicate
    // check numbers, the exact bug this task closes).
    const ref = doc(db, 'tenants', _checkPrintTenant(), 'financeSecure', 'checkPrinting');
    if (APState._bankSetupLoadedState === undefined) {
      window.showToast?.('Reopen Bank setup and try again', 'error'); return;
    }
    const loadedState = APState._bankSetupLoadedState;
    const loadedNext = APState._bankSetupLoadedNext; // what the form showed (null = blank)
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const cur = snap.exists() ? (snap.data() || {}) : null;
      const curState = _counterState(cur?.nextCheckNumber);
      const payload = {
        companyName,
        address1: document.getElementById('bank-address-1')?.value?.trim() || '',
        address2: document.getElementById('bank-address-2')?.value?.trim() || '',
        address3: document.getElementById('bank-address-3')?.value?.trim() || '',
        bankName,
        routingNumber,
        accountNumber,
        updatedAt: Timestamp.now(),
        updatedBy: window.AppState?.uid || 'unknown'
      };
      // Write the counter only when the admin changed the number, or when there
      // is no valid stored counter to keep (first setup / invalid value the
      // admin just re-typed). Either way the stored state must be what the form
      // loaded — otherwise a check was printed (or someone else saved) meanwhile.
      const mustWrite = nextCheckNumber !== loadedNext || curState === 'absent' || curState === 'invalid';
      if (mustWrite) {
        if (curState !== loadedState) {
          const err = new Error('stale-counter');
          err.code = 'stale-counter';
          err.curNext = typeof curState === 'number' ? curState : '?';
          throw err;
        }
        payload.nextCheckNumber = nextCheckNumber;
      }
      tx.set(ref, payload, { merge: true });
    });

    window.showToast?.('Bank account saved', 'success');
    closeBankSetup();
  } catch (err) {
    if (err?.code === 'stale-counter') {
      window.showToast?.(`A check was printed while this was open — the next check is now #${err.curNext}. Nothing saved; reopen Bank to change the number.`, 'error');
    } else if (err?.code === 'permission-denied') {
      window.showToast?.('Only an owner or admin can change bank details', 'error');
    } else {
      console.error('[AP] Save bank account error:', err);
      window.showToast?.('Failed to save bank account', 'error');
    }
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save Bank Account'; }
  }
}

// Is check printing set up? (used by the billing Financial Setup wizard).
// TASK-9338: goes through the apCheckPrint callable so it works for billing
// users too; it never returns bank numbers. Any failure reads as "not set up".
async function hasCheckPrintingSetup() {
  try {
    const status = await _checkPrintCall({ action: 'status' });
    return !!status.configured;
  } catch (e) {
    console.warn('[AP] check-print status unavailable:', e?.message);
    return false;
  }
}

function onInvoiceDateChange() {
  // Recalculate due date if vendor has payment terms and due date is empty
  const dueDateEl = document.getElementById('ap-due-date');
  if (dueDateEl && !dueDateEl.value) {
    onVendorChange(); // reuses vendor terms logic
  }
}

// ============================================
// QUICKBOOKS IIF EXPORT (AP Bills + Bill Payments)
// ============================================
function iifEsc(val) {
  if (val == null) return '';
  return String(val).replace(/[\t\r\n]/g, ' ').trim();
}

function downloadIIF(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatDateMMDDYYYY(d) {
  const date = d?.toDate ? d.toDate() : (d instanceof Date ? d : new Date(d));
  if (isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

// Map AP categories to QB expense accounts
function getQBExpenseAccount(category) {
  const settings = window.AppState?.settings || {};
  const apMap = settings.qbAPCategoryMap || {};
  if (apMap[category]) return apMap[category];

  // Default mappings
  const defaults = {
    fuel: 'Fuel Expense',
    tolls: 'Tolls Expense',
    lodging: 'Travel & Lodging',
    materials: 'Materials & Supplies',
    permits: 'Permits & Licenses',
    insurance: 'Insurance Expense',
    shop: 'Repairs & Maintenance',
    other: 'Miscellaneous Expense'
  };
  return defaults[category?.toLowerCase()] || 'Miscellaneous Expense';
}

// Export AP invoices as QB BILL transactions
function exportAPBillsIIF() {
  const settings = window.AppState?.settings || {};
  const apAccount = iifEsc(settings.qbAccountsPayable || 'Accounts Payable');

  // Get filtered invoices (use current filter, or all if on 'all')
  let invoices = getFilteredInvoices().filter(i => !i.draft); // TASK-9624: a Draft is not exported until saved
  if (!invoices.length) { window.showToast?.('No invoices to export', 'info'); return; }

  const T = '\t';
  const lines = [];

  // Headers
  lines.push(['!TRNS', 'TRNSID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO', 'DUEDATE'].join(T));
  lines.push(['!SPL', 'SPLID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO'].join(T));
  lines.push('!ENDTRNS');

  let count = 0;
  for (const inv of invoices) {
    const vendor = iifEsc(inv.vendorName || 'Unknown');
    const invDate = formatDateMMDDYYYY(inv.invoiceDate);
    const dueDate = inv.dueDate ? formatDateMMDDYYYY(inv.dueDate) : invDate;
    const docNum = iifEsc(inv.vendorInvoiceNumber || inv.poNumber || inv.id.substring(0, 8));
    const memo = iifEsc(inv.notes || `AP: ${inv.vendorName}`);
    const total = inv.grandTotal || 0;

    // TRNS line — AP account (credit)
    lines.push([
      'TRNS', '', 'BILL', invDate, apAccount, vendor,
      (-total).toFixed(2), docNum, memo, dueDate
    ].join(T));

    // SPL lines — expense accounts (debit) per line item
    if (inv.lineItems?.length) {
      for (const li of inv.lineItems) {
        if (!li.total) continue;
        const expAcct = iifEsc(getQBExpenseAccount(li.category));
        const liMemo = iifEsc(li.description || li.category || '');
        lines.push([
          'SPL', '', 'BILL', invDate, expAcct, vendor,
          li.total.toFixed(2), docNum, liMemo
        ].join(T));
      }
    } else {
      // No line items — single expense line
      lines.push([
        'SPL', '', 'BILL', invDate, iifEsc('Miscellaneous Expense'), vendor,
        total.toFixed(2), docNum, memo
      ].join(T));
    }

    lines.push('ENDTRNS');
    count++;
  }

  const today = new Date().toISOString().split('T')[0];
  downloadIIF(`ap-bills-${today}.iif`, lines.join('\n'));
  window.showToast?.(`Exported ${count} AP bill${count !== 1 ? 's' : ''} to IIF`, 'success');
}

// Export a single AP payment as QB BILLPMT transaction
function exportAPPaymentIIF(invoiceId) {
  const inv = APState.invoices.find(i => i.id === invoiceId);
  if (!inv) { window.showToast?.('Invoice not found', 'error'); return; }
  if (inv.paymentStatus !== 'paid') { window.showToast?.('Invoice is not paid', 'error'); return; }

  const settings = window.AppState?.settings || {};
  const apAccount = iifEsc(settings.qbAccountsPayable || 'Accounts Payable');
  const checkingAccount = iifEsc(settings.qbCheckingAccount || 'Checking');

  const T = '\t';
  const vendor = iifEsc(inv.vendorName || 'Unknown');
  // TASK-9367 — the payment is the cash that went out (paidAmount — earlier
  // partial payments + the check), never grandTotal. The BILL export keeps the
  // full total; the vendor credit itself is not exported yet (enter it in
  // QuickBooks by hand).
  const amount = _apPaidAmount(inv);
  const paidDate = formatDateMMDDYYYY(inv.paidAt || inv.invoiceDate);
  const docNum = iifEsc(inv.checkNumber || inv.vendorInvoiceNumber || inv.id.substring(0, 8));
  const memo = inv.checkNumber ? `Check #${inv.checkNumber}` : (inv.paymentMethod === 'ach' ? `ACH ${inv.paymentReference || ''}` : 'Payment');

  const lines = [];
  lines.push(['!TRNS', 'TRNSID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO'].join(T));
  lines.push(['!SPL', 'SPLID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO'].join(T));
  lines.push('!ENDTRNS');

  // TRNS — debit checking account
  lines.push([
    'TRNS', '', 'BILLPMT', paidDate, checkingAccount, vendor,
    (-amount).toFixed(2), docNum, iifEsc(memo)
  ].join(T));

  // SPL — credit AP
  lines.push([
    'SPL', '', 'BILLPMT', paidDate, apAccount, vendor,
    amount.toFixed(2), docNum, iifEsc(memo)
  ].join(T));

  lines.push('ENDTRNS');

  downloadIIF(`${docNum}-payment.iif`, lines.join('\n'));
  window.showToast?.('Payment IIF downloaded', 'success');
}

// ============================================
// HELPERS
// ============================================
function escapeHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatDate(d) {
  if (!d) return '';
  const date = d instanceof Date ? d : (d?.toDate ? d.toDate() : new Date(d));
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateInput(d) {
  const date = d instanceof Date ? d : (d?.toDate ? d.toDate() : new Date(d));
  if (isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function capitalizeFirst(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

function onVendorChange() {
  const vendorId = document.getElementById('ap-vendor')?.value;
  // TASK-10141 — charge-account notice follows the picked vendor.
  const _chargeNotice = document.getElementById('ap-charge-notice');
  if (_chargeNotice) _chargeNotice.innerHTML = _apChargeNoticeHtml(vendorId);
  if (!vendorId) return;

  const vendor = APState.vendors.find(v => v.id === vendorId);
  if (!vendor?.billingTerms) return;

  // Auto-calculate due date from invoice date + vendor payment terms
  const invoiceDateVal = document.getElementById('ap-invoice-date')?.value;
  if (!invoiceDateVal) return;

  const dueDateEl = document.getElementById('ap-due-date');
  if (!dueDateEl || dueDateEl.value) return; // Don't overwrite existing due date

  const invoiceDate = new Date(invoiceDateVal + 'T12:00:00');
  const terms = vendor.billingTerms;
  let daysToAdd = 0;
  if (terms === 'Due on Receipt') daysToAdd = 0;
  else if (terms === 'NET15') daysToAdd = 15;
  else if (terms === 'NET30') daysToAdd = 30;
  else if (terms === 'NET45') daysToAdd = 45;
  else if (terms === 'NET60') daysToAdd = 60;

  if (daysToAdd > 0) {
    const dueDate = new Date(invoiceDate);
    dueDate.setDate(dueDate.getDate() + daysToAdd);
    dueDateEl.value = formatDateInput(dueDate);
  } else if (terms === 'Due on Receipt') {
    dueDateEl.value = invoiceDateVal;
  }
}

// ============================================
// BANK BALANCE (Plaid)
// ============================================
async function fetchBankBalance() {
  const badge = document.getElementById('ap-bank-balance-badge');
  if (!badge) return;

  if (!window.AppState?.tenantData?.plaidConnected) {
    badge.innerHTML = '';
    return;
  }

  badge.innerHTML = `<span style="font-size:12px;color:var(--text-secondary);">Loading balance...</span>`;

  try {
    // Use page-init `functions, httpsCallable` from firebase-config.js (10.8.0).
    // Dynamic 10.7.1 imports break SDK identity (E2E 2026-05-12).
    const fn = httpsCallable(functions, 'plaidGetBalance');
    const res = await fn({});

    const bal = res.data?.balance;
    const avail = res.data?.available;
    if (bal !== undefined) {
      const fmtBal = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(bal);
      const fmtAvail = avail !== undefined ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(avail) : null;
      const inst = window.AppState?.tenantData?.plaidInstitution || 'Bank';
      const last4 = window.AppState?.tenantData?.plaidAccountLast4 || '';

      // Check count
      const checksOutstanding = APState.invoices.filter(i => i.paymentMethod === 'check' && i.checkNumber && !i.checkVoided && i.paymentStatus === 'paid' && !i.checkCleared).length;

      badge.innerHTML = `
        <div style="display:inline-flex;align-items:center;gap:8px;padding:4px 12px;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:16px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;color:#10B981;">account_balance</span>
          <span style="color:#065F46;font-weight:600;">${fmtBal}</span>
          <span style="color:#047857;">${inst} ••${last4}</span>
          ${checksOutstanding > 0 ? `<span style="color:#D97706;font-weight:500;">· ${checksOutstanding} check${checksOutstanding !== 1 ? 's' : ''} out</span>` : ''}
          ${res.data?.cached ? '<span style="color:#94A3B8;" title="Cached balance">●</span>' : ''}
        </div>`;
    }
  } catch (err) {
    console.error('[AP] Balance fetch error:', err);
    badge.innerHTML = '';
  }
}

// ============================================
// WINDOW EXPORTS
// ============================================
// ============================================
// DRIVER EXPENSES PANEL (filter pill: driverExpenses)
// ============================================
function _driverExpenseAmount(e) { return Number(e?.amount) || 0; }

// TASK-604 (COD Adversary/Supply-Chain): escapeHtml neutralizes HTML but does
// NOT block a `javascript:`/`data:` scheme in an href — a stored receiptUrl
// could execute JS in another admin's session when they click "View receipt".
// Allowlist http(s) only; anything else collapses to a non-navigating '#'.
function _safeReceiptHref(url) {
  return /^https?:\/\//i.test(String(url || '')) ? escapeHtml(url) : '';
}
// ---------------------------------------------------------------------------
// TASK-9213 — expense card presentation helpers (.de-* card language).
// Chris 2026-09-14: the queue "looks like an Excel sheet" — every row was the
// same white-on-white slab. These four helpers supply the three things that
// were missing and that the compliance portal's action queue gets right:
// a per-CATEGORY color, a per-STATUS left rail, and meta as chips not prose.
// ---------------------------------------------------------------------------

// Tint family per category. Keyed on the category VALUE (the slug drivers pick
// in jobboard.js), which is tenant-editable — anything unrecognised falls back
// to neutral slate rather than guessing a color that would lie.
const _DE_CATEGORY_TINTS = {
  fuel: 'fuel', def: 'def', parts: 'parts', tolls: 'tolls',
  meals: 'meals', materials: 'materials', lodging: 'lodging',
  // 'other' gets a real tint, not the neutral-unknown slate. Measured on live
  // TITS 2026-09-14: 'other' is 24 of 78 driver/staff costEntries — the SECOND
  // most common category. Leaving it on the unknown swatch put a third of the
  // list straight back into the "everything is the same color" complaint.
  other: 'other',
};

// A category whose configured icon is the placeholder. 'more_horiz' is what the
// default category list (and TITS's saved copy) ships for "Other", and at 38px
// it renders as three dots — a tile that looks unloaded rather than categorised.
const _DE_PLACEHOLDER_ICONS = new Set(['more_horiz', 'category', 'label', '']);

// Resolve {icon, tint} for an expense. Tenant categories carry their own icon
// (settings.driverExpenses.categories[].icon) — prefer that over the default
// table so a custom "Parking" category doesn't render as a generic dot.
function _deCategoryMeta(e) {
  const val = String(e?.category || '').trim().toLowerCase();
  const tenantCats = window.AppState?.settings?.driverExpenses?.categories;
  const list = Array.isArray(tenantCats) && tenantCats.length ? tenantCats : _DEFAULT_EXPENSE_CATS;
  const hit = list.find(c => String(c?.value || '').toLowerCase() === val);
  const icon = String(hit?.icon || '').trim();
  return {
    // Substitute at RENDER time only — never rewrite the tenant's saved
    // categories list, which is also what the driver's dropdown reads.
    icon: _DE_PLACEHOLDER_ICONS.has(icon) ? 'receipt_long' : (icon || 'receipt_long'),
    tint: _DE_CATEGORY_TINTS[val] || 'shop',
    label: hit?.label || (val ? val.charAt(0).toUpperCase() + val.slice(1) : ''),
  };
}

// Initials for the group avatar. Two letters max; never renders empty (a blank
// gradient puck reads as a loading state).
function _deInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Which left rail this row gets. `owed` deliberately OUTRANKS `approved`:
// an approved-but-unpaid reimbursement is a committed liability, and that is
// the thing a reviewer scanning the list needs to see (same predicate as
// _reimbOwed, so the rail can't disagree with the badge or with BI).
function _deStatusClass(e) {
  const pc = _personalOf(e); // TASK-9917 — owed back = an open balance; owner draw = settled
  if (pc && APState.receiptsOnly === true) return _isOwnPersonalCharge(e) && _personalOutstanding(e) > 0 ? 's-owed' : 's-approved'; // s4 (c): someone else's is neutral
  if (pc) return pc.payer === 'employee' && _personalOutstanding(e) > 0 ? 's-owed' : 's-approved';
  if (e.status === 'rejected') return 's-rejected';
  if (e.status === 'pending_ocr') return 's-parsing';
  if (_reimbOwed(e)) return 's-owed';
  if (e.status === 'approved') return 's-approved';
  if (e.status === 'clarification_requested') return 's-await';
  return 's-pending';
}

// Meta as discrete chips. The old renderer joined everything with " · ", which
// is exactly what made the row read as a spreadsheet cell. `job` gets the only
// accent because it's the one fact that changes a billing decision.
// TASK-9384 — an expense stores costAllocationId (the stable key) AND
// costAllocationLabel (the name at the time it was filed). Rename changes the
// allocation doc's label only; it never rewrites cost entries (same rule as
// TASK-9390 job types: rename changes the label, never rewrites history). Live
// views resolve id -> CURRENT label from the tenant's list so a renamed Shop
// shows as "Yard" on old and new rows alike; the stored snapshot stays on the
// entry and is the fallback when the id is unknown (deleted row, list not
// loaded, or a legacy entry with no id). Search matches either name.
const _allocIndexCache = new WeakMap();
function _currentAllocLabel(e) {
  const snapLabel = (e && typeof e.costAllocationLabel === 'string') ? e.costAllocationLabel : '';
  const id = e?.costAllocationId;
  const list = APState.costAllocations;
  if (!id || !Array.isArray(list) || list.length === 0) return snapLabel;
  let idx = _allocIndexCache.get(list);
  if (!idx) {
    idx = new Map();
    for (const a of list) if (a && a.id) idx.set(a.id, a);
    _allocIndexCache.set(list, idx);
  }
  const a = idx.get(id);
  const live = (a && typeof a.label === 'string') ? a.label.trim() : '';
  return live || snapLabel;
}
// TASK-9493 — a split receipt has no single allocation: its lines each name
// one (or a job / vehicle). Show every line with its share so the reviewer
// sees exactly where the money lands before approving. Unsplit entries fall
// straight through to the unchanged single-label path below.
function _splitLinesOf(e) {
  if (!window.CostSplit || !window.CostSplit.isSplitCostEntry(e)) return null;
  return window.CostSplit.getEffectiveCostAllocations(e);
}
function _splitLineLabel(l) {
  return _currentAllocLabel(l) || l.vehicleDisplay || l.jobName || 'Unassigned';
}
function _allocLabelHtml(e) {
  const _lines = _splitLinesOf(e);
  if (_lines) {
    return 'Split: ' + _lines.map(l => `${escapeHtml(_splitLineLabel(l))} ${escapeHtml(window.CostSplit.formatCents(window.CostSplit.toCents(l.amount)))}`).join(' · ');
  }
  const now = _currentAllocLabel(e);
  if (!now) return '';
  const was = (typeof e?.costAllocationLabel === 'string') ? e.costAllocationLabel.trim() : '';
  // Renamed since this was filed: show the current name, keep the recorded one visible on hover.
  if (was && was !== now) return `<span title="${escapeHtml('Recorded as: ' + was)}">${escapeHtml(now)}</span>`;
  return escapeHtml(now);
}
function _allocSearchText(e) {
  const _lines = _splitLinesOf(e); // TASK-9493 — search matches any split line
  if (_lines) return _lines.map(l => `${_splitLineLabel(l)} ${l.jobName || ''} ${l.vehicleDisplay || ''}`).join(' ').toLowerCase();
  return (_currentAllocLabel(e) + ' ' + (e?.costAllocationLabel || '')).toLowerCase();
}

// TASK-10141 — an expense the crew put on a vendor CHARGE ACCOUNT (paid later
// on the vendor's statement): not a card charge, not owed back to anyone.
// A typed vendor that is not set up in CRM is flagged for the office to link.
// TASK-10145 — once the office links it to the vendor's statement bill it says
// which bill covers it (the bill's cost books only what such receipts did not).
function _chargeAccountMetaHtml(e) {
  if (!e || e.paidOnAccount !== true) return '';
  const name = escapeHtml(String(e.chargeVendorName || 'a vendor'));
  const on = e.chargeBillId ? ` · covered by ${_apBillRefHtml(e.chargeBillId)}` : '';
  if (e.chargeVendorUnlinked === true) {
    return `<span style="color:#B45309;font-weight:600;" title="The crew typed this vendor. Set it up in CRM → the vendor → Charge account so the next one links.">Charge account at ${name} — vendor not set up</span>${on}`;
  }
  return `Charged on account at ${name}${on}`;
}
// "bill #123" / "the bill of Oct 1, 2026" for a bill id (escaped).
function _apBillRefHtml(billId) {
  const b = (APState.invoices || []).find(i => i && i.id === billId);
  if (!b) return 'a vendor statement bill';
  if (b.vendorInvoiceNumber) return 'bill #' + escapeHtml(String(b.vendorInvoiceNumber));
  const d = b.invoiceDate && b.invoiceDate.toDate ? b.invoiceDate.toDate() : null;
  return 'the bill of ' + escapeHtml(d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'unknown date');
}

// TASK-10141 / TASK-10145 — crew charge receipts that can sit on THIS vendor's
// statement: charged at it (or typed with its name — same key as
// billerQuestions.vendorKey), not a refund, not rejected, in the last 60 days
// and not on another bill — plus every receipt already linked to the bill being
// edited (any age), so none can drop off on save. Receipts on another bill are
// listed separately, read-only.
const AP_CHARGE_NOTICE_DAYS = 60;
function _apVendorKey(name) {
  return String(name || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}
// Mirrors functions/src/services/chargeStatement.js countsElsewhere: only a
// receipt that counts as a cost (or as a personal charge) may be newly linked.
function _apChargeCounts(e) {
  if (!e || e.entryType === 'credit') return false;
  if (e.personalCharge && (e.personalCharge.payer === 'owner' || e.personalCharge.payer === 'employee')) return true;
  if ((e.source === 'driver' || e.source === 'staff') && ['rejected', 'pending', 'clarification_requested', 'pending_ocr'].includes(e.status)) return false;
  return true;
}
function _apChargeLinkedIds(inv) {
  return inv && Array.isArray(inv.chargeReceiptIds) ? inv.chargeReceiptIds.map(String) : [];
}
function _apChargeReceiptsFor(vendorId) {
  if (!vendorId) return { list: [], elsewhere: [] };
  const since = Date.now() - AP_CHARGE_NOTICE_DAYS * 86400000;
  const vRec = (APState.vendors || []).find(x => x && x.id === vendorId);
  const vKey = vRec ? _apVendorKey(vRec.name) : '';
  const billId = APState.editingInvoice ? APState.editingInvoice.id : null;
  const mine = new Set(_apChargeLinkedIds(APState.editingInvoice));
  const seen = new Set();
  const list = [];
  const elsewhere = [];
  [...(APState.driverExpenses || []), ...(APState.companyExpenses || [])].forEach((e) => {
    if (!e || !e.id || seen.has(e.id)) return;
    seen.add(e.id);
    if (e.paidOnAccount !== true || e.entryType === 'credit') return;
    const linkedHere = !!billId && (e.chargeBillId === billId || mine.has(String(e.id)));
    const typedHere = e.chargeVendorUnlinked === true && !!vKey && _apVendorKey(e.chargeVendorName) === vKey;
    if (e.chargeVendorId !== vendorId && !typedHere && !linkedHere) return;
    if (e.chargeBillId && !linkedHere) { elsewhere.push(e); return; }
    if (!linkedHere && e.status === 'rejected') return;
    const d = e.date?.toMillis ? e.date.toMillis() : (e.submittedAt?.toMillis ? e.submittedAt.toMillis() : 0);
    if (!linkedHere && d && d < since) return;
    list.push(e);
  });
  return { list, elsewhere };
}
function _apChargeNoticeHtml(vendorId) {
  if (!vendorId) return '';
  const v = (APState.vendors || []).find(x => x && x.id === vendorId);
  const { list, elsewhere } = _apChargeReceiptsFor(vendorId);
  if (!(v && v.chargeAccount === true) && !list.length && !elsewhere.length) return '';
  const box = 'margin-top:8px;padding:10px 12px;border:1px solid #FCD34D;background:#FEF3C7;color:#92400E;border-radius:8px;font-size:0.85rem;line-height:1.4;';
  if (!list.length && !elsewhere.length) {
    return `<div style="${box}"><strong>Charge account.</strong> No crew receipts charged here are waiting. Receipts the crew charges here become expenses when they turn them in.</div>`;
  }
  const billId = APState.editingInvoice ? APState.editingInvoice.id : null;
  const mine = new Set(_apChargeLinkedIds(APState.editingInvoice));
  const row = (e, note) => {
    const d = e.date?.toDate ? e.date.toDate() : (e.submittedAt?.toDate ? e.submittedAt.toDate() : null);
    const ds = d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    const c = Math.round((Number(e.amount) || 0) * 100);
    const who = [e.personName, e.description].filter(Boolean).map(x => escapeHtml(String(x))).join(' · ');
    const linked = !!billId && (e.chargeBillId === billId || mine.has(String(e.id)));
    const counts = _apChargeCounts(e);
    const ok = linked || counts;
    const input = note
      ? '<span class="material-icons" style="font-size:18px;">link</span>'
      : `<input type="checkbox" class="ap-charge-net" value="${escapeHtml(String(e.id))}" data-cents="${c}"${linked ? ' checked' : ''}${ok ? '' : ' disabled'} style="width:20px;height:20px;">`;
    const tail = note ? ` · <em>${note}</em>`
      : (!ok ? ' · <em>approve it first</em>' : (linked && !counts ? ' · <em>not approved — this bill counts it for now</em>' : ''));
    return `<label style="display:flex;gap:8px;align-items:center;min-height:44px;cursor:${!note && ok ? 'pointer' : 'default'};">${input}<span>${escapeHtml(ds)} · $${(c / 100).toFixed(2)}${who ? ' · ' + who : ''}${tail}</span></label>`;
  };
  const rows = list.map(e => row(e, '')).join('');
  const others = elsewhere.map(e => row(e, 'on ' + _apBillRefHtml(e.chargeBillId))).join('');
  const linkedNow = _apChargeLinkedIds(APState.editingInvoice).length;
  const netted = APState.editingInvoice ? Number(APState.editingInvoice.chargeNettedCents) || 0 : 0;
  const summary = linkedNow
    ? `<div style="margin-top:4px;">This bill covers <strong>${linkedNow} crew receipt${linkedNow === 1 ? '' : 's'}</strong> ($${(netted / 100).toFixed(2)} already in expenses). Its cost counts only the rest.</div>`
    : '';
  return `<div style="${box}"><strong>Charge account.</strong> Tick the crew receipts that are on this statement. They are already expenses, so this bill's cost counts only the rest. The bill still shows and pays the full statement.${summary}`
    + (rows ? `<div style="max-height:220px;overflow:auto;margin-top:6px;color:var(--text-primary);">${rows}</div>` : '')
    + (others ? `<div style="margin-top:6px;">On other bills:</div><div style="max-height:120px;overflow:auto;">${others}</div>` : '')
    + '</div>';
}

// TASK-10145 — the receipts this bill should cover after Save. COD r1
// (Independent ❌): start from what the SERVER says is linked
// (editingInvoice.chargeReceiptIds) and apply only the boxes actually drawn —
// a linked receipt with no box (expenses not loaded yet, a listener that
// failed) STAYS linked; it is never unlinked by being missing from the cache.
// Ticks are re-checked against the same list the form drew; the server
// re-checks every one. cents = what the ticks add up to (unknown amounts of
// undrawn linked receipts come from the bill's own chargeLinkedCents).
function _apReadChargeLinks(vendorId) {
  const cands = new Map(_apChargeReceiptsFor(vendorId).list.map(e => [String(e.id), e]));
  const before = _apChargeLinkedIds(APState.editingInvoice);
  const set = new Set(before);
  document.querySelectorAll('#ap-charge-notice .ap-charge-net').forEach((el) => {
    const id = String(el.value || '');
    if (!cands.has(id)) return;
    if (el.checked) set.add(id); else set.delete(id);
  });
  const ids = [...set];
  const amt = (id) => Math.round((Number(cands.get(id).amount) || 0) * 100);
  // COD r2 (Edge): undrawn linked receipts are priced at what the bill says is
  // linked minus the linked receipts that ARE drawn (not the whole linked total).
  const drawnLinked = before.filter(id => cands.has(id)).reduce((s, id) => s + amt(id), 0);
  const undrawn = ids.some(id => !cands.has(id)) ? Math.max(0, (Number(APState.editingInvoice?.chargeLinkedCents) || 0) - drawnLinked) : 0;
  const cents = ids.filter(id => cands.has(id)).reduce((s, id) => s + amt(id), 0) + undrawn;
  return { ids, cents };
}
// TASK-10145 — link / unlink on the server (one transaction; a receipt sits on
// at most one bill). Returns null on success, else the message to show.
// integ/2125 COD (Edge): expectedIds = the links this editor loaded; the server
// refuses if another office changed them since (no unlink by omission).
async function _apSetChargeLinks(invoiceId, ids, expectedIds) {
  try {
    const payload = { invoiceId, receiptIds: ids };
    if (Array.isArray(expectedIds)) payload.expectedIds = expectedIds;
    await httpsCallable(functions, 'apChargeReceiptsSet')(payload);
    // COD r2: the open modal's snapshot now matches the server, so a retry after a
    // failed bill write (link-first path) is not refused as someone else's change.
    if (APState.editingInvoice && APState.editingInvoice.id === invoiceId) APState.editingInvoice.chargeReceiptIds = [...ids];
    return null;
  } catch (e) {
    return (e && e.message) ? String(e.message) : 'Could not update the receipts this bill covers.';
  }
}

function _deMetaHtml(bits) {
  return bits
    .filter(b => b && b.text)
    .map(b => `<span class="de-m${b.cls ? ' ' + b.cls : ''}">${b.icon ? `<span class="material-icons">${b.icon}</span>` : ''}${b.text}</span>`)
    .join('');
}

function _driverExpenseStatusBadge(e) {
  if (e.status === 'rejected') return '<span class="de-pill p-rejected">Rejected</span>';
  if (e.status === 'approved') return '<span class="de-pill p-approved">Approved</span>';
  if (e.status === 'clarification_requested') return '<span class="de-pill p-await">Awaiting driver</span>';
  // TASK-244 follow-up: rows transition through pending_ocr while the
  // onCostEntryCreate trigger runs Smart Scan (~3-15s). Show a distinct
  // pulsing pill — and gate action buttons in renderDriverExpenseRow — so
  // dispatchers don't approve placeholder $0 rows before OCR completes.
  if (e.status === 'pending_ocr') return '<span class="de-pill p-parsing has-ic parsing-receipt-badge"><span class="material-icons">hourglass_empty</span>Parsing receipt…</span>';
  return '<span class="de-pill p-pending">Pending</span>';
}
// TASK-1543 — THE predicate for "this expense asserts money is owed". Positive
// form on purpose: only an APPROVED, unpaid reimbursement is a committed
// liability. `status !== 'rejected'` would still count pending /
// clarification_requested / pending_ocr rows into a figure that reads as money
// owed — and a REJECTED row previously asserted a debt forever with no way to
// clear it (Mark Reimbursed is gated on status === 'approved', so rejected rows
// were a dead end by construction: $137.11 misreported on live data). This
// matches bi.js isRecognizedCost(), so AP and BI can't disagree about the
// liability. Used by the nav-pill sub-badge, the per-person group badge, and
// the panel "Reimbursement pending" total — one source of truth.
function _reimbOwed(e) {
  // TASK-9947 — a refund to the employee that covers the whole expense leaves nothing owed.
  return e.status === 'approved' && e.reimbursementRequired === true && e.reimbursementStatus !== 'paid' && _reimbNet(e) > 0;
}
// TASK-9917 — a company-card charge marked personal (markExpensePersonal).
// Same shape test as bi.js personalChargeOf / services/personalCharge.js.
function _personalOf(e) {
  const p = e && e.personalCharge;
  return p && typeof p === 'object' && (p.payer === 'owner' || p.payer === 'employee') ? p : null;
}
function _personalOutstanding(e) {
  const p = _personalOf(e);
  if (!p || p.payer !== 'employee') return 0;
  const repaid = Number(p.repaidAmount);
  const left = Math.round((_driverExpenseAmount(e) - (Number.isFinite(repaid) ? repaid : 0)) * 100) / 100;
  return left > 0 ? left : 0;
}
// TASK-9917 s4 (c) — who is looking, for the employee's own view. Identity is
// members.personId only (same as the server's debtor check); no name matching.
function _viewerPersonId() {
  const v = window.AppState?.memberAccess?.personId;
  return typeof v === 'string' && v ? v : null;
}
function _isOwnPersonalCharge(e) {
  const p = _personalOf(e);
  const mine = _viewerPersonId();
  return !!(p && p.payer === 'employee' && mine && p.personId === mine);
}
function _personalBadge(e) {
  const p = _personalOf(e);
  if (!p) return '';
  // TASK-9917 s4 (c) — the Receipts & Expenses view (an employee's own login):
  // a charge they owe says so plainly; anyone else's — an owner draw included —
  // reads as "not a business cost". The owner-draw case is never named there,
  // and nobody else's debt is.
  if (APState.receiptsOnly === true) {
    if (_isOwnPersonalCharge(e)) {
      const left = _personalOutstanding(e);
      if (left <= 0) return '<span class="de-pill p-approved has-ic" title="Marked personal — not a business cost. Repaid in full."><span class="material-icons">person</span>Marked personal — repaid</span>';
      const part = Number(p.repaidAmount) > 0 ? ` · $${left.toFixed(2)} still owed` : '';
      return `<span class="de-pill p-owed has-ic" title="Marked personal — not a business cost. Owed back to the company by you."><span class="material-icons">person</span>Marked personal — owed by you${part}</span>`;
    }
    return '<span class="de-pill p-neutral has-ic" title="Marked personal — not a business cost."><span class="material-icons">person</span>Marked personal — not a business cost</span>';
  }
  if (p.payer === 'owner') return '<span class="de-pill p-neutral has-ic" title="Personal — not a business cost. Counted as an owner draw."><span class="material-icons">person</span>Personal · owner draw</span>';
  // TASK-9917 slice 2 — repaid in full reads as settled; part-repaid shows what is left.
  const left = _personalOutstanding(e);
  if (left <= 0) return '<span class="de-pill p-approved has-ic" title="Personal — not a business cost. Repaid in full."><span class="material-icons">person</span>Personal · repaid</span>';
  const part = Number(p.repaidAmount) > 0 ? ` $${left.toFixed(2)} of $${_driverExpenseAmount(e).toFixed(2)}` : '';
  return `<span class="de-pill p-owed has-ic" title="Personal — not a business cost. Owed back to the company."><span class="material-icons">person</span>Personal · ${escapeHtml(p.personName || 'employee')} owes${part}</span>`;
}
// Office logins that can change money (owner / billing, not view-only), outside
// the Receipts & Expenses view. The server re-checks (modules, never roles).
function _canDecidePersonal() {
  if (APState.receiptsOnly === true) return false;
  return !(typeof window.canEditMoney === 'function' && window.canEditMoney() === false);
}
// A company-card charge that can still be marked personal. Out-of-pocket
// claims (reimbursementRequired) keep Reject: nothing left the company card.
function _canMarkPersonal(e) {
  if (!e || _personalOf(e) || !_canDecidePersonal()) return false;
  if (e.reimbursementRequired === true || e.status === 'pending_ocr' || _isCreditEntry(e)) return false;
  if (Array.isArray(e.allocations) && e.allocations.length) return false;
  if (['draws', 'contributions', 'other_income'].includes(e.category)) return false;
  return _driverExpenseAmount(e) > 0;
}
function _personalBtns(e, jsId) {
  if (_personalOf(e)) {
    // TASK-9917 slice 2 — record a repayment while something is still owed.
    const repay = _canRecordRepayment(e)
      ? `<button type="button" class="de-btn de-reimburse" onclick="window.APModule.recordPersonalRepayment('${jsId}')" title="The employee paid some or all of it back"><span class="material-icons">payments</span>Record repayment</button>`
      : '';
    // s2 COD r2: any repayment history (undone ones too) pins the mark — the server refuses.
    const undo = _canDecidePersonal() && !(Number(_personalOf(e).repaidAmount) > 0) && !_personalRepayments(e).length
      ? `<button type="button" class="de-btn" onclick="window.APModule.clearExpensePersonal('${jsId}')" title="Undo — this was a business cost after all"><span class="material-icons">undo</span>Not personal</button>`
      : '';
    return repay + undo;
  }
  // TASK-9917 — someone asked the owner to count it as an owner draw: no second
  // Personal decision while that waits; the owner answers in Personal Charges.
  if (_ownerDrawReqFor(e && e.id)) {
    return _canDecidePersonal() ? '<span class="de-note" style="font-weight:600;color:var(--text-primary);"><span class="material-icons">hourglass_top</span>Owner draw asked — waiting for the owner (Personal Charges)</span>' : '';
  }
  return _canMarkPersonal(e)
    ? `<button type="button" class="de-btn" onclick="window.APModule.markExpensePersonal('${jsId}')" title="Paid on the company card but not a business cost — record who pays it back"><span class="material-icons">person_off</span>Personal</button>`
    : '';
}
function _personalNote(e) {
  const p = _personalOf(e);
  if (!p) return '';
  // TASK-9917 s4 (c) — in the employee's own view only THEIR charge shows the note + repayments.
  if (APState.receiptsOnly === true && !_isOwnPersonalCharge(e)) return '';
  const note = p.note ? `<div class="de-note de-note-admin"><span class="material-icons" title="Personal charge note">person</span><span>${escapeHtml(String(p.note))}</span></div>` : '';
  return note + _personalRepayHistory(e);
}

// TASK-9917 s4 (b) — a charge split by a partial Personal mark: the personal
// part says what it came off; the original says what was carved off it.
function _splitMetaText(e) {
  const p = _personalOf(e);
  if (p && p.splitOf) return `part of a $${(Number(p.splitOriginalAmount) || 0).toFixed(2)} charge`;
  const t = Number(e?.personalSplit?.total);
  if (t > 0) return `$${t.toFixed(2)} of it marked personal`;
  return '';
}

// TASK-9917 slice 2 — repayments recorded against an employee personal charge
// (recordPersonalRepayment / undoPersonalRepayment; owner or billing module,
// one audit row each). An undone repayment stays listed, struck through.
const _REPAY_METHOD_LABEL = { cash: 'Cash', check: 'Check', payroll_deduction: 'Payroll deduction', other: 'Other' };
const _jsAttr = (v) => (window.escJsAttr ? window.escJsAttr(String(v || '')) : String(v || '').replace(/[^A-Za-z0-9_-]/g, ''));
function _personalRepayments(e) {
  const p = _personalOf(e);
  return p && Array.isArray(p.repayments) ? p.repayments.filter(r => r && typeof r.id === 'string') : [];
}
// The employee who owes it never records their own repayment (the server
// enforces it; the owner login excepted). Mirrors PC.debtorSelfRefusal: a
// non-owner login not linked to a Roster person is refused too (COD r1, fail
// closed). Hides the buttons, nothing more.
function _isPersonalDebtor(e) {
  const p = _personalOf(e);
  if (!p || p.payer !== 'employee' || _isOwnerLogin()) return false;
  const mine = window.AppState?.memberAccess?.personId;
  return !mine || mine === p.personId;
}
function _canRecordRepayment(e) {
  const p = _personalOf(e);
  return !!p && p.payer === 'employee' && _personalOutstanding(e) > 0 && _canDecidePersonal() && !_isPersonalDebtor(e);
}
function _personalRepayHistory(e) {
  const reps = _personalRepayments(e);
  if (!reps.length) return '';
  const canUndo = _canDecidePersonal() && !_isPersonalDebtor(e);
  const lines = reps.map(r => {
    const amt = `$${(Number(r.amount) || 0).toFixed(2)}`;
    const what = [r.date || '', _REPAY_METHOD_LABEL[r.method] || 'Other', r.reference ? '#' + String(r.reference) : '', r.note ? String(r.note) : '']
      .filter(Boolean).map(x => escapeHtml(x)).join(' · ');
    if (r.voidedAt) {
      return `<div style="color:var(--text-secondary);"><s>Repaid ${amt}</s> · ${what} · undone${r.voidReason ? ': ' + escapeHtml(String(r.voidReason)) : ''}</div>`;
    }
    const undo = canUndo
      ? ` <button type="button" class="de-btn" style="padding:4px 14px;min-height:44px;font-size:0.8rem;margin-left:4px;" onclick="window.APModule.undoPersonalRepayment('${_jsAttr(e.id)}','${_jsAttr(r.id)}')" title="Recorded by mistake — undo it (kept in the history)">Undo</button>`
      : '';
    return `<div><strong>Repaid ${amt}</strong> · ${what}${undo}</div>`;
  }).join('');
  return `<div class="de-note"><span class="material-icons" title="Repayments">payments</span><div style="display:flex;flex-direction:column;gap:2px;">${lines}</div></div>`;
}

function _reimbursementBadge(e) {
  if (e.reimbursementRequired !== true) return '';
  if (e.reimbursementStatus === 'paid') {
    return '<span class="de-pill p-paid has-ic"><span class="material-icons">check_circle</span>Reimbursed</span>';
  }
  // TASK-1543 (UX Lead) — a REJECTED row gets explicit "not owed" confirmation
  // instead of the red badge silently vanishing: the driver DID claim
  // reimbursement (kept for audit), but rejection voids the debt.
  if (e.status === 'rejected') {
    return '<span class="de-pill p-neutral has-ic"><span class="material-icons">block</span>Not owed</span>';
  }
  if (e.status === 'approved') {
    // TASK-9947 — refunds that went back to the employee net what is owed.
    const refunded = _employeeRefundCents(e.id, 'approved');
    if (refunded > 0 && _reimbNet(e) <= 0) return '<span class="de-pill p-paid has-ic"><span class="material-icons">undo</span>Covered by refund</span>';
    if (refunded > 0) return `<span class="de-pill p-owed has-ic"><span class="material-icons">priority_high</span>Owed $${_reimbNet(e).toFixed(2)} after refund</span>`;
    return '<span class="de-pill p-owed has-ic"><span class="material-icons">priority_high</span>Reimbursement owed</span>';
  }
  // Not yet reviewed (pending / clarification_requested / pending_ocr): the
  // claim exists but isn't a committed liability until approval — show it as a
  // request so the reviewer still sees it, without red urgency.
  return '<span class="de-pill p-await has-ic"><span class="material-icons">schedule</span>Reimbursement requested</span>';
}
function _driverGroupByPerson(expenses) {
  const byPerson = new Map();
  for (const e of expenses) {
    // TASK-1542 ("Two Stevens") — group by the SUBJECT (whose money it is),
    // not the actor. `submittedBy` carries two different KINDS of identifier:
    // a person-doc id on source:'driver' rows but a Firebase Auth UID on
    // source:'staff' rows (createMemberExpense deliberately writes it as the
    // actor's uid) — so keying on it split one human into two groups.
    // `personId` is the payee on every path and, since TASK-1544, is
    // CLAIM-BOUND server-side on the member path (the client cannot forge it).
    // Do NOT "normalize" submittedBy at the writers — actor-vs-subject is an
    // intentional distinction; this render site was just reading the actor
    // field to answer a subject question.
    const key = e.personId || e.submittedBy || '_unknown';
    if (!byPerson.has(key)) {
      byPerson.set(key, {
        personId: key,
        personName: e.personName || 'Unknown driver',
        items: [],
        total: 0,
        pending: 0,
        reimbursementPending: 0,
      });
    }
    const g = byPerson.get(key);
    g.items.push(e);
    g.total += _driverExpenseAmount(e);
    if (e.status === 'pending' || e.status === 'clarification_requested') g.pending += 1;
    if (_reimbOwed(e)) g.reimbursementPending += 1; // TASK-1543 — approved+unpaid only
  }
  return Array.from(byPerson.values()).sort((a, b) => b.total - a.total);
}

// Setup-needed CTA shown in Driver Expenses panel when costAllocations is empty.
// Amber warning card with a "Seed from fleet" primary action that reuses
// _seedCostAllocations(). Fleet counts fetched lazily once + cached on APState.
function _renderDriverExpenseSetupCTA(area) {
  // Trucks + trailers are already live-subscribed on APState.assets. Assets
  // aren't, so we one-shot fetch once and cache.
  const trucks = (APState.assets || []).filter(a => a._assetType === 'truck').length;
  const trailers = (APState.assets || []).filter(a => a._assetType === 'trailer').length;
  const assetsCount = APState._fleetCounts._loaded ? APState._fleetCounts.assets : null;

  // Compose count phrase. When assets count isn't loaded yet, render with
  // trucks/trailers only and let the async fetch trigger a re-render.
  const parts = [`${trucks} truck${trucks === 1 ? '' : 's'}`, `${trailers} trailer${trailers === 1 ? '' : 's'}`];
  if (assetsCount !== null) parts.push(`${assetsCount} asset${assetsCount === 1 ? '' : 's'}`);
  const fleetPhrase = parts.join(', ');

  area.innerHTML = `
    <div style="max-width:560px;margin:32px auto;padding:24px;background:#FEF3C7;border:1px solid #F59E0B;border-radius:12px;">
      <div style="display:flex;align-items:flex-start;gap:12px;">
        <span class="material-icons" style="color:#B45309;font-size:28px;flex-shrink:0;">warning_amber</span>
        <div style="flex:1;">
          <h3 style="margin:0 0 8px;font-size:1.05rem;font-weight:700;color:#78350F;">Setup needed</h3>
          <p style="margin:0 0 16px;color:#78350F;font-size:0.9rem;line-height:1.5;">
            Drivers can't submit expenses until cost allocations exist. We can auto-create them from your fleet — ${escapeHtml(fleetPhrase)} detected.
          </p>
          <button class="btn btn-primary" onclick="window.APModule._seedCostAllocations()" style="padding:10px 18px;font-size:0.9rem;font-weight:600;background:#D97706;border:none;">
            <span class="material-icons" style="font-size:18px;vertical-align:middle;">cloud_download</span> Seed from fleet
          </button>
        </div>
      </div>
    </div>`;

  // Lazily fetch the assets count (subcollection not otherwise loaded by AP)
  // and re-render once it arrives. Single fetch per session.
  if (!APState._fleetCounts._loaded && !APState._fleetCounts._loading && APState.tenantId) {
    APState._fleetCounts._loading = true;
    getDocs(collection(db, 'tenants', APState.tenantId, 'assets'))
      .then(snap => {
        const activeAssets = snap.docs.filter(d => d.data().active !== false);
        APState._fleetCounts.assets = activeAssets.length;
        // TASK-4606 — keep the ids too, not just the count: the drift indicator
        // has to name WHICH fleet items lack an allocation, and a bare count
        // can't answer that. Same single fetch, nothing extra over the wire.
        APState._fleetCounts.assetRefs = activeAssets.map(d => ({ id: d.id, label: (d.data() || {}).name || d.id }));
        APState._fleetCounts._loaded = true;
        APState._fleetCounts._loading = false;
        if (APState.filter === 'driverExpenses') renderAPView();
      })
      .catch(err => {
        console.warn('[AP] fleet assets count fetch failed:', err?.message);
        APState._fleetCounts._loading = false;
      });
  }
}

// TASK-4606 — which ACTIVE fleet entities have no cost allocation row.
// The server-side trigger (onTruck/Trailer/AssetWriteCostAllocSync) keeps this
// at zero going forward; this exists because nothing repaired the drift that
// accumulated BEFORE the trigger existed — on trippin-trucking that was 5
// entities, silently unallocatable, with a "Seed from fleet" button sitting
// right there that nobody knew to press again.
// Matching is by type+refId, the same key the seeder uses, so a legacy
// random-id row still counts as covering its entity.
function _allocDriftList(allocations) {
  const covered = new Set((allocations || []).map(a => `${a.type}_${a.refId || ''}`));
  const out = [];
  for (const a of (APState.assets || [])) {
    const type = a._assetType === 'truck' ? 'truck' : 'trailer';
    if (a.status === 'inactive') continue;
    if (covered.has(`${type}_${a.id}`)) continue;
    const unit = a.unit || a.id;
    out.push({ type, label: a.nickname ? `${a.nickname} — #${unit}` : `${type === 'trailer' ? 'Trailer' : 'Truck'} #${unit}` });
  }
  // Assets only once their ids have arrived; a bare count can't be diffed.
  for (const r of (APState._fleetCounts.assetRefs || [])) {
    if (covered.has(`asset_${r.id}`)) continue;
    out.push({ type: 'asset', label: r.label });
  }
  return out;
}

function _allocDriftBannerHtml(allocations) {
  // Only meaningful once the tenant has opted in at all — an empty collection
  // is the "Setup needed" CTA's story, not a drift story.
  if (!allocations || allocations.length === 0) return '';
  const drift = _allocDriftList(allocations);
  if (drift.length === 0) return '';
  const names = drift.slice(0, 6).map(d => escapeHtml(d.label)).join(', ');
  const more = drift.length > 6 ? ` +${drift.length - 6} more` : '';
  return `
    <div style="margin-top:8px;padding:10px 12px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:8px;font-size:0.78rem;color:#92400E;line-height:1.45;">
      <span class="material-icons" style="font-size:15px;vertical-align:middle;color:#B45309;">warning_amber</span>
      <strong>${drift.length} fleet item${drift.length === 1 ? '' : 's'} ha${drift.length === 1 ? 's' : 've'} no cost allocation</strong> —
      expenses can't be assigned to ${drift.length === 1 ? 'it' : 'them'}: ${names}${more}.
      Click <strong>Seed from fleet</strong> to add ${drift.length === 1 ? 'it' : 'them'}.
    </div>`;
}

function renderDriverExpensesPanel(area) {
  if (!area) return;

  // TASK-368 pattern (2026-05-28) — gate BOTH cost-allocations + driver-expenses
  // on their _loaded flags so we don't flash "Setup needed" (or "No driver
  // expenses yet") before the first onSnapshot lands. Pre-fix this rendered
  // the misleading "Setup needed" amber card on every panel-open until the
  // costAllocations subscription replied — looked broken on slow connections.
  if (!APState._costAllocationsLoaded || !APState._driverExpensesLoaded) {
    area.innerHTML = `
      <div class="ap-loading-skeleton" style="margin-top:var(--spacing-lg);display:flex;flex-direction:column;gap:8px;opacity:0.55;">
        ${[1,2,3].map(() => `
          <div style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:14px 16px;display:flex;align-items:center;gap:12px;">
            <div style="width:36px;height:36px;border-radius:6px;background:var(--bg-elevated);flex-shrink:0;"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
              <div style="height:14px;width:50%;background:var(--bg-elevated);border-radius:4px;"></div>
              <div style="height:12px;width:30%;background:var(--bg-elevated);border-radius:4px;"></div>
            </div>
          </div>
        `).join('')}
        <div style="text-align:center;color:var(--text-secondary);font-size:0.85rem;margin-top:6px;">Loading driver expenses…</div>
      </div>
    `;
    return;
  }

  // Setup-needed CTA: drivers can't submit expenses until cost allocations exist.
  // Live `costAllocations` snapshot drives this — after Seed, the panel auto-flips
  // to the normal expense list (no manual refresh).
  // TASK-9778 — a Receipts & Expenses login cannot seed cost allocations (money
  // config), so it skips the setup CTA and sees the (empty) list instead.
  if ((APState.costAllocations || []).length === 0 && !APState.receiptsOnly) {
    _renderDriverExpenseSetupCTA(area);
    return;
  }

  const all = APState.driverExpenses || [];
  const q = APState.searchQuery;
  const filtered = q
    ? all.filter(e =>
        (e.personName || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        _allocSearchText(e).includes(q) ||
        (e.category || '').toLowerCase().includes(q) ||
        // TASK-1338 — find an expense by the job or customer it was tied to.
        (e.jobName || '').toLowerCase().includes(q) ||
        (e.customerName || '').toLowerCase().includes(q))
    : all;

  // Summary
  const pendingItems = filtered.filter(e => e.status === 'pending' || e.status === 'clarification_requested');
  // TASK-1543 — the "Reimbursement pending" DOLLAR figure reads as a committed
  // liability, so it counts only approved+unpaid claims (_reimbOwed). Rejected
  // rows ($137.11 live) and not-yet-reviewed rows are excluded, matching BI.
  const reimbItems = filtered.filter(_reimbOwed);
  const pendingTotal = pendingItems.reduce((s, e) => s + _driverExpenseAmount(e), 0);
  const reimbTotal = reimbItems.reduce((s, e) => s + _reimbNet(e), 0); // TASK-9947 — net of approved refunds
  const selCount = APState.driverExpenseSelection?.size || 0;

  const groups = _driverGroupByPerson(filtered);

  let html = `
    <div class="ap-summary-bar">
      <div class="ap-summary-item">
        <span class="ap-summary-label">Pending review</span>
        <span class="ap-summary-value ap-summary-open">$${pendingTotal.toFixed(2)}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Reimbursement pending</span>
        <span class="ap-summary-value" style="color:#991B1B;">$${reimbTotal.toFixed(2)}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Expenses</span>
        <span class="ap-summary-value">${filtered.length}</span>
      </div>
    </div>

    <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin:12px 0;">
      <button class="btn btn-primary" id="btn-bulk-approve-driver" onclick="window.APModule.bulkApproveDriver()" ${selCount === 0 ? 'disabled' : ''} style="padding:8px 14px;font-size:0.85rem;font-weight:600;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">check_circle</span> Approve Selected (${selCount})
      </button>
      <button class="btn btn-secondary" onclick="window.APModule.selectAllPendingDriver()" style="padding:8px 14px;font-size:0.85rem;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">select_all</span> Select all pending
      </button>
      <button class="btn btn-secondary" onclick="window.APModule.clearDriverSelection()" style="padding:8px 14px;font-size:0.85rem;">Clear</button>
      ${APState.receiptsOnly ? '' : `<button class="btn btn-secondary" onclick="window.APModule.consolidateDriverExpenses()" style="padding:8px 14px;font-size:0.85rem;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">compare_arrows</span> Consolidate (match bank/CSV)
      </button>
      <button class="btn btn-secondary" onclick="window.APModule.openDriverExpenseSettings()" style="padding:8px 14px;font-size:0.85rem;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">settings</span> Settings
      </button>`}
    </div>
  `;
  html += _returnsInboxHtml(); // TASK-9949 — returns inbox (hidden while none are open)
  html += _owesBackHtmlStub(); // TASK-9948 — employee owes back from returns (hidden while none)

  if (!filtered.length) {
    html += `
      <div class="empty-state" style="padding:48px 20px;text-align:center;">
        <span class="material-icons empty-state-icon" style="font-size:56px;color:var(--text-muted);">receipt_long</span>
        <div class="empty-state-title" style="margin-top:12px;">No driver expenses yet</div>
        <div class="empty-state-text">When drivers submit expenses from the clock screen or Build Ticket, they'll land here for review.</div>
      </div>`;
    area.innerHTML = html;
    return;
  }

  // TASK-9213 — each driver is a card whose body is a RECESSED well, so the
  // expense rows inside it read as raised objects instead of as table rows on
  // the same white plane as everything else. Initials puck replaces the orange
  // person glyph that was byte-identical on every group.
  for (const g of groups) {
    const expanded = APState.expandedDriverGroups[g.personId] !== false;
    const jsPid = (window.escJsAttr ? window.escJsAttr(g.personId || '') : String(g.personId || ''));
    html += `
      <div class="de-group">
        <div class="de-group-h" role="button" tabindex="0" aria-expanded="${expanded}"
             onclick="window.APModule.toggleDriverGroup('${jsPid}')"
             onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();window.APModule.toggleDriverGroup('${jsPid}');}">
          <span class="material-icons de-group-chev">${expanded ? 'expand_more' : 'chevron_right'}</span>
          <span class="de-avatar" aria-hidden="true">${escapeHtml(_deInitials(g.personName))}</span>
          <span class="de-group-name">${escapeHtml(g.personName)}</span>
          <span class="de-group-chips">
            <span class="de-chip">${g.items.length} expense${g.items.length === 1 ? '' : 's'}</span>
            ${g.pending > 0 ? `<span class="de-chip blue">${g.pending} to review</span>` : ''}
            ${g.reimbursementPending > 0 ? `<span class="de-chip red"><span class="material-icons" style="font-size:12px;">priority_high</span>${g.reimbursementPending} owed</span>` : ''}
          </span>
          <span class="de-group-total">$${g.total.toFixed(2)}</span>
        </div>
        ${expanded ? `<div class="de-well">${g.items.map(renderDriverExpenseRow).join('')}</div>` : ''}
      </div>`;
  }
  area.innerHTML = html;
}

// TASK-604: Company Expenses — admin-recorded costs (source:'admin', already
// approved). Flat read-only ledger; NO approve/reject (they're approved by
// definition). Deliberately NOT grouped-by-driver (these have no driver) and
// NOT mixed into the Driver Expenses queue. All user-supplied fields escaped.
function renderCompanyExpensesPanel(area) {
  if (!area) return;

  if (!APState._companyExpensesLoaded) {
    area.innerHTML = `
      <div class="ap-loading-skeleton" style="margin-top:var(--spacing-lg);display:flex;flex-direction:column;gap:8px;opacity:0.55;">
        ${[1,2,3].map(() => `
          <div style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:14px 16px;display:flex;align-items:center;gap:12px;">
            <div style="width:36px;height:36px;border-radius:6px;background:var(--bg-elevated);flex-shrink:0;"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
              <div style="height:14px;width:50%;background:var(--bg-elevated);border-radius:4px;"></div>
              <div style="height:12px;width:30%;background:var(--bg-elevated);border-radius:4px;"></div>
            </div>
          </div>
        `).join('')}
        <div style="text-align:center;color:var(--text-secondary);font-size:0.85rem;margin-top:6px;">Loading company expenses…</div>
      </div>`;
    return;
  }

  const all = APState.companyExpenses || [];
  const q = APState.searchQuery;
  const filtered = q
    ? all.filter(e =>
        (e.description || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q) ||
        (e.vehicleDisplay || '').toLowerCase().includes(q) ||
        _allocSearchText(e).includes(q) ||
        // TASK-1338 — find an expense by the job or customer it was tied to.
        (e.jobName || '').toLowerCase().includes(q) ||
        (e.customerName || '').toLowerCase().includes(q))
    : all;

  // TASK-9367 — refunds / credits are stored negative, so this total is NET.
  // TASK-9947 — a refund waiting for approval (or rejected) is not in the net.
  const total = filtered.filter(_refundCounts).reduce((s, e) => s + _driverExpenseAmount(e), 0);
  const creditTotal = filtered.filter(e => _isCreditEntry(e) && _refundCounts(e)).reduce((s, e) => s + _driverExpenseAmount(e), 0);
  const reimbItems = filtered.filter(e => e.reimbursementRequired === true && e.reimbursementStatus !== 'paid' && e.status !== 'rejected' && _reimbNet(e) > 0);
  const reimbTotal = reimbItems.reduce((s, e) => s + _reimbNet(e), 0);

  let html = `
    <div class="ap-summary-bar">
      <div class="ap-summary-item">
        <span class="ap-summary-label">${creditTotal ? 'Net recorded' : 'Total recorded'}</span>
        <span class="ap-summary-value">${_fmtSignedMoney(total)}</span>
      </div>
      ${creditTotal ? `
      <div class="ap-summary-item">
        <span class="ap-summary-label">Refunds / credits</span>
        <span class="ap-summary-value" style="color:#166534;">${_fmtSignedMoney(creditTotal)}</span>
      </div>` : ''}
      <div class="ap-summary-item">
        <span class="ap-summary-label">Reimbursement pending</span>
        <span class="ap-summary-value" style="color:#991B1B;">$${reimbTotal.toFixed(2)}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Expenses</span>
        <span class="ap-summary-value">${filtered.length}</span>
      </div>
    </div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin:12px 0;">
      <button class="btn btn-primary" onclick="window.APModule.openAdminExpense()" style="padding:8px 14px;font-size:0.85rem;font-weight:600;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">add</span> Record Expense
      </button>
      <button type="button" class="btn btn-secondary" onclick="window.APModule.openVendorCredit()" style="padding:8px 12px;font-size:0.85rem;font-weight:600;min-height:40px;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;color:#059669;">undo</span> Refund / Credit
      </button>
      <span style="font-size:0.78rem;color:var(--text-secondary);">Admin-recorded company costs (already approved), and refunds / credits that came back. Both roll into Business Intelligence totals.</span>
    </div>
  `;

  if (!filtered.length) {
    html += `
      <div class="empty-state" style="padding:48px 20px;text-align:center;">
        <span class="material-icons empty-state-icon" style="font-size:56px;color:var(--text-muted);">store</span>
        <div class="empty-state-title" style="margin-top:12px;">No company expenses yet</div>
        <div class="empty-state-text">Use <strong>Record Expense</strong> to log company-paid receipts, paper invoices, or card charges. They'll appear here as approved costs.</div>
      </div>`;
    area.innerHTML = html;
    return;
  }

  // TASK-9213 — the flat ledger gets the same recessed well as a driver group,
  // so its cards sit on something instead of floating on the page white.
  html += `<div class="de-well" style="border-radius:12px;border-top:none;">${filtered.map(renderCompanyExpenseRow).join('')}</div>`;
  area.innerHTML = html;
}

// TASK-1338 — "Customer — Job" for an expense card, whichever half exists.
// Shared by the company ledger and the driver reimbursement queue so the two
// can't drift (the company renderer was missed in the original spec).
function _expenseJobMeta(e) {
  const cust = String(e?.customerName || '').trim();
  const job = String(e?.jobName || '').trim();
  if (!cust && !job) return '';
  if (cust && job) return `${escapeHtml(cust)} — ${escapeHtml(job)}`;
  return escapeHtml(cust || job);
}

// TASK-9947 — status + actions of a refund / credit (shared by the credit row
// and the refund lines under a driver expense).
function _refundStatusPill(c) {
  if (!_isNewRefund(c)) return '<span class="de-pill p-approved">Refund / Credit</span>';
  if (c.status === 'pending') return '<span class="de-pill p-await has-ic"><span class="material-icons">schedule</span>Waiting for approval</span>';
  if (c.status === 'rejected') return '<span class="de-pill p-neutral has-ic"><span class="material-icons">block</span>Rejected</span>';
  if (c.originalApproved === false) return '<span class="de-pill p-await">Counts once the expense is approved</span>';
  return '<span class="de-pill p-approved">Refund approved</span>';
}
function _refundActionsHtml(c) {
  if (!_isNewRefund(c) || c.status !== 'pending' || APState.receiptsOnly === true) return '';
  if (typeof window.canEditMoney === 'function' && window.canEditMoney() === false) return '';
  const jsId = (window.escJsAttr ? window.escJsAttr(c.id || '') : String(c.id || ''));
  return `<button type="button" class="de-btn de-approve" onclick="window.APModule.decideRefund('${jsId}', 'approve')"><span class="material-icons">check</span>Approve refund</button>`
    + `<button type="button" class="de-btn de-reject" onclick="window.APModule.decideRefund('${jsId}', 'reject')"><span class="material-icons">close</span>Reject refund</button>`;
}
function _owesBackHtml(c) {
  const ob = c && c.owesBack;
  if (!ob || ob.status !== 'open') return '';
  // TASK-9948 — the balance itself lives on the server (Owed back from returns,
  // views/apOwesBack.js); this is the bold flag on the refund line (record only — never taken from pay).
  return `<div class="de-note" style="font-weight:700;color:#991B1B;"><span class="material-icons" style="color:#991B1B;">priority_high</span><span>${escapeHtml(ob.personName || 'Employee')} owes ${_fmtSignedMoney(Number(ob.amount || 0))} from return (already reimbursed). Never taken out of pay.</span></div>`;
}
// TASK-9952 — a refund whose pay period had ended posts on the open period's
// first day (date); the date it was entered with stays as refundDate. A
// persistent line on the row, not only the approve toast.
function _apYmdShort(v) {
  const d = v?.toDate ? v.toDate() : (typeof v?.seconds === 'number' ? new Date(v.seconds * 1000) : null);
  return d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}
function _refundCorrectionText(c) {
  const pc = c && c.periodCorrection;
  if (!pc || typeof pc !== 'object' || !Array.isArray(pc.reasons) || !pc.reasons.length) return '';
  const posts = _apYmdShort(c.date);
  const entered = _apYmdShort(c.refundDate);
  if (entered && posts && entered !== posts) return `Correction — posts ${escapeHtml(posts)} (refund dated ${escapeHtml(entered)}, its pay period was closed)`;
  if (pc.reasons.includes('original_in_closed_period')) return 'Correction — the expense is in a closed pay period, which is not changed';
  if (pc.reasons.includes('original_in_quickbooks')) return 'Correction — the expense is already in QuickBooks, which is not changed';
  return '';
}
// TASK-9952 slice B — an approved refund on a bill that is in QuickBooks can be
// sent there as a vendor credit (qboPushVendorCredit refuses everything else,
// incl. read-only companies). A refund back to a card is never offered: the
// card's bank feed brings it into QuickBooks.
function _refundQboHtml(c) {
  if (!_isNewRefund(c) || c.status !== 'approved') return '';
  const sent = c.qboVendorCreditId == null ? '' : String(c.qboVendorCreditId).trim();
  if ((sent && !sent.startsWith('STUB_')) || c.qboVendorCreditSynced === true) {
    return `<span class="de-pill p-approved" title="Sent to QuickBooks as a vendor credit${sent ? ' (id ' + escapeHtml(sent) + ')' : ''}">In QuickBooks</span>`;
  }
  if (!window.AppState?.tenantData?.qboConnected || APState.receiptsOnly === true) return '';
  if (c.refundTo === 'company_card' || c.originalApproved === false || !c.creditOfApInvoiceId) return '';
  const bill = (APState.invoices || []).find(i => i && i.id === c.creditOfApInvoiceId);
  if (!bill || !_apQboPushedBillId(bill)) return '';
  if (typeof window.canEditMoney === 'function' && window.canEditMoney() === false) return '';
  const jsId = (window.escJsAttr ? window.escJsAttr(c.id || '') : String(c.id || ''));
  return `<button type="button" class="de-btn" onclick="window.APModule.pushRefundToQbo('${jsId}', this)"><span class="material-icons">cloud_upload</span>Send to QuickBooks</button>`;
}
async function pushRefundToQbo(creditId, btn) {
  if (btn) btn.disabled = true;
  try {
    const res = await httpsCallable(functions, 'qboPushVendorCredit')({ creditId });
    const d = res?.data || {};
    if (d.sandbox) window.showToast?.('QuickBooks is not fully connected (test connection) — nothing was sent.', 'info');
    else window.showToast?.(`Refund sent to QuickBooks as a vendor credit dated ${d.txnDate || 'in the open period'}`, 'success');
  } catch (err) {
    console.error('[AP] qboPushVendorCredit', err);
    window.showToast?.(String(err?.message || 'Could not send the refund to QuickBooks.'), 'error');
    if (btn) btn.disabled = false;
  }
}
function _refundToText(c) {
  if (!_isNewRefund(c)) return '';
  if (c.refundTo === 'employee') return `Back to ${escapeHtml(c.personName || 'the employee')}`;
  if (c.refundTo === 'company_card') return `Back to card${c.paymentCardLast4 ? ' ••' + escapeHtml(String(c.paymentCardLast4)) : ''} — will show on the statement`;
  if (c.refundTo === 'store_credit') return 'Store credit with the vendor'; // TASK-9948
  return 'To the company';
}
// The refunds recorded against one expense, as lines under its row.
function _refundLinesHtml(e) {
  const list = _creditsOf(e.id);
  if (!list.length) return '';
  return list.map(c => `
    <div class="de-note" style="align-items:flex-start;flex-wrap:wrap;gap:6px;">
      <span class="material-icons" style="color:#166534;">undo</span>
      <span style="flex:1;min-width:180px;"><strong style="color:#166534;">${_fmtSignedMoney(_driverExpenseAmount(c))}</strong> ${escapeHtml((_VC_REASONS.find(r => r.value === c.creditReason) || {}).label || 'Refund')}${_refundToText(c) ? ' · ' + _refundToText(c) : ''}${_refundCorrectionText(c) ? ' · ' + _refundCorrectionText(c) : ''}</span>
      ${_refundStatusPill(c)}
      ${_refundActionsHtml(c)}
      ${_refundQboHtml(c)}
    </div>${_owesBackHtml(c)}`).join('');
}

// TASK-9367 — a refund / credit row: green, ↩, negative amount, and what it
// reverses. TASK-9947: status pill + Approve / Reject while it waits.
function renderCompanyCreditRow(e) {
  const date = e.date?.toDate ? e.date.toDate() : (e.submittedAt?.toDate ? e.submittedAt.toDate() : new Date());
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const reason = (_VC_REASONS.find(r => r.value === e.creditReason) || {}).label || 'Refund / Credit';
  let against = '';
  if (e.creditOf) {
    const orig = _vcDescribeOriginal(e.creditOf);
    against = orig ? `Against ${escapeHtml(orig.label)}` : '';
  }
  const cat = _deCategoryMeta(e);
  const meta = _deMetaHtml([
    { icon: 'event', text: dateStr },
    { text: escapeHtml(reason) },
    { text: cat.label ? escapeHtml(cat.label) : '' },
    { icon: 'link', text: against },
    { icon: 'credit_card', text: e.paymentCardLast4 ? '••' + escapeHtml(String(e.paymentCardLast4)) : '' },
    { icon: 'work', text: _expenseJobMeta(e), cls: 'de-m-job' },
    { icon: 'receipt_long', text: Number(e.appliedToBill) > 0 ? `$${Number(e.appliedToBill).toFixed(2)} off the bill` : '' },
    { icon: 'payments', text: _refundToText(e) }, // TASK-9947
    { icon: 'event_repeat', text: _refundCorrectionText(e) }, // TASK-9952
  ]);
  return `
    <div class="de-row ${e.status === 'rejected' ? 's-rejected' : (e.status === 'pending' ? 's-pending' : 's-approved')}">
      <div class="de-ic" style="background:#DCFCE7;border-color:#86EFAC;"><span class="material-icons" style="color:#166534;">undo</span></div>
      <div class="de-body">
        <div class="de-title">${escapeHtml(e.description || 'Refund / credit')}</div>
        <div class="de-meta">${meta}</div>
        ${_companyReceiptLink(e)}
        ${e.notes ? `<div class="de-note"><span class="material-icons" title="Note">sticky_note_2</span><span>${escapeHtml(String(e.notes)).replace(/\n/g, '<br>')}</span></div>` : ''}
      </div>
      <div class="de-right">
        <div class="de-amt" style="color:${e.status === 'rejected' ? 'var(--text-secondary)' : '#166534'};${e.status === 'rejected' ? 'text-decoration:line-through;' : ''}">${_fmtSignedMoney(_driverExpenseAmount(e))}</div>
        ${_refundStatusPill(e)}
      </div>
      ${_owesBackHtml(e)}
      <div class="de-actions">${_refundActionsHtml(e)}${_refundQboHtml(e)}</div>
    </div>
  `;
}

function renderCompanyExpenseRow(e) {
  if (_isCreditEntry(e)) return renderCompanyCreditRow(e);
  const date = e.date?.toDate ? e.date.toDate() : (e.submittedAt?.toDate ? e.submittedAt.toDate() : new Date());
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  // TASK-9213 — same .de-* card language as the driver queue. These two lists
  // sit one filter-pill apart; rendering them differently was making an
  // already-flat surface feel like two half-finished screens.
  const jsId = (window.escJsAttr ? window.escJsAttr(e.id || '') : String(e.id || ''));
  const cat = _deCategoryMeta(e);
  const owed = e.reimbursementRequired === true && e.reimbursementStatus !== 'paid' && _reimbNet(e) > 0 && _employeeRefundCents(e.id, 'pending') === 0; // TASK-9947
  const meta = _deMetaHtml([
    { icon: 'event', text: dateStr },
    { icon: 'call_split', text: escapeHtml(_splitMetaText(e)) }, // TASK-9917 s4
    { text: cat.label ? escapeHtml(cat.label) : '' },
    { icon: 'local_shipping', text: e.vehicleDisplay ? escapeHtml(e.vehicleDisplay) : '' },
    { icon: 'sell', text: _allocLabelHtml(e) },
    { icon: 'credit_card', text: e.paymentCardLast4 ? '••' + escapeHtml(String(e.paymentCardLast4)) : '' },
    { icon: 'storefront', text: _chargeAccountMetaHtml(e) }, // TASK-10141
    { icon: 'local_gas_station', text: e.fuelGallons ? `${Number(e.fuelGallons).toFixed(1)} gal${e.fuelState ? ' · ' + escapeHtml(e.fuelState) : ''}` : '' },
    // TASK-1338 — the half Chris pointed at: show what this cost was tied to.
    // This is the renderer AP-recorded expenses actually land in.
    { icon: 'work', text: _expenseJobMeta(e), cls: 'de-m-job' },
  ]);
  // TASK-9367 — what has come back against this charge, and the row action.
  const credited = _creditedAgainst(e.id);
  const personal = _personalOf(e); // TASK-9917
  const canCredit = !personal && _driverExpenseAmount(e) > 0 && Math.round(credited * 100) < Math.round(_driverExpenseAmount(e) * 100);
  const creditBtn = canCredit
    ? `<button type="button" class="de-btn" onclick="window.APModule.openVendorCredit('${jsId}')" title="Record money that came back against this expense"><span class="material-icons" style="color:#059669;">undo</span>Refund / Credit</button>`
    : '';

  return `
    <div class="de-row ${personal ? _deStatusClass(e) : (owed ? 's-owed' : 's-approved')}">
      <div class="de-ic t-${cat.tint}"><span class="material-icons">${escapeHtml(cat.icon)}</span></div>
      <div class="de-body">
        <div class="de-title">${escapeHtml(e.description || cat.label || e.category || '—')}</div>
        <div class="de-meta">${meta}</div>
        ${_companyReceiptLink(e)}
        ${e.notes ? `<div class="de-note"><span class="material-icons" title="Note">sticky_note_2</span><span>${escapeHtml(String(e.notes)).replace(/\n/g, '<br>')}</span></div>` : ''}
        ${_personalNote(e)}
      </div>
      <div class="de-right">
        <div class="de-amt">$${_driverExpenseAmount(e).toFixed(2)}</div>
        ${credited > 0 ? `<div style="font-size:0.75rem;font-weight:600;color:#166534;">credited −$${credited.toFixed(2)}</div>` : ''}
        ${personal ? _personalBadge(e) : '<span class="de-pill p-approved">Approved</span>'}
        ${_reimbursementBadge(e)}
      </div>
      <div class="de-actions">${owed ? `<button type="button" class="de-btn de-reimburse" onclick="window.APModule.markReimbursed('${jsId}')"><span class="material-icons">paid</span>Mark Reimbursed</button>` : ''}${creditBtn}${_personalBtns(e, jsId)}${!personal && _canReallocate(e) ? _reallocBtn(e, jsId) : ''}${_stockInExpenseBtn(e, jsId)}</div>
    </div>
  `;
}

// Shared safe receipt link (http(s)-only href — see _safeReceiptHref).
// TASK-9213: promoted from a bare underlined link to a tappable chip. It was
// the single most-clicked thing in the row and read as the least important.
// TASK-9855 slice 1 — the receipt opens through the Storage SDK on its PATH
// (receiptPath/receiptStoragePath, else the path inside receiptUrl), so
// storage.rules decide; the stored token URL is no longer the href.
// StorageFile.linkAttrs keeps the http(s)-only rule for a non-Storage link.
function _companyReceiptLink(e) {
  if (!e) return '';
  const SF = window.StorageFile;
  if (!SF) {
    const href = _safeReceiptHref(e.receiptUrl);
    return href ? `<a class="de-receipt" href="${href}" target="_blank" rel="noopener"><span class="material-icons">receipt</span>View receipt</a>` : '';
  }
  const fref = SF.fileRef(e, { urlKey: 'receiptUrl' });
  if (!fref || (!fref.path && !/^https?:\/\//i.test(String(fref.url || '')))) return '';
  return `<a class="de-receipt" ${SF.linkAttrs(e, { urlKey: 'receiptUrl' })}><span class="material-icons">receipt</span>View receipt</a>`;
}

function renderDriverExpenseRow(e) {
  const date = e.submittedAt?.toDate ? e.submittedAt.toDate() : (e.date?.toDate ? e.date.toDate() : new Date());
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const checked = APState.driverExpenseSelection?.has(e.id) ? 'checked' : '';
  const canSelect = (e.status === 'pending' || e.status === 'clarification_requested');
  // TASK-244 follow-up: gate ALL action buttons while OCR is still running
  // (status === 'pending_ocr'). Without these gates, AP could approve a
  // placeholder row whose amount/category will be overwritten when the
  // onCostEntryCreate trigger completes — silent data loss.
  // TASK-9778 — Receipts & Expenses view: a view-only login gets no approve /
  // reject / ask (the server refuses them), and nobody there marks a
  // reimbursement paid (that stays owner/admin + billing).
  const rcptView = APState.receiptsOnly === true;
  const personal = _personalOf(e); // TASK-9917 — decided: no review buttons
  const rcptCanAct = (!rcptView || _receiptsCanChange()) && !personal;
  const showApprove = rcptCanAct && e.status !== 'approved' && e.status !== 'rejected' && e.status !== 'pending_ocr';
  // TASK-527: "Ask Driver" is a driver-PIN clarification flow (SMS/email back to
  // the driver). It only applies to driver-submitted entries — the backend
  // clarification endpoint hard-rejects non-driver source. Hide it for staff.
  const showAskDriver = rcptCanAct && e.status === 'pending' && e.source === 'driver';
  const showReject = rcptCanAct && e.status !== 'rejected' && e.status !== 'pending_ocr';
  // TASK-9947 — nothing to pay when an approved refund covers it, and no
  // payout while a refund to the employee waits (the server refuses both).
  const showMarkReimbursed = !rcptView && e.reimbursementRequired === true && e.reimbursementStatus !== 'paid' && e.status === 'approved'
    && _reimbNet(e) > 0 && _employeeRefundCents(e.id, 'pending') === 0;
  // TASK-9947 — Refund / Return on a submitted expense (billing, not view-only).
  const refundLeft = Math.round((_driverExpenseAmount(e) - _creditedAgainst(e.id)) * 100);
  const showRefund = !rcptView && !personal && e.status !== 'rejected' && e.status !== 'pending_ocr' && refundLeft > 0
    && !(typeof window.canEditMoney === 'function' && window.canEditMoney() === false);
  // TASK-9778 (approve-txn follow-up) — a paid reimbursement can't be re-marked;
  // its payment details (check #, paid date, method, memo) are corrected here.
  const showCorrectPayment = !rcptView && e.reimbursementRequired === true && e.reimbursementStatus === 'paid';

  // TASK-9213 — `escJsAttr` not `escapeHtml`: these ids sit inside a JS string
  // literal inside an HTML attribute, and no HTML escaper closes that hole.
  const jsId = (window.escJsAttr ? window.escJsAttr(e.id || '') : String(e.id || ''));
  const cat = _deCategoryMeta(e);
  const meta = _deMetaHtml([
    { icon: 'event', text: dateStr },
    { icon: 'call_split', text: escapeHtml(_splitMetaText(e)) }, // TASK-9917 s4
    { text: cat.label ? escapeHtml(cat.label) : '' },
    { icon: 'sell', text: _allocLabelHtml(e) },
    { icon: 'local_gas_station', text: e.fuelGallons ? `${Number(e.fuelGallons).toFixed(1)} gal${e.fuelState ? ' · ' + escapeHtml(e.fuelState) : ''}` : '' },
    { icon: 'work', text: _expenseJobMeta(e), cls: 'de-m-job' },
    // TASK-9593 — the submitter picked "Other / not listed": a last-4 that is
    // not one of their assigned cards. Shown so AP checks it (and assigns the
    // card on the person page if it really is theirs). No card id is stored.
    { icon: 'credit_card', text: e.paymentCardUnlisted === true
      ? `<span style="color:#B45309;font-weight:600;" title="Not one of this person's assigned cards. Check it, and assign the card on their person page if it is theirs.">Card ••${escapeHtml(String(e.paymentCardLast4 || '????'))} not assigned to them — check</span>`
      : '' },
    { icon: 'storefront', text: _chargeAccountMetaHtml(e) }, // TASK-10141
  ]);

  return `
    <div class="de-row ${_deStatusClass(e)}${checked ? ' is-selected' : ''}">
      ${canSelect
        ? `<input type="checkbox" class="de-check driver-expense-select" data-id="${escapeHtml(e.id)}" ${checked} aria-label="Select expense for bulk approval" onchange="window.APModule.toggleDriverExpenseSelect('${jsId}', this.checked)">`
        : '<span class="de-check-spacer"></span>'}
      <div class="de-ic t-${cat.tint}"><span class="material-icons">${escapeHtml(cat.icon)}</span></div>
      <div class="de-body">
        <div class="de-title">${escapeHtml(e.description || cat.label || e.category || '—')}</div>
        <div class="de-meta">${meta}</div>
        ${_companyReceiptLink(e)}
        ${e.notes ? `<div class="de-note"><span class="material-icons" title="Driver note">sticky_note_2</span><span>${escapeHtml(String(e.notes)).replace(/\n/g, '<br>')}</span></div>` : ''}
        ${e.adminNotes ? `<div class="de-note de-note-admin"><span class="material-icons" title="Admin note">admin_panel_settings</span><span>${escapeHtml(e.adminNotes)}</span></div>` : ''}
        ${_personalNote(e)}
        ${rcptView ? '' : _refundLinesHtml(e)}
      </div>
      <div class="de-right">
        <div class="de-amt">$${_driverExpenseAmount(e).toFixed(2)}</div>
        ${personal ? _personalBadge(e) : _driverExpenseStatusBadge(e)}
        ${_reimbursementBadge(e)}
      </div>
      <div class="de-actions">
        ${showApprove ? `<button type="button" class="de-btn de-approve" onclick="window.APModule.approveDriverExpense('${jsId}')"><span class="material-icons">check</span>Approve</button>` : ''}
        ${showAskDriver ? `<button type="button" class="de-btn de-ask" onclick="window.APModule.askDriverClarify('${jsId}')"><span class="material-icons">help_outline</span>Ask Driver</button>` : ''}
        ${/* Primary action first: an approved+owed row shows only {Mark Reimbursed, Reject},
              and with Reject declared first that row led with the destructive button while
              every pending row led with the primary one. */''}
        ${showMarkReimbursed ? `<button type="button" class="de-btn de-reimburse" onclick="window.APModule.markReimbursed('${jsId}')"><span class="material-icons">paid</span>Mark Reimbursed</button>` : ''}
        ${showCorrectPayment ? `<button type="button" class="de-btn de-reimburse" onclick="window.APModule.correctReimbursement('${jsId}')"><span class="material-icons">edit</span>Correct payment details</button>` : ''}
        ${_personalBtns(e, jsId)}
        ${!rcptView && e.status === 'approved' && e.reimbursementRequired === true && e.reimbursementStatus !== 'paid' && _employeeRefundCents(e.id, 'pending') > 0 ? '<span class="de-note" style="font-weight:600;color:var(--text-primary);"><span class="material-icons">schedule</span>Approve or reject the refund first, then Mark Reimbursed pays the right amount.</span>' : ''}
        ${showRefund ? `<button type="button" class="de-btn" onclick="window.APModule.openVendorCredit('${jsId}')" title="Record money that came back on this receipt (a return or refund)"><span class="material-icons" style="color:#059669;">undo</span>Refund / Return</button>` : ''}
        ${showReject ? `<button type="button" class="de-btn de-reject" onclick="window.APModule.rejectDriverExpense('${jsId}')"><span class="material-icons">close</span>Reject</button>` : ''}
        ${!personal && _canReallocate(e) ? _reallocBtn(e, jsId) : ''}
        ${_stockInExpenseBtn(e, jsId)}
      </div>
    </div>
  `;
}

function toggleDriverGroup(personId) {
  APState.expandedDriverGroups[personId] = APState.expandedDriverGroups[personId] === false;
  renderDriverExpensesPanel(document.getElementById('ap-content-area'));
}

function toggleDriverExpenseSelect(id, checked) {
  if (!APState.driverExpenseSelection) APState.driverExpenseSelection = new Set();
  if (checked) APState.driverExpenseSelection.add(id);
  else APState.driverExpenseSelection.delete(id);
  renderDriverExpensesPanel(document.getElementById('ap-content-area'));
}

function selectAllPendingDriver() {
  if (!APState.driverExpenseSelection) APState.driverExpenseSelection = new Set();
  (APState.driverExpenses || []).forEach(e => {
    if (e.status === 'pending' || e.status === 'clarification_requested') {
      APState.driverExpenseSelection.add(e.id);
    }
  });
  renderDriverExpensesPanel(document.getElementById('ap-content-area'));
}

function clearDriverSelection() {
  APState.driverExpenseSelection = new Set();
  renderDriverExpensesPanel(document.getElementById('ap-content-area'));
}

async function _callExpenseFn(name, payload) {
  // Use page-init `functions, httpsCallable` from firebase-config.js (10.8.0).
  // Dynamic 10.7.1 imports break SDK identity (E2E 2026-05-12).
  return httpsCallable(functions, name)(payload);
}

// TASK-9778 (approve-txn) — the server decides each expense once, in a
// transaction. It is told the status this screen showed (expectedStatus), so a
// decision someone else made after the list loaded is refused, not overwritten.
// "Already decided" comes back as {applied:false, message} (this login's own
// double click / retry) or a failed-precondition with details.alreadyDecided
// (another login) — both are information, not a failure.
function _expectedStatusOf(costEntryId) {
  const e = _findExpense(costEntryId);
  return e && typeof e.status === 'string' ? e.status : undefined;
}
function _decisionToast(res, okText, okKind) {
  const d = res?.data || {};
  if (d.applied === false && d.message) window.showToast?.(d.message, 'info');
  // build/1017: the only login that can decide did it on its own entry; say so.
  else if (d.note && (d.selfApproved || d.selfRejected || d.selfPaid)) window.showToast?.(`${okText}. ${d.note}`, okKind);
  else window.showToast?.(okText, okKind);
}
// build/1017: a refusal (your own expense, no AR/AP access, login not linked)
// is a plain sentence from the server, shown as it is, not as a failure.
function _isDecisionRefusal(err) {
  return err?.code === 'functions/permission-denied' || err?.code === 'permission-denied';
}
function _decisionErrToast(err, failLead) {
  if (err?.details?.alreadyDecided) window.showToast?.(err.message, 'warning');
  else if (_isDecisionRefusal(err) && err?.message) window.showToast?.(err.message, 'warning');
  else window.showToast?.(failLead + (err?.message || 'try again'), 'error');
}

async function approveDriverExpense(costEntryId) {
  try {
    const res = await _callExpenseFn('approveDriverExpense', { tenantId: APState.tenantId, costEntryId, expectedStatus: _expectedStatusOf(costEntryId) });
    _decisionToast(res, 'Expense approved', 'success');
  } catch (err) {
    console.error('[AP] approveDriverExpense', err);
    _decisionErrToast(err, 'Failed to approve — ');
  }
}

async function rejectDriverExpense(costEntryId) {
  // COD r1 (Code Reviewer): capture the status the row showed BEFORE the note
  // dialog — the live listener can update the list while the dialog is open.
  const expectedStatus = _expectedStatusOf(costEntryId);
  // TASK-9917 — a company-card charge: Reject takes it off the books and nobody
  // owes it. Say so, and point at Personal (warn, never block: a duplicate
  // upload is a fair reject).
  const rx = _findExpense(costEntryId);
  const cardCharge = !!rx && rx.reimbursementRequired !== true && _canMarkPersonal(rx);
  const note = await _promptText(cardCharge
    ? 'Company card: rejecting drops it from the books and nobody pays it back. Personal? Cancel and use Personal. Note (optional):'
    : 'Reject expense — note (optional)', '');
  if (note === null) return;
  try {
    const res = await _callExpenseFn('rejectDriverExpense', { tenantId: APState.tenantId, costEntryId, adminNote: note, expectedStatus });
    _decisionToast(res, 'Expense rejected', 'info');
  } catch (err) {
    console.error('[AP] rejectDriverExpense', err);
    _decisionErrToast(err, 'Failed to reject — ');
  }
}

async function askDriverClarify(costEntryId) {
  const note = await _promptText('Add a note for the driver (optional, e.g. "Is this the company card?")', '');
  if (note === null) return;
  try {
    const res = await _callExpenseFn('requestExpenseClarification', {
      tenantId: APState.tenantId, costEntryId, customMessage: note,
    });
    _decisionToast(res, 'SMS sent — driver has 7 days to clarify', 'success');
  } catch (err) {
    console.error('[AP] askDriverClarify', err);
    if (err?.details?.alreadyDecided) window.showToast?.(err.message, 'warning');
    else window.showToast?.('Failed to send SMS — ' + (err?.message || 'check driver has phone on file'), 'error');
  }
}

async function markReimbursed(costEntryId) {
  // TASK-9947 — say what will be paid when a refund nets it down.
  const exp = _findExpense(costEntryId);
  const refunded = exp ? _employeeRefundCents(costEntryId, 'approved') : 0;
  const subtitle = exp && refunded > 0
    ? `Pay $${_reimbNet(exp).toFixed(2)} ($${_driverExpenseAmount(exp).toFixed(2)} less $${(refunded / 100).toFixed(2)} refunded to them).`
    : '';
  // Method picker via lightweight modal
  const method = await _pickMethod(subtitle);
  if (!method) return;
  let checkNumber = null;
  if (method === 'check') {
    checkNumber = await _promptText('Check number', '');
    if (checkNumber === null) return;
  }
  try {
    const res = await _callExpenseFn('markExpenseReimbursed', {
      tenantId: APState.tenantId, costEntryId, method, checkNumber: checkNumber || null,
    });
    _decisionToast(res, 'Marked reimbursed', 'success');
  } catch (err) {
    console.error('[AP] markReimbursed', err);
    _decisionErrToast(err, 'Failed to mark — ');
  }
}

// ---- TASK-9592-FU — Re-allocate an expense (audited, server-side) ----
// TASK-9592 locked costAllocationId + an allocation-derived vehicle against
// client writes, so the fix for "allocation says truck 14, driver fueled 12"
// goes through the reallocateExpense callable: it re-derives the vehicle,
// refuses splits / credits / non-expense rows, and writes an audit row with
// the reason in the same transaction. The button mirrors what the server
// accepts so nobody is offered an action that can only fail.
function _canReallocate(e) {
  if (!e || !['driver', 'staff', 'admin'].includes(e.source)) return false;
  if (e.status === 'pending_ocr') return false;      // receipt still being read
  if (_isCreditEntry(e) || e.entryType === 'credit') return false; // follows its original charge
  if (Array.isArray(e.allocations) && e.allocations.length > 0) return false; // split receipt
  // TASK-9778 — Receipts & Expenses: driver / staff entries, not view-only
  // (reallocateExpense's receipts arm). Billing's canEditMoney does not apply.
  if (APState.receiptsOnly) return e.source !== 'admin' && _receiptsCanChange();
  if (typeof window.canEditMoney === 'function' && window.canEditMoney() === false) return false; // view-only
  return true;
}
function _reallocBtn(e, jsId) {
  if (!_canReallocate(e)) return '';
  return `<button type="button" class="de-btn de-realloc" onclick="window.APModule.openReallocateExpense('${jsId}')" title="Move this expense to a different cost allocation / truck"><span class="material-icons">swap_horiz</span>Re-allocate</button>`;
}
function _findExpense(id) {
  return (APState.driverExpenses || []).find(x => x.id === id)
    || (APState.companyExpenses || []).find(x => x.id === id)
    || (APState.personalExtra || []).find(x => x.id === id) || null; // TASK-9917 s4
}
function _reallocVehicleLabel(a) {
  const unit = a.unit || a.id;
  return a.nickname ? `${a.nickname} — #${unit}` : `${a._assetType === 'trailer' ? 'Trailer' : 'Truck'} #${unit}`;
}
let _reallocTarget = null;
let _reallocExpectedAlloc = null; // TASK-9778 (approve-txn) — the allocation this modal showed
let _reallocExpectedVeh = null;   // ...and the vehicle (COD r2)
function openReallocateExpense(costEntryId) {
  const e = _findExpense(costEntryId);
  if (!e) { window.showToast?.('Expense not found — refresh and try again', 'error'); return; }
  if (!_canReallocate(e)) { window.showToast?.('This expense cannot be re-allocated here', 'warning'); return; }
  closeReallocateExpense();
  _reallocTarget = e.id;
  _reallocExpectedAlloc = e.costAllocationId || null;
  _reallocExpectedVeh = e.vehicleId || null;
  const allocs = (APState.costAllocations || [])
    .filter(a => a && a.id && a.active !== false)
    .sort((x, y) => String(x.label || x.id).localeCompare(String(y.label || y.id)));
  const vehicles = (APState.assets || []).filter(a => a._assetType === 'truck' || a._assetType === 'trailer')
    .sort((x, y) => _reallocVehicleLabel(x).localeCompare(_reallocVehicleLabel(y)));
  const cur = _currentAllocLabel(e) || 'None';
  const curVeh = e.vehicleDisplay || e.vehicleId || 'None';
  const allocOpts = allocs.map(a => `<option value="${escapeHtml(a.id)}" data-type="${escapeHtml(a.type || '')}" data-ref="${escapeHtml(a.refId == null ? '' : String(a.refId))}"${a.id === e.costAllocationId ? ' selected' : ''}>${escapeHtml(a.label || a.id)}</option>`).join('');
  const vehOpts = vehicles.map(v => `<option value="${escapeHtml(v._assetType + ':' + v.id)}">${escapeHtml(_reallocVehicleLabel(v))}</option>`).join('');
  const overlay = document.createElement('div');
  overlay.id = 'realloc-modal';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:11000;padding:16px;';
  overlay.innerHTML = `
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="realloc-title" style="width:100%;max-width:480px;max-height:90vh;max-height:90dvh;overflow-y:auto;padding:18px;background:#fff;border-radius:12px;box-sizing:border-box;">
      <h3 id="realloc-title" style="margin:0 0 4px;font-size:1.05rem;font-weight:700;color:var(--text-primary);">Re-allocate expense</h3>
      <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:12px;">${escapeHtml(e.description || e.category || 'Expense')} · $${_driverExpenseAmount(e).toFixed(2)}</div>
      <div style="font-size:0.85rem;color:var(--text-primary);background:#F8FAFC;border:1px solid var(--border-light);border-radius:8px;padding:8px 10px;margin-bottom:12px;">
        Now: <strong>${escapeHtml(cur)}</strong> · Vehicle: <strong>${escapeHtml(curVeh)}</strong>
      </div>
      <label for="realloc-alloc" style="display:block;font-size:0.85rem;font-weight:600;margin-bottom:4px;color:var(--text-primary);">Move to</label>
      <select id="realloc-alloc" style="width:100%;padding:10px;border:1px solid var(--border-light);border-radius:8px;font-size:16px;margin-bottom:10px;" onchange="window.APModule._reallocSync()">
        ${allocs.length ? '' : '<option value="">No cost allocations set up</option>'}${allocOpts}
      </select>
      <div id="realloc-veh-derived" style="display:none;font-size:0.85rem;color:var(--text-primary);margin-bottom:10px;"><span class="material-icons" style="font-size:16px;vertical-align:-3px;color:var(--brand-blue);">local_shipping</span> Vehicle comes from this allocation.</div>
      <div id="realloc-veh-field" style="display:none;margin-bottom:10px;">
        <label for="realloc-vehicle" style="display:block;font-size:0.85rem;font-weight:600;margin-bottom:4px;color:var(--text-primary);">Vehicle</label>
        <select id="realloc-vehicle" style="width:100%;padding:10px;border:1px solid var(--border-light);border-radius:8px;font-size:16px;">
          <option value="__keep__">${e.vehicleSource === 'allocation'
            ? `No vehicle (${escapeHtml(curVeh)} came from the old allocation)`
            : `Keep as is (${escapeHtml(curVeh)})`}</option>
          <option value="__none__">No vehicle</option>
          ${vehOpts}
        </select>
      </div>
      <label for="realloc-reason" style="display:block;font-size:0.85rem;font-weight:600;margin-bottom:4px;color:var(--text-primary);">Why? <span style="font-weight:400;color:var(--text-secondary);">(required — kept in the audit log)</span></label>
      <textarea id="realloc-reason" rows="2" maxlength="500" placeholder="e.g. Driver fueled truck 12, not 14" style="width:100%;padding:10px;border:1px solid var(--border-light);border-radius:8px;font-size:16px;box-sizing:border-box;"></textarea>
      <div id="realloc-error" role="alert" style="display:none;color:#B91C1C;font-size:0.85rem;margin-top:8px;"></div>
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;flex-wrap:wrap;">
        <button type="button" class="btn btn-secondary" onclick="window.APModule.closeReallocateExpense()">Cancel</button>
        <button type="button" class="btn btn-primary" id="realloc-save" onclick="window.APModule.saveReallocateExpense()">Re-allocate</button>
      </div>
    </div>`;
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) closeReallocateExpense(); });
  document.body.appendChild(overlay);
  _reallocSync();
}
function _reallocSync() {
  const sel = document.getElementById('realloc-alloc');
  const opt = sel && sel.selectedOptions && sel.selectedOptions[0];
  const type = opt ? opt.getAttribute('data-type') : '';
  const isVehicleAlloc = type === 'truck' || type === 'trailer';
  const derived = document.getElementById('realloc-veh-derived');
  const field = document.getElementById('realloc-veh-field');
  if (derived) derived.style.display = (opt && isVehicleAlloc) ? '' : 'none';
  if (field) field.style.display = (opt && opt.value && !isVehicleAlloc) ? '' : 'none';
}
function closeReallocateExpense() {
  document.getElementById('realloc-modal')?.remove();
  _reallocTarget = null;
}
async function saveReallocateExpense() {
  const errEl = document.getElementById('realloc-error');
  const showErr = (m) => { if (errEl) { errEl.textContent = m; errEl.style.display = ''; } };
  const expenseId = _reallocTarget;
  const sel = document.getElementById('realloc-alloc');
  const costAllocationId = sel ? sel.value : '';
  const opt = sel && sel.selectedOptions && sel.selectedOptions[0];
  const type = opt ? opt.getAttribute('data-type') : '';
  const reason = (document.getElementById('realloc-reason')?.value || '').trim();
  if (!expenseId) { closeReallocateExpense(); return; }
  if (!costAllocationId) { showErr('Pick a cost allocation.'); return; }
  if (reason.length < 3) { showErr('Say why you are re-allocating it (a few words).'); document.getElementById('realloc-reason')?.focus(); return; }
  const payload = { tenantId: APState.tenantId, expenseId, costAllocationId, reason, expectedCostAllocationId: _reallocExpectedAlloc, expectedVehicleId: _reallocExpectedVeh };
  if (type !== 'truck' && type !== 'trailer') {
    const v = document.getElementById('realloc-vehicle')?.value || '__keep__';
    if (v === '__none__') payload.vehicle = null;
    else if (v !== '__keep__') {
      const i = v.indexOf(':');
      payload.vehicle = { type: v.slice(0, i), id: v.slice(i + 1) };
    }
  }
  const btn = document.getElementById('realloc-save');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const res = await _callExpenseFn('reallocateExpense', payload);
    const data = res?.data || {};
    closeReallocateExpense();
    const credMsg = data.creditsMoved > 0 ? ` (its ${data.creditsMoved === 1 ? 'refund / credit' : data.creditsMoved + ' refunds / credits'} moved too)` : '';
    window.showToast?.(data.unchanged ? (data.message || 'Already on that allocation — nothing changed') : 'Expense re-allocated' + credMsg, data.unchanged ? 'info' : 'success');
  } catch (err) {
    console.error('[AP] reallocateExpense', err);
    showErr('Not saved — ' + (err?.message || 'try again'));
    if (btn) { btn.disabled = false; btn.textContent = 'Re-allocate'; }
  }
}

async function bulkApproveDriver() {
  const ids = Array.from(APState.driverExpenseSelection || []);
  if (!ids.length) {
    window.showToast?.('Select expenses to approve first', 'error');
    return;
  }
  const selectedExpenses = (APState.driverExpenses || []).filter(e => APState.driverExpenseSelection.has(e.id));
  const total = selectedExpenses.reduce((s, e) => s + _driverExpenseAmount(e), 0);
  const ok = await _confirmBulkApprove(ids.length, total);
  if (!ok) return;
  try {
    const res = await _callExpenseFn('bulkApproveDriverExpenses', {
      tenantId: APState.tenantId, costEntryIds: ids,
    });
    const data = res?.data || {};
    APState.driverExpenseSelection = new Set();
    const already = data.alreadyApproved ? ` (${data.alreadyApproved} already approved by someone)` : '';
    // build/1017: rows this login may not approve (its own, or not linked to
    // the Roster) are skipped by the server; say why in plain words.
    const notYours = data.notYours ? ` ${data.notYours} need another reviewer (${data.notYoursWhy || 'your own expense'}).` : '';
    window.showToast?.(`${data.approved || 0} approved · ${data.skipped || 0} skipped${already}.${notYours}`, data.notYours ? 'warning' : 'success');
  } catch (err) {
    console.error('[AP] bulkApproveDriver', err);
    if (_isDecisionRefusal(err) && err?.message) window.showToast?.(err.message, 'warning');
    else window.showToast?.('Bulk approve failed — ' + (err?.message || 'try again'), 'error');
  }
}

function consolidateDriverExpenses() {
  // Pre-filter the reconciliation flow to submitted expenses.
  // reconciliation.js reads window.__reconciliationPreFilter on init.
  // TASK-527: cover staff-submitted entries too (drive the same banner/context).
  window.__reconciliationPreFilter = { source: 'driver', sources: ['driver', 'staff'] };
  // 🔴 THIS BUTTON WAS DEAD IN PRODUCTION until 2026-09-09. It called
  // navigateToView('reconciliation'), and there is no `case 'reconciliation'` in
  // app.js's view switch — so it fell to the default branch and rendered
  // "🚧 Coming Soon — The reconciliation module is being built." Reconciliation
  // has always lived INSIDE bi.js as a tab, never as a top-level view. Found by
  // the TASK-4867 lane while adding the Billing-strip pill; this is the same
  // workflow Lacey asked for (reconciling card statements against driver
  // expenses), reached from the other end.
  //
  // Reuses TASK-4867's hand-off rather than inventing a second path: set the
  // target tab, navigate to BI, and initBI() consumes it synchronously right
  // after it writes its own markup. The pre-filter above is untouched — it is
  // read separately by reconciliation.js on init.
  if (window.navigateToView) {
    window._billingBiDeepLink = { tab: 'reconciliation', at: Date.now() };
    window.navigateToView('bi');
  } else {
    window.location.hash = '#/bi';
  }
}

function openDriverExpenseSettings() {
  window.APModule._showDriverExpenseSettingsModal?.();
}

// ---- TASK-9778 (approve-txn follow-up) — Correct payment details ----
// Server: correctExpenseReimbursement (billing / owner, transactional, payment
// metadata only, reason required, audit row old -> new). Paid date is sent
// ONLY when changed: the stored paid time is the click time, so re-sending the
// same calendar day would read as a change.
function _ymdLocal(ts) {
  const d = ts?.toDate ? ts.toDate() : (ts instanceof Date ? ts : null);
  if (!d || isNaN(d)) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function correctReimbursement(costEntryId) {
  const e = _findExpense(costEntryId);
  if (!e) { window.showToast?.('Expense not found — refresh and try again', 'error'); return; }
  document.getElementById('reimb-fix-modal')?.remove();
  const paidYmd = _ymdLocal(e.reimbursementPaidAt);
  const method = e.reimbursementMethod || 'check';
  const opt = (v, l) => `<option value="${v}" ${method === v ? 'selected' : ''}>${l}</option>`;
  const field = 'width:100%;padding:10px;border:1px solid var(--border-light);border-radius:8px;font-size:16px;box-sizing:border-box;';
  const lab = 'display:block;font-size:0.85rem;font-weight:600;margin:10px 0 4px;color:var(--text-primary);';
  const overlay = document.createElement('div');
  overlay.id = 'reimb-fix-modal';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:11000;';
  overlay.innerHTML = `
    <div class="modal-content" style="width:100%;max-width:440px;margin:0 16px;padding:18px;background:#fff;border-radius:12px;max-height:100vh;max-height:100dvh;overflow:auto;">
      <h3 style="margin:0 0 4px;font-size:1rem;font-weight:700;">Correct payment details</h3>
      <p style="margin:0 0 6px;font-size:0.85rem;color:var(--text-secondary);">Fixes how this reimbursement was paid. The amount, who it's for and its paid status stay as they are.</p>
      <label for="reimb-fix-method" style="${lab}">Method</label>
      <select id="reimb-fix-method" style="${field}">${opt('check', 'Check')}${opt('ach', 'ACH / Direct Deposit')}${opt('cash', 'Cash')}${opt('next_payroll', 'Next Payroll')}</select>
      <label for="reimb-fix-check" style="${lab}">Check number</label>
      <input id="reimb-fix-check" type="text" maxlength="50" value="${escapeHtml(e.reimbursementCheckNumber || '')}" style="${field}">
      <label for="reimb-fix-date" style="${lab}">Paid date</label>
      <input id="reimb-fix-date" type="date" value="${escapeHtml(paidYmd)}" style="${field}">
      <label for="reimb-fix-note" style="${lab}">Memo</label>
      <input id="reimb-fix-note" type="text" maxlength="500" value="${escapeHtml(e.reimbursementNote || '')}" style="${field}">
      <label for="reimb-fix-reason" style="${lab}">Why? <span style="font-weight:400;color:var(--text-secondary);">(required — kept in the audit log)</span></label>
      <textarea id="reimb-fix-reason" rows="2" maxlength="500" placeholder="e.g. Wrong check number typed" style="${field}"></textarea>
      <div id="reimb-fix-error" role="alert" style="display:none;color:#B91C1C;font-size:0.85rem;margin-top:8px;"></div>
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;flex-wrap:wrap;">
        <button type="button" class="btn btn-secondary" id="reimb-fix-cancel">Cancel</button>
        <button type="button" class="btn btn-primary" id="reimb-fix-save">Save correction</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const $ = (id) => overlay.querySelector('#' + id);
  const syncCheck = () => { const isCheck = $('reimb-fix-method').value === 'check'; $('reimb-fix-check').disabled = !isCheck; if (!isCheck) $('reimb-fix-check').value = ''; };
  $('reimb-fix-method').onchange = syncCheck;
  syncCheck();
  const close = () => overlay.remove();
  $('reimb-fix-cancel').onclick = close;
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  $('reimb-fix-save').onclick = async () => {
    const err = $('reimb-fix-error');
    const showErr = (m) => { err.textContent = m; err.style.display = ''; };
    const reason = $('reimb-fix-reason').value.trim();
    if (reason.length < 3) { showErr('Say why you are correcting it (a few words).'); $('reimb-fix-reason').focus(); return; }
    // Only the fields edited here, each with the value this modal showed, so a
    // correction someone else saved meanwhile is refused, not overwritten.
    const payload = { tenantId: APState.tenantId, costEntryId, reason };
    const shownCheck = e.reimbursementCheckNumber || null;
    const shownNote = e.reimbursementNote || null;
    const m = $('reimb-fix-method').value;
    if (m !== (e.reimbursementMethod || 'check')) { payload.method = m; payload.expectedMethod = e.reimbursementMethod || null; }
    const cn = $('reimb-fix-check').value.trim() || null;
    if (cn !== shownCheck && !(m !== 'check' && cn === null)) { payload.checkNumber = cn; payload.expectedCheckNumber = shownCheck; }
    const nt = $('reimb-fix-note').value.trim() || null;
    if (nt !== shownNote) { payload.note = nt; payload.expectedNote = shownNote; }
    const d = $('reimb-fix-date').value;
    if (d && d !== paidYmd) {
      payload.paidDate = d;
      payload.expectedPaidAtMs = e.reimbursementPaidAt?.toMillis ? e.reimbursementPaidAt.toMillis() : null;
    }
    if (!('method' in payload || 'checkNumber' in payload || 'note' in payload || 'paidDate' in payload)) { showErr('Nothing changed.'); return; }
    const btn = $('reimb-fix-save');
    btn.disabled = true; btn.textContent = 'Saving…';
    try {
      const res = await _callExpenseFn('correctExpenseReimbursement', payload);
      close();
      _decisionToast(res, 'Payment details corrected', 'success');
    } catch (ex) {
      console.error('[AP] correctReimbursement', ex);
      showErr('Not saved — ' + (ex?.message || 'try again'));
      btn.disabled = false; btn.textContent = 'Save correction';
    }
  };
}

// ============================================
// TASK-9917 — PERSONAL CHARGES ON THE COMPANY CARD
// ============================================
// Rows come from the two expense subscriptions AP already holds (driver/staff
// and admin-recorded). Server: markExpensePersonal / clearExpensePersonal
// (owner or billing module, transactional, one audit row each).
function _personalEntries() {
  const seen = new Set();
  const out = [];
  for (const e of [...(APState.driverExpenses || []), ...(APState.companyExpenses || []), ...(APState.personalExtra || [])]) {
    if (!_personalOf(e) || seen.has(e.id)) continue;
    seen.add(e.id);
    out.push(e);
  }
  return out;
}

function renderPersonalChargesPanel(area) {
  if (!area) return;
  const all = _personalEntries();
  const employee = all.filter(e => _personalOf(e).payer === 'employee');
  const owner = all.filter(e => _personalOf(e).payer === 'owner');
  const owedTotal = employee.reduce((s, e) => s + _personalOutstanding(e), 0);
  const ownerTotal = owner.reduce((s, e) => s + _driverExpenseAmount(e), 0);
  const row = (e) => (e.source === 'driver' || e.source === 'staff') ? renderDriverExpenseRow(e) : renderCompanyExpenseRow(e);
  const waiting = _ownerDrawWaiting();
  if (Date.now() - (APState._odrAt || 0) > 60000) _loadOwnerDrawRequests(); // COD r1: another login's ask shows up
  const byPerson = new Map();
  for (const e of employee) {
    const p = _personalOf(e);
    const key = p.personId || p.personName || '_unknown';
    if (!byPerson.has(key)) byPerson.set(key, { name: p.personName || 'Employee', items: [] });
    byPerson.get(key).items.push(e);
  }
  let html = `
    <div class="ap-summary-bar">
      <div class="ap-summary-item">
        <span class="ap-summary-label">Owed back by employees</span>
        <span class="ap-summary-value" style="color:#991B1B;">$${owedTotal.toFixed(2)}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Owner draws (personal)</span>
        <span class="ap-summary-value">$${ownerTotal.toFixed(2)}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Charges</span>
        <span class="ap-summary-value">${all.length}</span>
      </div>
    </div>
    <p style="margin:10px 0 14px;font-size:0.85rem;color:var(--text-secondary);">Paid on the company card but not a business cost. They stay off the profit &amp; loss; owner charges count as owner draws, and what an employee owes stays here until it is repaid.</p>`;
  if (APState._prMod) html += APState._prMod.ownerDrawNoticesHtml(_odrNotices(), { escapeHtml, jsAttr: _jsAttr });
  if (waiting.length && APState._prMod) html += APState._prMod.ownerDrawWaitingHtml(waiting, { isOwner: _isOwnerLogin(), me: window.AppState?.user?.uid || null, escapeHtml, jsAttr: _jsAttr });
  if (!all.length && !waiting.length && !_odrNotices().length) {
    html += `<div class="empty-state" style="padding:40px 20px;text-align:center;"><div class="empty-state-title">No personal charges</div><div class="empty-state-text">Use <strong>Personal</strong> on an expense paid with the company card — here, or in BI → P&amp;L → Charges for statement and fuel-card imports.</div></div>`;
    area.innerHTML = html;
    return;
  }
  for (const g of byPerson.values()) {
    const owed = g.items.reduce((s, e) => s + _personalOutstanding(e), 0);
    // TASK-9917 slice 2 — what this person has paid back so far, across their charges.
    const repaid = g.items.reduce((s, e) => s + (Number(_personalOf(e).repaidAmount) || 0), 0);
    html += `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px;flex-wrap:wrap;margin:14px 0 6px;">
        <div style="font-weight:700;color:var(--text-primary);">${escapeHtml(g.name)}</div>
        <div style="font-weight:700;color:${owed > 0 ? '#991B1B' : '#166534'};">${owed > 0 ? `owes $${owed.toFixed(2)}` : 'paid up'}${repaid > 0 ? ` <span style="font-weight:500;color:var(--text-secondary);">· repaid $${repaid.toFixed(2)}</span>` : ''}</div>
      </div>
      <div class="de-well" style="border-radius:12px;">${g.items.map(row).join('')}</div>`;
  }
  if (owner.length) {
    html += `
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin:18px 0 6px;">
        <div style="font-weight:700;color:var(--text-primary);">Owner</div>
        <div style="font-weight:700;color:var(--text-primary);">$${ownerTotal.toFixed(2)} in draws</div>
      </div>
      <div class="de-well" style="border-radius:12px;">${owner.map(row).join('')}</div>`;
  }
  area.innerHTML = html;
}

// Only the owner login may count a charge as an owner draw (server enforces).
function _isOwnerLogin() {
  const td = window.AppState?.tenantData || {};
  return !!(td.ownerId && td.ownerId === window.AppState?.user?.uid) || window.AppState?.isDemo === true;
}
async function markExpensePersonal(costEntryId) {
  const e = _findExpense(costEntryId);
  if (!e) return;
  if (_apMoneyEditBlocked()) return;
  // Capture the status the row showed before the dialog (the listener can move it).
  const expectedStatus = _expectedStatusOf(costEntryId);
  // TASK-9917 s4 — the dialog (who pays, all or part of it) and the call live in
  // the LAZY personalRepay.js, shared with BI → P&L → Charges.
  try {
    const PR = await _personalRepayMod();
    await PR.markPersonal(e, { ..._repayDeps(), people: APState.people || [], isOwner: _isOwnerLogin(), expectedStatus, canAskOwner: true, onOwnerRequest: _loadOwnerDrawRequests });
  } catch (err) {
    console.error('[AP] personalRepay load', err);
    window.showToast?.('Could not open the Personal form — refresh and try again.', 'error');
  }
}

// TASK-9917 — the owner path. Only the owner login counts a charge as an owner
// draw; other billing logins ASK (Personal → "The owner") and the owner answers
// in Personal Charges (second sign-off). Server-only docs → callable list; the
// rows + answer live in the lazy personalRepay.js (perf budget).
async function _loadOwnerDrawRequests() {
  APState.ownerDrawNotices = [];
  if (APState.receiptsOnly === true || !_canDecidePersonal()) { APState.ownerDrawRequests = []; return; }
  // COD: this load's id. A newer load, another company or the receipts view
  // (which zeroes it) makes the answer stale — dropped, never shown.
  const at = APState._odrAt = Date.now();
  try {
    const d = (await _callExpenseFn('ownerDrawRequest', { tenantId: APState.tenantId, action: 'list' }))?.data || {};
    const reqs = Array.isArray(d.requests) ? d.requests : [], ns = Array.isArray(d.notices) ? d.notices : [];
    if ((reqs.length || ns.length || d.scanCapped) && !APState._prMod) APState._prMod = await _personalRepayMod();
    if (at !== APState._odrAt) return;
    APState.ownerDrawRequests = reqs;
    APState.ownerDrawNotices = ns;
    // TASK-9917 expiry follow-up — the owner's 3-day reminder and the asker's
    // lapsed-ask notice: toasts once per AP visit, in the lazy personalRepay.js.
    APState._prMod?.ownerDrawToasts(d, { st: APState, tenantId: APState.tenantId, overdue: _isOwnerLogin() ? _ownerDrawOverdue().length : 0, notices: ns, toast: window.showToast });
  } catch (err) {
    console.warn('[AP] ownerDrawRequest list', err?.message);
    if (at !== APState._odrAt) return;
    APState.ownerDrawRequests = [];
  }
  if (['personalCharges', 'driverExpenses', 'companyExpenses'].includes(APState.filter)) renderAPView();
  else renderFilterPills();
}
function _odrNotices() {
  return APState.ownerDrawNotices || [];
}
function _ownerDrawWaiting() {
  return (APState.ownerDrawRequests || []).filter(r => r && r.status === 'open' && !_personalOf(_findExpense(r.costEntryId) || {}));
}
function _ownerDrawOverdue() {
  return _ownerDrawWaiting().filter(r => Number.isFinite(r.remindedAtMs));
}
function _ownerDrawReqFor(id) {
  return id ? _ownerDrawWaiting().find(r => r.costEntryId === id) || null : null;
}
async function answerOwnerDraw(costEntryId, action, gen) {
  if (_apMoneyEditBlocked()) return;
  const r = action === 'seen' ? _odrNotices().find(x => x.costEntryId === costEntryId && (x.gen || '') === gen) : (APState.ownerDrawRequests || []).find(x => x && x.costEntryId === costEntryId);
  if (!r) return;
  try {
    await (await _personalRepayMod()).answerOwnerDraw(r, action, _repayDeps());
  } catch (err) {
    console.error('[AP] personalRepay load', err);
  }
  _loadOwnerDrawRequests();
}

async function clearExpensePersonal(costEntryId) {
  if (_apMoneyEditBlocked()) return;
  const reason = await _promptText('Not personal after all — why? (kept in the history)', '');
  if (reason === null) return;
  if (reason.length < 3) { window.showToast?.('Say why in a few words.', 'warning'); return; }
  try {
    const res = await _callExpenseFn('clearExpensePersonal', { tenantId: APState.tenantId, costEntryId, reason });
    _decisionToast(res, 'Back to a business expense', 'info');
  } catch (err) {
    console.error('[AP] clearExpensePersonal', err);
    _decisionErrToast(err, 'Could not undo it — ');
  }
}

// TASK-9917 slice 2 — record / undo a repayment. The dialog and the calls live
// in the LAZY views/personalRepay.js (perf budget: nothing there paints a
// screen); ap.js only hands it the helpers it needs (pitfall #1: no
// cross-import between ?v= modules, so deps are passed, not imported).
function _personalRepayMod() {
  return __ddChunkWrap(import('./personalRepay.js?v=9917701'));
}
function _repayDeps() {
  return {
    tenantId: APState.tenantId,
    personalOf: _personalOf,
    outstanding: _personalOutstanding,
    amountOf: _driverExpenseAmount,
    escapeHtml,
    toast: (m, k) => window.showToast?.(m, k),
    callFn: _callExpenseFn,
    decisionToast: _decisionToast,
    decisionErrToast: _decisionErrToast,
    promptText: _promptText,
  };
}
async function recordPersonalRepayment(costEntryId) {
  const e = _findExpense(costEntryId);
  if (!e || !_canRecordRepayment(e)) return;
  if (_apMoneyEditBlocked()) return;
  try {
    (await _personalRepayMod()).recordRepayment(e, _repayDeps());
  } catch (err) {
    console.error('[AP] personalRepay load', err);
    window.showToast?.('Could not open the repayment form — refresh and try again.', 'error');
  }
}
async function undoPersonalRepayment(costEntryId, repaymentId) {
  if (_apMoneyEditBlocked()) return;
  const rep = _personalRepayments(_findExpense(costEntryId) || {}).find(r => r.id === repaymentId);
  if (!rep || rep.voidedAt) return;
  try {
    (await _personalRepayMod()).undoRepayment(costEntryId, rep, _repayDeps());
  } catch (err) {
    console.error('[AP] personalRepay load', err);
    window.showToast?.('Could not undo the repayment — refresh and try again.', 'error');
  }
}

// ---- Lightweight prompts (avoid pulling another modal library) ----
function _promptText(label, prefill) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal';
    overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:11000;';
    overlay.innerHTML = `
      <div class="modal-content" style="max-width:420px;padding:18px;background:#fff;border-radius:12px;">
        <h3 style="margin:0 0 10px;font-size:1rem;font-weight:700;">${escapeHtml(label)}</h3>
        <textarea id="_prompt-input" rows="3" style="width:100%;padding:8px;border:1px solid var(--border-light);border-radius:8px;">${escapeHtml(prefill || '')}</textarea>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:10px;">
          <button class="btn btn-secondary" id="_prompt-cancel">Cancel</button>
          <button class="btn btn-primary" id="_prompt-ok">OK</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const input = overlay.querySelector('#_prompt-input');
    input.focus();
    overlay.querySelector('#_prompt-cancel').onclick = () => { overlay.remove(); resolve(null); };
    overlay.querySelector('#_prompt-ok').onclick = () => {
      const val = input.value.trim();
      overlay.remove();
      resolve(val);
    };
  });
}

function _pickMethod(subtitle) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal';
    overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:11000;';
    overlay.innerHTML = `
      <div class="modal-content" style="max-width:420px;padding:18px;background:#fff;border-radius:12px;">
        <h3 style="margin:0 0 10px;font-size:1rem;font-weight:700;">How did you reimburse?</h3>
        ${subtitle ? `<p id="_method-net" style="margin:0 0 10px;font-size:0.9rem;color:var(--text-primary);font-weight:600;">${escapeHtml(subtitle)}</p>` : ''}
        <div style="display:flex;flex-direction:column;gap:8px;">
          <button class="btn btn-secondary" data-method="check">Check</button>
          <button class="btn btn-secondary" data-method="ach">ACH / Direct Deposit</button>
          <button class="btn btn-secondary" data-method="cash">Cash</button>
          <button class="btn btn-secondary" data-method="next_payroll">Next Payroll</button>
        </div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-secondary" id="_method-cancel">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.querySelectorAll('[data-method]').forEach(btn => {
      btn.onclick = () => { const m = btn.getAttribute('data-method'); overlay.remove(); resolve(m); };
    });
    overlay.querySelector('#_method-cancel').onclick = () => { overlay.remove(); resolve(null); };
  });
}

function _confirmBulkApprove(count, total) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal';
    overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:11000;';
    overlay.innerHTML = `
      <div class="modal-content" style="max-width:460px;padding:20px;background:#fff;border-radius:12px;">
        <h3 style="margin:0 0 12px;font-size:1.05rem;font-weight:700;text-align:center;">Approve ${count} expense${count === 1 ? '' : 's'}?</h3>
        <p style="margin:0 0 8px;text-align:center;color:var(--text-secondary);">Total: <strong style="color:var(--text-primary);font-size:1.2rem;">$${(Number(total) || 0).toFixed(2)}</strong></p>
        <p style="margin:0 0 16px;text-align:center;color:var(--text-secondary);font-size:0.85rem;">Approved expenses flow into BI and (if reimbursement was flagged) join the payout queue.</p>
        <div style="display:flex;justify-content:center;gap:8px;">
          <button class="btn btn-secondary" id="_bulk-cancel">Cancel</button>
          <button class="btn btn-primary" id="_bulk-ok">Approve ${count}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.querySelector('#_bulk-cancel').onclick = () => { overlay.remove(); resolve(false); };
    overlay.querySelector('#_bulk-ok').onclick = () => { overlay.remove(); resolve(true); };
  });
}

// ---- Driver-expense settings + cost allocations CRUD modal ----
async function _showDriverExpenseSettingsModal() {
  const tenant = APState.tenantId;
  if (!tenant) return;
  const settingsSnap = await getDoc(doc(db, 'tenants', tenant));
  const settings = settingsSnap.data()?.settings?.driverExpenses || {};
  const allocSnap = await getDocs(collection(db, 'tenants', tenant, 'costAllocations'));
  const allocations = allocSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));

  // TASK-4606 — the drift banner has to diff ASSETS too, and the lazy fetch that
  // populates assetRefs lives in the empty-state CTA, which a tenant that already
  // has allocations never renders. Without this, a standalone asset missing its
  // row would be invisible on exactly the accounts the banner exists for.
  // Awaited inline (the modal is already async and awaits two reads); failure is
  // non-fatal and just narrows the banner to trucks/trailers.
  if (!APState._fleetCounts._loaded) {
    try {
      const assetSnap = await getDocs(collection(db, 'tenants', tenant, 'assets'));
      const activeAssets = assetSnap.docs.filter(d => d.data().active !== false);
      APState._fleetCounts.assets = activeAssets.length;
      APState._fleetCounts.assetRefs = activeAssets.map(d => ({ id: d.id, label: (d.data() || {}).name || d.id }));
      APState._fleetCounts._loaded = true;
    } catch (err) {
      console.warn('[AP] fleet assets fetch for drift banner failed:', err?.message);
    }
  }

  // Categories — tenant-scoped editable list. Falls back to default-of-defaults
  // (the 8 hardcoded categories) when the tenant hasn't customized.
  // Lacey @ TITS 2026-05-14 asked to remove "Tolls" — this is the surface.
  const DEFAULT_EXPENSE_CATS = [
    { value: 'fuel', label: 'Fuel', icon: 'local_gas_station' },
    { value: 'def', label: 'DEF / Fluids', icon: 'water_drop' },
    { value: 'parts', label: 'Parts / Supplies', icon: 'build' },
    { value: 'tolls', label: 'Tolls', icon: 'toll' },
    { value: 'meals', label: 'Meals', icon: 'restaurant' },
    { value: 'materials', label: 'Materials', icon: 'inventory_2' },
    { value: 'lodging', label: 'Lodging', icon: 'hotel' },
    { value: 'other', label: 'Other', icon: 'more_horiz' },
  ];
  const tenantCats = Array.isArray(settings.categories) && settings.categories.length > 0
    ? settings.categories
    : DEFAULT_EXPENSE_CATS;
  // Stash on window so _addExpenseCategory/_removeExpenseCategory can mutate.
  window.__deCategoriesDraft = JSON.parse(JSON.stringify(tenantCats));
  if (typeof setTimeout === 'function') setTimeout(() => { _loadRefundPolicyBox(); }, 0); // TASK-9947

  const overlay = document.createElement('div');
  overlay.id = 'driver-expense-settings-modal';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:flex-start;justify-content:center;z-index:11000;overflow-y:auto;padding:20px;';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:640px;width:100%;padding:0;background:#fff;border-radius:12px;overflow:hidden;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, var(--brand-blue), #2563EB);color:#fff;display:flex;justify-content:space-between;align-items:center;">
        <h3 style="margin:0;font-size:1.05rem;font-weight:700;"><span class="material-icons" style="vertical-align:middle;">request_quote</span> Driver Expense Settings</h3>
        <button class="btn-icon" onclick="document.getElementById('driver-expense-settings-modal')?.remove()" style="color:#fff;"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:20px;flex:1;overflow-y:auto;min-height:0;">
        <!-- Cost Allocations moved to TOP (action first, toggles below) — UX fix 2026-05-14 -->
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h4 style="margin:0;font-size:0.95rem;font-weight:700;">Cost Allocations</h4>
          <div style="display:flex;gap:6px;">
            <button class="btn btn-sm btn-secondary" onclick="window.APModule._seedCostAllocations()" style="padding:6px 10px;font-size:0.78rem;">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">cloud_download</span> Seed from fleet
            </button>
            <button class="btn btn-sm btn-primary" onclick="window.APModule._addCostAllocation()" style="padding:6px 10px;font-size:0.78rem;">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">add</span> Add
            </button>
          </div>
        </div>
        ${_allocDriftBannerHtml(allocations)}
        <div id="de-allocations" style="margin-top:8px;max-height:280px;overflow-y:auto;border:1px solid var(--border-light);border-radius:8px;">
          ${allocations.length ? allocations.map(a => {
            // TASK-9213 pattern — the id sits inside a JS string inside an attribute:
            // escJsAttr, not escapeHtml (an html escaper does not make it JS-safe).
            const jsId = (window.escJsAttr ? window.escJsAttr(a.id || '') : String(a.id || ''));
            // TASK-9384 — fleet rows are Roster-owned (Seed from fleet + the server
            // costAllocSync trigger rewrite their label from the unit), so they get no
            // rename pencil; custom / Shop / Office rows do.
            const rosterOwned = _isRosterOwnedAllocation(a);
            return `
            <div data-id="${escapeHtml(a.id)}" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-bottom:1px solid var(--border-light);">
              <span class="material-icons" style="color:${a.active === false ? 'var(--text-muted)' : 'var(--brand-blue)'};font-size:18px;">${a.type === 'truck' ? 'local_shipping' : (a.type === 'trailer' ? 'rv_hookup' : (a.type === 'shop' || a.type === 'office' ? 'business' : 'category'))}</span>
              <div style="flex:1;min-width:0;">
                <div style="font-weight:600;${a.active === false ? 'text-decoration:line-through;color:var(--text-muted);' : ''}">${escapeHtml(a.label || a.id)}</div>
                <div style="font-size:0.72rem;color:var(--text-secondary);">${escapeHtml(a.type || 'custom')}${a.qbClassId ? ' · QB Class ' + escapeHtml(String(a.qbClassId)) : ''}${rosterOwned ? ' · <span style="color:var(--text-muted);">Named from Roster — rename the unit there, then Seed from fleet</span>' : ''}</div>
              </div>
              ${rosterOwned ? '' : `<button class="btn-icon" title="Rename" onclick="window.APModule._renameCostAllocation('${jsId}')" style="padding:6px;border-radius:6px;border:1px solid var(--border-light);background:#fff;">
                <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">edit</span>
              </button>`}
              <button class="btn-icon" title="${a.active === false ? 'Activate' : 'Archive'}" onclick="window.APModule._toggleCostAllocation('${jsId}', ${a.active === false ? 'true' : 'false'})" style="padding:6px;border-radius:6px;border:1px solid var(--border-light);background:#fff;">
                <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">${a.active === false ? 'check_circle' : 'archive'}</span>
              </button>
            </div>`;
          }).join('') : '<div class="empty-state-small" style="padding:20px;text-align:center;color:var(--text-secondary);font-size:0.85rem;"><span class="material-icons" style="font-size:24px;vertical-align:middle;margin-right:6px;opacity:0.5;">category</span>No cost allocations yet — click "Seed from fleet" to import trucks, trailers, and assets.</div>'}
        </div>

        <hr style="margin:18px 0;border:none;border-top:1px solid var(--border-light);">

        <!-- Categories — tenant-scoped editable list. Default is the 8 hardcoded
             categories; admins can remove ones their crews don't use (e.g. Tolls)
             or add custom ones. Saved as tenant.settings.driverExpenses.categories array.
             Drivers see the resulting list in the expense modal dropdown. -->
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div>
            <h4 style="margin:0;font-size:0.95rem;font-weight:700;">Categories</h4>
            <p style="margin:2px 0 0;font-size:0.72rem;color:var(--text-secondary);">What drivers can pick from when recording an expense. Remove ones you don't use.</p>
          </div>
          <div style="display:flex;gap:6px;">
            <button class="btn btn-sm btn-secondary" onclick="window.APModule._resetExpenseCategories()" style="padding:6px 10px;font-size:0.78rem;" title="Restore the 8 default categories">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">restore</span> Reset
            </button>
            <button class="btn btn-sm btn-primary" onclick="window.APModule._addExpenseCategory()" style="padding:6px 10px;font-size:0.78rem;">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">add</span> Add
            </button>
          </div>
        </div>
        <div id="de-categories" style="margin-top:8px;max-height:240px;overflow-y:auto;border:1px solid var(--border-light);border-radius:8px;">
          ${_renderExpenseCategoriesList(tenantCats)}
        </div>

        <hr style="margin:18px 0;border:none;border-top:1px solid var(--border-light);">
        <div id="de-refund-policy"><div style="font-size:0.8rem;color:var(--text-secondary);">Loading refund approval rule…</div></div>

        <hr style="margin:18px 0;border:none;border-top:1px solid var(--border-light);">

        <label style="display:flex;align-items:flex-start;gap:10px;padding:12px;border:1px solid var(--border-light);border-radius:8px;background:var(--bg-secondary);">
          <input type="checkbox" id="de-enabled" ${settings.enabled ? 'checked' : ''} style="margin-top:3px;accent-color:var(--brand-blue);">
          <span>
            <strong style="display:block;">Allow drivers to submit expenses</strong>
            <span style="font-size:0.8rem;color:var(--text-secondary);">When ON, drivers see "Record Expense" under the Actions tab of their driver app, and staff logins see "Record an Expense" on the Clock screen.</span>
          </span>
        </label>

        <label style="display:flex;align-items:flex-start;gap:10px;padding:12px;border:1px solid var(--border-light);border-radius:8px;margin-top:10px;">
          <input type="checkbox" id="de-require-alloc" ${settings.requireCostAllocation !== false ? 'checked' : ''} style="margin-top:3px;accent-color:var(--brand-blue);">
          <span>
            <strong style="display:block;">Require Cost Allocation</strong>
            <span style="font-size:0.8rem;color:var(--text-secondary);">Drivers must pick a truck/trailer/shop before saving. Keeps your BI breakdown clean.</span>
          </span>
        </label>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:14px;">
          <div>
            <label style="display:block;font-weight:600;font-size:0.85rem;">Notify on submit</label>
            <select id="de-notify-submit" class="form-control">
              <option value="off" ${settings.notifyOnSubmit === 'off' ? 'selected' : ''}>Off</option>
              <option value="immediate" ${settings.notifyOnSubmit === 'immediate' ? 'selected' : ''}>Immediate</option>
              <option value="digest" ${(settings.notifyOnSubmit || 'digest') === 'digest' ? 'selected' : ''}>Daily digest</option>
            </select>
          </div>
          <div>
            <label style="display:block;font-weight:600;font-size:0.85rem;">Notify on reimbursement</label>
            <select id="de-notify-reimburse" class="form-control">
              <option value="off" ${settings.notifyOnReimbursement === 'off' ? 'selected' : ''}>Off</option>
              <option value="immediate" ${(settings.notifyOnReimbursement || 'immediate') === 'immediate' ? 'selected' : ''}>Immediate</option>
              <option value="digest" ${settings.notifyOnReimbursement === 'digest' ? 'selected' : ''}>Daily digest</option>
            </select>
          </div>
        </div>

        <div style="margin-top:14px;">
          <label style="display:block;font-weight:600;font-size:0.85rem;">Fallback when nobody is opted-in via roster</label>
          <select id="de-notify-fallback" class="form-control">
            <option value="admins" ${(settings.arApNotificationFallback || 'admins') === 'admins' ? 'selected' : ''}>All tenant admins (owner if there are none)</option>
            <option value="owner" ${settings.arApNotificationFallback === 'owner' ? 'selected' : ''}>Tenant owner only</option>
            <option value="none" ${settings.arApNotificationFallback === 'none' ? 'selected' : ''}>None (silent — not recommended)</option>
          </select>
          <p style="font-size:0.72rem;color:var(--text-secondary);margin:4px 0 0;">Prevents the silent-drop failure when nobody opted in via Roster → notification preferences. "All tenant admins" falls back to the account owner if no admins are set up, so a notice is never lost.</p>
        </div>
      </div>
      <div style="padding:14px 20px;background:var(--bg-secondary);display:flex;justify-content:flex-end;gap:8px;border-top:1px solid var(--border-light);flex-shrink:0;">
        <button class="btn btn-secondary" onclick="document.getElementById('driver-expense-settings-modal')?.remove()">Cancel</button>
        <button class="btn btn-primary" onclick="window.APModule._saveDriverExpenseSettings()">Save</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
}

async function _saveDriverExpenseSettings() {
  const tenant = APState.tenantId;
  if (!tenant) return;
  // TASK-9986 S1 — settings.driverExpenses (categories included) is a Billing
  // setting: owner or the owner's per-person switch. Rules refuse it otherwise.
  if (typeof window.canChangeModuleSettings === 'function' && !window.canChangeModuleSettings('billing')) {
    window.showToast?.(window.SETTINGS_READ_ONLY_MSG || 'Ask the account owner to change these settings.', 'error');
    return;
  }
  // Carry the draft categories array through to Firestore. Falls back to the
  // pristine baseline if the user reset and never touched it.
  const draftCats = Array.isArray(window.__deCategoriesDraft) ? window.__deCategoriesDraft : null;
  const patch = {
    'settings.driverExpenses.enabled': document.getElementById('de-enabled')?.checked === true,
    'settings.driverExpenses.requireCostAllocation': document.getElementById('de-require-alloc')?.checked === true,
    'settings.driverExpenses.notifyOnSubmit': document.getElementById('de-notify-submit')?.value || 'digest',
    'settings.driverExpenses.notifyOnReimbursement': document.getElementById('de-notify-reimburse')?.value || 'immediate',
    'settings.driverExpenses.arApNotificationFallback': document.getElementById('de-notify-fallback')?.value || 'admins',
  };
  if (draftCats) {
    // Strip whitespace + dedupe by value. Always keep at least one row — a
    // tenant with zero categories can't record any expenses, so save would
    // brick the feature.
    const seen = new Set();
    const cleaned = draftCats
      .map(c => ({
        value: String(c?.value || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '_').slice(0, 32),
        label: String(c?.label || '').trim().slice(0, 60),
        icon: String(c?.icon || 'more_horiz').trim().slice(0, 40),
      }))
      .filter(c => c.value && c.label && !seen.has(c.value) && seen.add(c.value));
    if (cleaned.length === 0) {
      window.showToast?.('Keep at least one category — restore defaults if needed.', 'error');
      return;
    }
    patch['settings.driverExpenses.categories'] = cleaned;
  }
  try {
    await updateDoc(doc(db, 'tenants', tenant), patch);
    if (window.AppState) {
      window.AppState.settings = window.AppState.settings || {};
      window.AppState.settings.driverExpenses = {
        ...(window.AppState.settings.driverExpenses || {}),
        enabled: patch['settings.driverExpenses.enabled'],
        requireCostAllocation: patch['settings.driverExpenses.requireCostAllocation'],
        notifyOnSubmit: patch['settings.driverExpenses.notifyOnSubmit'],
        notifyOnReimbursement: patch['settings.driverExpenses.notifyOnReimbursement'],
        arApNotificationFallback: patch['settings.driverExpenses.arApNotificationFallback'],
      };
      if (patch['settings.driverExpenses.categories']) {
        window.AppState.settings.driverExpenses.categories = patch['settings.driverExpenses.categories'];
      }
    }
    // Tier-A audit listener: categories CRUD is a tenant-policy change that
    // affects every driver's expense flow downstream. Tag for searchability.
    if (patch['settings.driverExpenses.categories']) {
      window.logAudit?.('settings', 'DRIVER_EXPENSE_CATEGORIES_UPDATE', {
        categoryCount: patch['settings.driverExpenses.categories'].length,
        categoryValues: patch['settings.driverExpenses.categories'].map(c => c.value),
      });
    }
    window.__deCategoriesDraft = null;
    document.getElementById('driver-expense-settings-modal')?.remove();
    window.showToast?.('Driver expense settings saved', 'success');
  } catch (err) {
    console.error('[AP] saveDriverExpenseSettings', err);
    window.showToast?.('Failed to save settings — ' + (err?.message || 'try again'), 'error');
  }
}

// ---- Expense Categories CRUD (in the Driver Expense Settings modal) ----
// Tenant-scoped editable list. Mutates `window.__deCategoriesDraft` in memory
// until the Save button writes it to `tenant.settings.driverExpenses.categories[]`.
// Drivers see the resulting list in the expense modal dropdown
// (jobboard.js getActiveExpenseCategories).
const _DEFAULT_EXPENSE_CATS = [
  { value: 'fuel', label: 'Fuel', icon: 'local_gas_station' },
  { value: 'def', label: 'DEF / Fluids', icon: 'water_drop' },
  { value: 'parts', label: 'Parts / Supplies', icon: 'build' },
  { value: 'tolls', label: 'Tolls', icon: 'toll' },
  { value: 'meals', label: 'Meals', icon: 'restaurant' },
  { value: 'materials', label: 'Materials', icon: 'inventory_2' },
  { value: 'lodging', label: 'Lodging', icon: 'hotel' },
  { value: 'other', label: 'Other', icon: 'more_horiz' },
];

function _renderExpenseCategoriesList(cats) {
  if (!Array.isArray(cats) || cats.length === 0) {
    return '<div class="empty-state-small" style="padding:20px;text-align:center;color:var(--text-secondary);font-size:0.85rem;"><span class="material-icons" style="font-size:24px;vertical-align:middle;margin-right:6px;opacity:0.5;">label_off</span>No categories — click Reset to restore the defaults.</div>';
  }
  return cats.map((c, idx) => `
    <div data-value="${escapeHtml(c.value)}" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-bottom:1px solid var(--border-light);">
      <span class="material-icons" style="color:var(--brand-blue);font-size:18px;">${escapeHtml(c.icon || 'more_horiz')}</span>
      <div style="flex:1;">
        <div style="font-weight:600;">${escapeHtml(c.label || c.value)}</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);">${escapeHtml(c.value)}</div>
      </div>
      <button class="btn-icon" title="Remove" onclick="window.APModule._removeExpenseCategory(${idx})" style="padding:6px;border-radius:6px;border:1px solid var(--border-light);background:#fff;">
        <span class="material-icons" style="font-size:14px;color:#991B1B;">delete</span>
      </button>
    </div>
  `).join('');
}

async function _addExpenseCategory() {
  const label = await _promptText('Category name (e.g. "Parking", "Per Diem")', '');
  if (!label) return;
  const value = String(label).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 32);
  if (!value) {
    window.showToast?.('Category name must include at least one letter or number.', 'error');
    return;
  }
  const draft = Array.isArray(window.__deCategoriesDraft) ? window.__deCategoriesDraft : [];
  if (draft.some(c => c.value === value)) {
    window.showToast?.('That category already exists.', 'error');
    return;
  }
  draft.push({ value, label: String(label).trim().slice(0, 60), icon: 'more_horiz' });
  window.__deCategoriesDraft = draft;
  const host = document.getElementById('de-categories');
  if (host) host.innerHTML = _renderExpenseCategoriesList(draft);
}

function _removeExpenseCategory(idx) {
  const draft = Array.isArray(window.__deCategoriesDraft) ? window.__deCategoriesDraft : [];
  if (idx < 0 || idx >= draft.length) return;
  // Don't allow removing the last category — feature would break.
  if (draft.length === 1) {
    window.showToast?.('Keep at least one category.', 'error');
    return;
  }
  draft.splice(idx, 1);
  window.__deCategoriesDraft = draft;
  const host = document.getElementById('de-categories');
  if (host) host.innerHTML = _renderExpenseCategoriesList(draft);
}

function _resetExpenseCategories() {
  window.__deCategoriesDraft = JSON.parse(JSON.stringify(_DEFAULT_EXPENSE_CATS));
  const host = document.getElementById('de-categories');
  if (host) host.innerHTML = _renderExpenseCategoriesList(window.__deCategoriesDraft);
  window.showToast?.('Defaults restored. Click Save to apply.', 'info');
}

async function _addCostAllocation() {
  if (_apMoneyEditBlocked()) return; // TASK-9491 — cost centers are canEditMoney in firestore.rules
  const label = await _promptText('Cost allocation name (e.g. "Truck #15", "Shop", "Office")', '');
  if (!label) return;
  try {
    await addDoc(collection(db, 'tenants', APState.tenantId, 'costAllocations'), {
      label,
      type: 'custom',
      refId: null,
      qbClassId: null,
      active: true,
      sortOrder: 0,
      createdAt: Timestamp.now(),
    });
    window.showToast?.('Allocation added', 'success');
    // Refresh modal
    document.getElementById('driver-expense-settings-modal')?.remove();
    _showDriverExpenseSettingsModal();
  } catch (err) {
    console.error('[AP] addCostAllocation', err);
    window.showToast?.('Failed to add — ' + (err?.message || 'try again'), 'error');
  }
}

async function _toggleCostAllocation(id, makeActive) {
  if (_apMoneyEditBlocked()) return; // TASK-9491 — cost centers are canEditMoney in firestore.rules
  try {
    await updateDoc(doc(db, 'tenants', APState.tenantId, 'costAllocations', id), {
      active: makeActive === true || makeActive === 'true',
      updatedAt: Timestamp.now(),
    });
    document.getElementById('driver-expense-settings-modal')?.remove();
    _showDriverExpenseSettingsModal();
  } catch (err) {
    console.error('[AP] toggleCostAllocation', err);
    window.showToast?.('Failed to update — ' + (err?.message || 'try again'), 'error');
  }
}

// TASK-9384 — which rows does the tenant own the NAME of? Fleet rows (truck /
// trailer / asset) are Roster-owned: _seedCostAllocations AND the server-side
// costAllocSync trigger (functions/src/index.js onTruckWriteCostAllocSync & co)
// rewrite their label from the unit on every seed / roster write, so a rename
// there would silently revert. Custom (or untyped), Shop and Office rows are the
// tenant's to name.
function _isRosterOwnedAllocation(a) {
  const t = String(a?.type || 'custom');
  return t === 'truck' || t === 'trailer' || t === 'asset';
}

// TASK-9384 — inline rename for custom / Shop / Office rows.
// Gate = exactly the one Add and Archive already ride: this view loads behind the
// 'billing' module (app.js moduleForView('ap') → checkModuleAccess) and
// firestore.rules `costAllocations` `allow update` admits
// hasModule(tenantId, 'billing') with no field allow-list. No role check here, by
// design (memory: hardline module access, Chris 2026-09-24).
// Expenses snapshot `costAllocationLabel` when saved (jobboard.js) — a rename does
// NOT rewrite them: the stored name stays on each entry as the record of what it
// was filed under, and the AP lists show the CURRENT name by costAllocationId
// (_currentAllocLabel). The rename itself is audited old -> new.
async function _renameCostAllocation(id) {
  if (_apMoneyEditBlocked()) return; // TASK-9491 — cost centers are canEditMoney in firestore.rules
  const tenant = APState.tenantId;
  if (!tenant || !id) return;
  let current;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant, 'costAllocations', id));
    if (!snap.exists()) { window.showToast?.('That allocation no longer exists', 'error'); return; }
    current = { id: snap.id, ...snap.data() };
  } catch (err) {
    console.error('[AP] renameCostAllocation read', err);
    window.showToast?.('Failed to load — ' + (err?.message || 'try again'), 'error');
    return;
  }
  if (_isRosterOwnedAllocation(current)) {
    window.showToast?.('Named from Roster — rename the unit there, then Seed from fleet', 'info');
    return;
  }
  const label = await _promptText('Rename cost allocation', current.label || '');
  if (label === null) return;                                   // cancelled
  if (!label) { window.showToast?.('Name can\u2019t be blank', 'error'); return; }
  if (label.length > 100) { window.showToast?.('Keep the name under 100 characters', 'error'); return; }
  if (label === current.label) return;
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'costAllocations', id), {
      label,
      updatedAt: Timestamp.now(),
    });
    // Audited old -> new (TASK-9390 SAVE_JOB_TYPES shape). Fire-and-forget:
    // logAudit never throws and an audit failure must not undo the rename.
    window.logAudit?.('settings', 'RENAME_COST_ALLOCATION', {
      entityType: 'costAllocation',
      entityId: id,
      allocationType: current.type || 'custom',
      labelFrom: current.label || '',
      labelTo: label,
    });
    window.showToast?.('Renamed', 'success');
    // Refresh modal — same way Add does.
    document.getElementById('driver-expense-settings-modal')?.remove();
    _showDriverExpenseSettingsModal();
  } catch (err) {
    console.error('[AP] renameCostAllocation', err);
    window.showToast?.('Failed to rename — ' + (err?.message || 'try again'), 'error');
  }
}

// Idempotent: uses deterministic doc IDs (`{type}_{refId}` for trucks/trailers/assets,
// `shop_default` / `office_default` for the two singletons) + `setDoc({merge:true})`,
// so concurrent invocations (amber CTA + Settings-modal button) collapse to a single
// doc per entity instead of racing into duplicates (root cause of TITS 2026-05-13 dupes).
// Pre-fetches existing allocations to (a) preserve `createdAt` on re-seeds and (b) skip
// recreating a deterministic doc when a legacy random-id doc already covers the entity.
// Also guards with an in-flight flag so the same click can't fan out into parallel runs.
let _seedingCostAllocations = false;
async function _seedCostAllocations() {
  if (_apMoneyEditBlocked()) return; // TASK-9491 — cost centers are canEditMoney in firestore.rules
  if (_seedingCostAllocations) {
    window.showToast?.('Already seeding — hold on…', 'info');
    return;
  }
  _seedingCostAllocations = true;
  // Seed from active trucks/trailers/assets + "Shop" + "Office".
  const tenant = APState.tenantId;
  try {
    const [trucksSnap, trailersSnap, assetsSnap, allocSnap] = await Promise.all([
      getDocs(collection(db, 'tenants', tenant, 'trucks')),
      getDocs(collection(db, 'tenants', tenant, 'trailers')),
      getDocs(collection(db, 'tenants', tenant, 'assets')),
      getDocs(collection(db, 'tenants', tenant, 'costAllocations')),
    ]);
    // Map existing allocations by doc id (for deterministic-id lookups) and by
    // entity-key (for detecting legacy random-id docs that still cover an entity).
    const existingById = new Map(allocSnap.docs.map(d => [d.id, d.data()]));
    const legacyByEntityKey = new Map(); // key = `${type}_${refId||label}`
    for (const [id, data] of existingById.entries()) {
      if (id === 'shop_default' || id === 'office_default') continue;
      if (id === `${data.type}_${data.refId}`) continue; // already deterministic
      const refKey = data.refId || data.label || '';
      legacyByEntityKey.set(`${data.type}_${refKey}`, { id, data });
    }
    let added = 0;
    let kept = 0;
    const trucks = trucksSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.status !== 'inactive');
    const trailers = trailersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.status !== 'inactive');
    const assets = assetsSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.active !== false);
    const writes = []; // { docId, payload }
    for (const t of trucks) {
      const unit = t.unit || t.id;
      const label = t.nickname ? `${t.nickname} — #${unit}` : `Truck #${unit}`;
      writes.push({ docId: `truck_${t.id}`, payload: { label, type: 'truck', refId: t.id } });
    }
    for (const t of trailers) {
      const unit = t.unit || t.id;
      const label = t.nickname ? `${t.nickname} — #${unit}` : `Trailer #${unit}`;
      writes.push({ docId: `trailer_${t.id}`, payload: { label, type: 'trailer', refId: t.id } });
    }
    for (const a of assets) {
      writes.push({ docId: `asset_${a.id}`, payload: { label: a.name || a.id, type: 'asset', refId: a.id } });
    }
    writes.push({ docId: 'shop_default', payload: { label: 'Shop', type: 'shop', refId: null } });
    writes.push({ docId: 'office_default', payload: { label: 'Office', type: 'office', refId: null } });

    for (const w of writes) {
      const legacyKey = `${w.payload.type}_${w.payload.refId || w.payload.label || ''}`;
      if (legacyByEntityKey.has(legacyKey) && !existingById.has(w.docId)) {
        // A legacy random-id doc already represents this entity — leave it alone.
        kept++;
        continue;
      }
      const prev = existingById.get(w.docId);
      if (prev) {
        await setDoc(doc(db, 'tenants', tenant, 'costAllocations', w.docId), {
          ...w.payload,
          // TASK-9384 — Shop / Office labels are tenant-renameable, so a re-seed keeps
          // the rename. Fleet labels (truck / trailer / asset) still sync from Roster.
          label: _isRosterOwnedAllocation(w.payload) ? w.payload.label : (prev.label || w.payload.label),
          qbClassId: prev.qbClassId ?? null,
          active: prev.active ?? true,
          sortOrder: prev.sortOrder ?? 0,
        }, { merge: true });
        kept++;
      } else {
        await setDoc(doc(db, 'tenants', tenant, 'costAllocations', w.docId), {
          ...w.payload, qbClassId: null, active: true, sortOrder: 0, createdAt: Timestamp.now(),
        });
        added++;
      }
    }
    const msg = kept > 0
      ? `Added ${added}, kept ${kept} existing`
      : `Seeded ${added} allocation${added === 1 ? '' : 's'}`;
    window.showToast?.(msg, 'success');
    // Only reopen the settings modal if it was already open (in-modal Seed button).
    // When invoked from the panel-level "Setup needed" CTA, the live costAllocations
    // onSnapshot will swap the empty-state for the normal panel.
    const settingsModal = document.getElementById('driver-expense-settings-modal');
    if (settingsModal) {
      settingsModal.remove();
      _showDriverExpenseSettingsModal();
    }
  } catch (err) {
    console.error('[AP] seedCostAllocations', err);
    window.showToast?.('Seed failed — ' + (err?.message || 'try again'), 'error');
  } finally {
    _seedingCostAllocations = false;
  }
}

// ============================================
// ADMIN "RECORD EXPENSE" — v1.1 (2026-05-12)
// Reuses the driver-side modal in jobboard.js. The modal already supports an
// admin context (no driverModePin / isPublicAccess on AppState) — see the
// `isDriverScope` branch in jobboard.js submitExpense. Admin writes route
// through the new `createExpenseAdmin` callable which stamps source:'admin'
// + status:'approved' + actor's uid as approvedBy.
// ============================================
async function openAdminExpense() {
  if (!APState.tenantId) {
    window.showToast?.('AP not initialized', 'error');
    return;
  }
  if (!window.JobBoardModule?.openExpenseModal) {
    window.showToast?.('Job Board module not loaded — try refreshing', 'error');
    return;
  }

  // Pre-fetch cost allocations so the modal dropdown is populated even when
  // the user hasn't visited Job Board this session (which is the case for
  // many bookkeepers who live in AP).
  let costAllocations = [];
  let costAllocationsSettled = false;   // TASK-4585
  try {
    const allocSnap = await getDocs(collection(db, 'tenants', APState.tenantId, 'costAllocations'));
    costAllocations = allocSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(a => a.active !== false)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.label || '').localeCompare(String(b.label || '')));
    costAllocationsSettled = true;   // TASK-4585 — distinguish "none" from "fetch failed"
  } catch (e) {
    console.warn('[AP] openAdminExpense: cost allocations fetch failed', e?.message);
  }

  // Trucks for the vehicle dropdown — pulled from APState.assets which is
  // already synced from the trucks + trailers subscriptions in subscribeToData.
  const trucks = (APState.assets || []).filter(a => a._assetType === 'truck');

  // Cost-allocation requirement falls back to AppState.settings.driverExpenses
  // (set globally in app.js when tenant doc loads).
  const driverExpenseSettings = window.AppState?.settings?.driverExpenses || {};

  window.JobBoardModule.openExpenseModal(null, null, null, {
    tenantId: APState.tenantId,
    clockEntries: [],
    trucks,
    jobs: APState.jobs || [],
    jobDays: [],
    costAllocations,
    costAllocationsSettled,
    driverExpenseSettings,
    // Admin context: NO driverId / driverName → modal submits via the admin
    // path (createExpenseAdmin callable).
    driverId: null,
    driverName: '',
  });
}

// ============================================
// TASK-9367 — "Refund / Credit" dialog. Two entry points:
//   • a row action on a Company Expense or an AP bill → creditOf = that cost's
//     id (`ap_<invoiceId>` for a bill); everything is copied from the original
//     server-side, so only amount / date / reason are typed;
//   • the caret on "Record Expense" → a standalone credit (category + vendor).
// Writes ONLY through the createVendorCreditAdmin callable (rules refuse a
// client-written credit). A refusal renders inline and the dialog stays open.
// ============================================
const _VC_REASONS = [
  { value: 'refund', label: 'Refund' },
  { value: 'return', label: 'Returned item' },
  { value: 'dispute', label: 'Dispute / chargeback won' },
  { value: 'pricing_adjustment', label: 'Price adjustment' },
  { value: 'other', label: 'Other' },
];
let _vcState = null; // { creditOf, original, remaining, requestId, busy }

function toggleRecordMenu(ev) {
  ev?.stopPropagation?.();
  const menu = document.getElementById('ap-record-menu');
  if (!menu) return;
  const open = menu.hasAttribute('hidden');
  if (open) {
    menu.removeAttribute('hidden');
    const close = (e) => {
      if (menu.contains(e.target)) return;
      menu.setAttribute('hidden', '');
      document.removeEventListener('click', close, true);
    };
    setTimeout(() => document.addEventListener('click', close, true), 0);
  } else {
    menu.setAttribute('hidden', '');
  }
}

// TASK-9771 slice 1 — "Upload receipts" lives in its own lazily-loaded module
// (receipts.js, the qboSetup.js pattern). Nothing it does reaches costEntries:
// every row is a PROPOSED read a person confirms in receiptInbox.
async function openReceiptInbox(opts) {
  document.getElementById('ap-record-menu')?.setAttribute('hidden', '');
  try {
    const mod = await __ddChunkWrap(import('./receipts.js?v=10162501')); // TASK-10162 S5 merchant coding · TASK-10170 attach a late receipt to the expense already booked · TASK-9771 Read again · TASK-9770 Send to Expenses · 2089-on-2088.6: TASK-9775 driver WO receipt + 2089 receipts · integ/2093 TASK-9770 follow-ups (Send selected, fuel, double-count warnings)
    // TASK-9774: { search: true } opens straight to "Search receipts".
    mod.openReceiptInbox(APState.tenantId, opts && opts.search === true ? { search: true } : undefined);
  } catch (err) {
    console.error('[AP] Upload receipts failed to open:', err);
    window.showToast?.('Could not open Upload receipts. Reload the page and try again.', 'error');
  }
}

function _vcMintRequestId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return 'vc' + crypto.randomUUID().replace(/-/g, '');
  } catch (_) { /* fall through */ }
  return 'vc' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

// What the dialog knows about the original, from data AP already holds.
function _vcDescribeOriginal(creditOf) {
  if (!creditOf) return null;
  if (creditOf.startsWith('ap_')) {
    const inv = (APState.invoices || []).find(i => i.id === creditOf.slice(3));
    if (!inv) return { label: 'AP bill', amount: null, paymentCardId: null };
    return {
      label: `${inv.vendorName || 'Vendor'} bill${inv.vendorInvoiceNumber ? ' #' + inv.vendorInvoiceNumber : ''}`,
      amount: _apNum(inv.grandTotal),
      paymentCardId: null,
      billOpen: inv.paymentStatus !== 'paid',
      owed: _apOwed(inv),
    };
  }
  // TASK-9947 — driver / staff expenses take refunds too.
  const e = (APState.companyExpenses || []).find(x => x.id === creditOf)
    || (APState.driverExpenses || []).find(x => x.id === creditOf);
  if (!e) return { label: 'Expense', amount: null, paymentCardId: null };
  return {
    label: e.description || e.vendorName || e.category || 'Expense',
    amount: _apNum(e.amount),
    paymentCardId: e.paymentCardId || null,
    paymentCardLast4: e.paymentCardLast4 ? String(e.paymentCardLast4) : null,
    submitted: e.source === 'driver' || e.source === 'staff',
    reimbursement: e.reimbursementRequired === true,
    reimbursed: e.reimbursementRequired === true && e.reimbursementStatus === 'paid',
    personName: e.personName || null,
    status: e.status || null,
    lines: Array.isArray(e.allocations) && e.allocations.length >= 2 ? e.allocations : null,
    // TASK-9950 — the receipt's lines (server-written) and which a refund named already.
    receiptLines: Array.isArray(e.lineItems) && e.lineItems.length ? e.lineItems : null,
    receiptLinesMeta: e.lineItemsMeta && typeof e.lineItemsMeta === 'object' ? e.lineItemsMeta : null,
    refundedLines: _creditsOf(creditOf).filter(c => c.status !== 'rejected')
      .flatMap(c => (Array.isArray(c.refundLines?.lines) ? c.refundLines.lines : []).map(l => l && l.i)).filter(Number.isInteger),
  };
}

function openVendorCredit(creditOf) {
  document.getElementById('ap-record-menu')?.setAttribute('hidden', '');
  if (!APState.tenantId) { window.showToast?.('AP not initialized', 'error'); return; }
  const original = _vcDescribeOriginal(creditOf || null);
  const remaining = original && original.amount != null
    ? Math.max(0, _apCents(original.amount) - _apCents(_creditedAgainst(creditOf))) / 100
    : null;
  if (creditOf && remaining === 0) {
    window.showToast?.('That expense has already been fully refunded / credited.', 'info');
    return;
  }
  _vcState = { creditOf: creditOf || null, original, remaining, requestId: _vcMintRequestId(), busy: false, confirmSeparate: false };

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const cards = _apCardRegistry();
  const cardPrefill = original?.paymentCardId || '';
  const tenantCats = window.AppState?.settings?.driverExpenses?.categories;
  const cats = Array.isArray(tenantCats) && tenantCats.length ? tenantCats : _DEFAULT_EXPENSE_CATS;
  const lbl = 'display:block;font-size:0.8125rem;font-weight:600;color:var(--text-secondary);margin:12px 0 4px;';
  const inp = 'width:100%;padding:10px 12px;font-size:16px;border:1px solid var(--border-medium);border-radius:8px;box-sizing:border-box;';

  document.getElementById('modal-ap-credit')?.remove();
  const wrap = document.createElement('div');
  wrap.className = 'modal';
  wrap.id = 'modal-ap-credit';
  wrap.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h2 style="display:flex;align-items:center;gap:8px;"><span class="material-icons" style="color:#059669;">undo</span> Refund / Credit</h2>
        <button type="button" class="modal-close" aria-label="Close" onclick="window.APModule.closeVendorCredit()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body">
        <p style="margin:0 0 4px;color:var(--text-secondary);font-size:0.9rem;">Money that came back from a vendor (a return or refund). It lowers your costs${original?.billOpen ? ' and what you owe on this bill' : ''} once another billing login approves it.</p>
        ${original ? `
          <div style="background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;margin-top:8px;">
            <div style="font-size:0.8rem;color:var(--text-secondary);">Against</div>
            <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(original.label)}${original.amount != null ? ` · $${original.amount.toFixed(2)}` : ''}</div>
            ${remaining != null ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">Up to $${remaining.toFixed(2)} can still be credited${original.billOpen ? ` · owed on the bill now $${original.owed.toFixed(2)}` : ''}</div>` : ''}
          </div>` : ''}
        ${_vcItemsPickerHtml(original, lbl)}
        <div id="ap-vc-amount-wrap">
        <label for="ap-vc-amount" style="${lbl}">Amount that came back</label>
        <input id="ap-vc-amount" type="number" inputmode="decimal" step="0.01" min="0.01" ${remaining != null ? `max="${remaining.toFixed(2)}"` : ''} placeholder="0.00" style="${inp}">
        </div>
        <label for="ap-vc-date" style="${lbl}">Date it posted</label>
        <input id="ap-vc-date" type="date" value="${todayStr}" max="${todayStr}" style="${inp}">
        <label for="ap-vc-reason" style="${lbl}">Reason</label>
        <select id="ap-vc-reason" style="${inp}">
          ${_VC_REASONS.map(r => `<option value="${r.value}">${escapeHtml(r.label)}</option>`).join('')}
        </select>
        ${_vcRefundToHtml(original, lbl)}
        ${_vcLinePickerHtml(original, lbl, inp)}
        ${original ? '' : `
          <label for="ap-vc-category" style="${lbl}">Category</label>
          <select id="ap-vc-category" style="${inp}">
            <option value="">Pick one…</option>
            ${cats.map(c => `<option value="${escapeHtml(String(c.value || ''))}">${escapeHtml(String(c.label || c.value || ''))}</option>`).join('')}
          </select>
          <label for="ap-vc-vendor" style="${lbl}">Vendor</label>
          <input id="ap-vc-vendor" type="text" maxlength="200" placeholder="Who sent it back" style="${inp}">`}
        ${cards.length && !(original && (original.paymentCardId || original.paymentCardLast4)) ? `
          <label for="ap-vc-card" style="${lbl}">Card it went back to (optional)</label>
          <select id="ap-vc-card" style="${inp}">
            <option value="">— None —</option>
            ${cards.map(c => `<option value="${escapeHtml(c.id)}" ${c.id === cardPrefill ? 'selected' : ''}>${escapeHtml(c.name || 'Card')}${c.lastFourDigits ? ' ••' + escapeHtml(String(c.lastFourDigits)) : ''}</option>`).join('')}
          </select>` : ''}
        <label for="ap-vc-note" style="${lbl}">Note (optional)</label>
        <input id="ap-vc-note" type="text" maxlength="500" placeholder="e.g. RMA 4471, returned 2 filters" style="${inp}">
        <div id="ap-vc-error" role="alert" style="display:none;margin-top:12px;padding:10px 12px;border-radius:8px;background:#FEE2E2;color:#991B1B;border:1px solid #FCA5A5;font-size:0.875rem;"></div>
        <button type="button" id="ap-vc-confirm-separate" class="btn btn-secondary" style="display:none;margin-top:8px;width:100%;min-height:44px;" onclick="window.APModule._vcConfirmSeparate()">It is a different refund — record it</button>
      </div>
      <div class="modal-footer" style="display:flex;justify-content:flex-end;gap:8px;">
        <button type="button" class="btn btn-secondary" onclick="window.APModule.closeVendorCredit()">Cancel</button>
        <button type="button" class="btn btn-primary" id="ap-vc-save" onclick="window.APModule.saveVendorCredit()" style="background:#059669;border-color:#059669;">Record refund / credit</button>
      </div>
    </div>`;
  document.body.appendChild(wrap);
  setTimeout(() => document.getElementById('ap-vc-amount')?.focus(), 50);
}

// TASK-9947 — "Where did the money go?" One doc can carry both a company card
// and a reimbursement claim, so it is asked, never guessed. Required for a
// driver / staff expense or a reimbursement (the server refuses without it).
function _vcRefundToHtml(original, lbl) {
  if (!original) return '';
  const card = original.paymentCardLast4 ? ` ••${escapeHtml(original.paymentCardLast4)}` : '';
  const who = escapeHtml(original.personName || 'the employee');
  const must = original.submitted || original.reimbursement;
  const opts = [];
  if (original.reimbursement) opts.push(['employee', `Back to ${who} (their own card or cash)`]);
  // A card on the receipt, or (no card on it) any company card the dialog can pick below.
  if (original.paymentCardId || original.paymentCardLast4 || _apCardRegistry().length) opts.push(['company_card', card ? `Back to the card${card}` : 'Back to a company card (pick it below)']);
  opts.push(['company', 'To the company (cash or check)']);
  opts.push(['store_credit', 'Store credit with the vendor']); // TASK-9948 — a credit the company holds, not money the employee owes
  const preset = must ? '' : ((original.paymentCardId || original.paymentCardLast4) ? 'company_card' : 'company');
  return `
    <fieldset style="border:none;padding:0;margin:12px 0 0;">
      <legend style="${lbl}margin-top:0;">Where did the money go?${must ? '' : ' (optional)'}</legend>
      ${opts.map(([v, t]) => `
        <label style="display:flex;align-items:center;gap:10px;min-height:44px;padding:4px 10px;border:1px solid var(--border-light);border-radius:8px;margin-top:6px;cursor:pointer;color:var(--text-primary);">
          <input type="radio" name="ap-vc-refundto" value="${v}" ${v === preset ? 'checked' : ''} onchange="window.APModule._vcRefundToChanged()" style="width:20px;height:20px;">
          <span>${t}</span>
        </label>`).join('')}
    </fieldset>
    <div id="ap-vc-owes-note" style="display:none;margin-top:8px;padding:8px 10px;border-radius:8px;background:#FEF3C7;border:1px solid #F59E0B;color:#78350F;font-size:0.85rem;">${who} was already paid back for this. Approving the refund records that ${who} owes it back. It is never taken out of pay.</div>`;
}
function _vcRefundToChanged() {
  const v = document.querySelector('input[name="ap-vc-refundto"]:checked')?.value || '';
  const note = document.getElementById('ap-vc-owes-note');
  if (note) note.style.display = (v === 'employee' && _vcState?.original?.reimbursed) ? 'block' : 'none';
}
// TASK-9947 — one line of a split receipt came back (only truck 104's part).
function _vcLinePickerHtml(original, lbl, inp) {
  if (!original || !Array.isArray(original.lines)) return '';
  const label = (l, i) => escapeHtml(String(l.vehicleDisplay || l.costAllocationLabel || l.jobName || `Line ${i + 1}`)) + ` — $${_apNum(l.amount).toFixed(2)}`;
  return `
    <label for="ap-vc-line" style="${lbl}">Which part of the receipt came back?</label>
    <select id="ap-vc-line" style="${inp}">
      <option value="">All of it — spread across every line</option>
      ${original.lines.map((l, i) => `<option value="${i}" data-key="${escapeHtml([l.costAllocationId || '', l.vehicleId || '', l.jobId || ''].join('|'))}">${label(l, i)}</option>`).join('')}
    </select>`;
}
// TASK-9950 — refund picked receipt lines. PREVIEW ONLY: the server recomputes
// the amount from the stored lines (vendorCredits.receiptLineTaxes /
// lineRefundAmount) and ignores any amount sent. Same rule, to the cent: the
// slip's tax total (else the slip's total above the lines) is the pool; a
// line with its own slip tax keeps it; the rest is spread over the other lines
// by price, largest remainder, ties to the earlier line.
function _vcProrateCents(lineCents, totalCents) {
  const parent = lineCents.reduce((s, c) => s + Math.abs(c), 0);
  if (!parent || !totalCents) return lineCents.map(() => 0);
  const parts = lineCents.map((c, i) => {
    const exact = Math.abs(c) * totalCents / parent;
    const fl = Math.floor(exact + 1e-9);
    return { i, c: fl, r: exact - fl };
  });
  let left = totalCents - parts.reduce((s, p) => s + p.c, 0);
  const order = parts.slice().sort((a, b) => (b.r - a.r) || (a.i - b.i));
  for (let k = 0; k < order.length && left > 0; k++) { order[k].c += 1; left--; }
  return parts.map(p => p.c);
}
function _vcReceiptLineTaxes(original) {
  const L = original && Array.isArray(original.receiptLines) ? original.receiptLines : null;
  if (!L || !L.length) return null;
  const meta = original.receiptLinesMeta || {};
  const off = { why: 'The saved receipt lines don\'t add up to this expense, so enter the amount instead.' };
  if (meta.truncated === true || L.length > 60) return off;
  const money = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100000 ? Math.round(v * 100) : null);
  const item = L.map(l => { const c = l && typeof l === 'object' ? money(l.total) : null; return c && c > 0 ? c : null; });
  if (item.some(c => c === null)) return { why: 'Some saved receipt lines could not be read, so enter the amount instead.' };
  const itemsTotal = item.reduce((a, b) => a + b, 0);
  const amount = Math.round((Number(original.amount) || 0) * 100);
  const slipTax = money(meta.tax);
  const slipTotal = money(meta.total);
  const pool = slipTax !== null ? slipTax : (slipTotal !== null ? slipTotal - itemsTotal : null);
  if (pool === null || pool < 0 || itemsTotal + pool !== amount) return off;
  const own = L.map((l, i) => { const t = money(l.tax); return t !== null && t <= item[i] ? t : null; });
  const rest = pool - own.reduce((a, t) => a + (t || 0), 0);
  const free = own.map((t, i) => (t === null ? i : -1)).filter(i => i >= 0);
  if (rest < 0 || (!free.length && rest !== 0)) return off;
  const shares = _vcProrateCents(free.map(i => item[i]), rest);
  const tax = own.slice();
  free.forEach((i, k) => { tax[i] = shares[k]; });
  return { item, tax, slip: own.map(t => t !== null) };
}
function _vcItemsPickerHtml(original, lbl) {
  if (!original || !Array.isArray(original.receiptLines)) return '';
  const r = _vcReceiptLineTaxes(original);
  if (!r || r.why) return `<div style="margin-top:12px;font-size:0.85rem;color:var(--text-secondary);">${escapeHtml((r && r.why) || '')}</div>`;
  const done = new Set(original.refundedLines || []);
  const rows = original.receiptLines.map((l, i) => {
    const d = escapeHtml(String(l.description || `Line ${i + 1}`));
    const was = done.has(i);
    return `
      <label style="display:flex;align-items:center;gap:10px;min-height:44px;padding:4px 10px;border:1px solid var(--border-light);border-radius:8px;margin-top:6px;cursor:${was ? 'default' : 'pointer'};color:var(--text-primary);${was ? 'opacity:0.6;' : ''}">
        <input type="checkbox" class="ap-vc-item" value="${i}" ${was ? 'disabled' : ''} onchange="window.APModule._vcItemsChanged()" style="width:20px;height:20px;">
        <span style="flex:1;">${d}${was ? ' — already refunded' : ''}</span>
        <span style="white-space:nowrap;">$${(r.item[i] / 100).toFixed(2)}</span>
      </label>`;
  }).join('');
  return `
    <fieldset style="border:none;padding:0;margin:12px 0 0;">
      <legend style="${lbl}margin-top:0;">What came back?</legend>
      <label style="display:flex;align-items:center;gap:10px;min-height:44px;padding:4px 10px;border:1px solid var(--border-light);border-radius:8px;margin-top:6px;cursor:pointer;color:var(--text-primary);">
        <input type="radio" name="ap-vc-mode" value="amount" checked onchange="window.APModule._vcItemsChanged()" style="width:20px;height:20px;"><span>An amount</span>
      </label>
      <label style="display:flex;align-items:center;gap:10px;min-height:44px;padding:4px 10px;border:1px solid var(--border-light);border-radius:8px;margin-top:6px;cursor:pointer;color:var(--text-primary);">
        <input type="radio" name="ap-vc-mode" value="items" onchange="window.APModule._vcItemsChanged()" style="width:20px;height:20px;"><span>Items on the receipt</span>
      </label>
    </fieldset>
    <div id="ap-vc-items" style="display:none;">
      <div style="max-height:280px;overflow-y:auto;">${rows}</div>
      <div id="ap-vc-items-total" style="margin-top:8px;font-weight:600;color:var(--text-primary);"></div>
    </div>`;
}
function _vcPickedLines() {
  return Array.from(document.querySelectorAll('#ap-vc-items .ap-vc-item:checked')).map(x => Number(x.value)).filter(Number.isInteger);
}
function _vcItemsMode() {
  return document.querySelector('input[name="ap-vc-mode"]:checked')?.value === 'items';
}
function _vcItemsChanged() {
  const items = _vcItemsMode();
  const box = document.getElementById('ap-vc-items');
  const amt = document.getElementById('ap-vc-amount-wrap');
  if (box) box.style.display = items ? 'block' : 'none';
  if (amt) amt.style.display = items ? 'none' : 'block';
  const out = document.getElementById('ap-vc-items-total');
  if (!out) return;
  const r = _vcReceiptLineTaxes(_vcState?.original);
  const picked = _vcPickedLines();
  if (!r || r.why || !picked.length) { out.textContent = items ? 'Tick the items that came back.' : ''; return; }
  const it = picked.reduce((s, i) => s + (r.item[i] || 0), 0);
  const tx = picked.reduce((s, i) => s + (r.tax[i] || 0), 0);
  const allSlip = picked.every(i => r.slip[i]);
  out.textContent = `Items $${(it / 100).toFixed(2)} + tax $${(tx / 100).toFixed(2)} = $${((it + tx) / 100).toFixed(2)} back`
    + (allSlip ? ' (tax as printed on the receipt)' : ' (includes its share of the receipt\'s tax — an estimate; check it against the store\'s return slip)');
}
function _vcConfirmSeparate() {
  if (!_vcState || _vcState.busy) return;
  _vcState.confirmSeparate = true;
  saveVendorCredit();
}

function closeVendorCredit() {
  if (_vcState?.busy) return;
  _vcState = null;
  document.getElementById('modal-ap-credit')?.remove();
}

function _vcShowError(msg, opts) {
  const el = document.getElementById('ap-vc-error');
  const sep = document.getElementById('ap-vc-confirm-separate');
  if (sep) sep.style.display = opts && opts.needsConfirm ? 'block' : 'none';
  if (!el) { window.showToast?.(msg, 'error'); return; }
  el.textContent = msg;
  el.style.display = 'block';
}

async function saveVendorCredit() {
  const st = _vcState;
  if (!st || st.busy) return;
  const amount = parseFloat(document.getElementById('ap-vc-amount')?.value);
  const dateVal = document.getElementById('ap-vc-date')?.value || '';
  const creditReason = document.getElementById('ap-vc-reason')?.value || '';
  // TASK-9950 — items mode: the server prices the picked lines; no amount is sent.
  const itemsMode = _vcItemsMode();
  const picked = itemsMode ? _vcPickedLines() : null;
  if (itemsMode && !picked.length) { _vcShowError('Tick the items that came back.'); return; }
  if (itemsMode && Array.isArray(st.original?.lines) && (document.getElementById('ap-vc-line')?.value || '') === '') {
    _vcShowError('This receipt is split across trucks / jobs. Pick which one these items were for.');
    return;
  }
  if (!itemsMode && (!Number.isFinite(amount) || amount <= 0)) { _vcShowError('Enter the amount that came back.'); return; }
  if (!itemsMode && st.remaining != null && Math.round(amount * 100) > Math.round(st.remaining * 100)) {
    _vcShowError(`Only $${st.remaining.toFixed(2)} of that expense is left to credit.`);
    return;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) { _vcShowError('Pick the date it posted.'); return; }
  const payload = {
    tenantId: APState.tenantId === 'demo-tenant' ? 'demo' : APState.tenantId,
    creditOf: st.creditOf,
    ...(itemsMode ? { refundLines: picked } : { amount: Math.round(amount * 100) / 100 }),
    // Noon LOCAL, the same anchor AP uses for invoice dates, so the day never
    // slides across midnight in any US zone.
    dateMs: new Date(dateVal + 'T12:00:00').getTime(),
    creditReason,
    description: (document.getElementById('ap-vc-note')?.value || '').trim() || null,
    clientRequestId: st.requestId,
  };
  // TASK-9947 — where the money went + which split line.
  const refundTo = document.querySelector('input[name="ap-vc-refundto"]:checked')?.value || '';
  if (st.original && (st.original.submitted || st.original.reimbursement) && !refundTo) { _vcShowError('Say where the money went.'); return; }
  if (refundTo) payload.refundTo = refundTo;
  const lineSel = document.getElementById('ap-vc-line');
  if (lineSel && lineSel.value !== '') {
    payload.targetLine = Number(lineSel.value);
    payload.targetLineKey = lineSel.selectedOptions?.[0]?.getAttribute('data-key') || null;
  }
  if (st.confirmSeparate) payload.confirmSeparateRefund = true;
  if (!st.creditOf) {
    payload.category = document.getElementById('ap-vc-category')?.value || '';
    payload.vendorName = (document.getElementById('ap-vc-vendor')?.value || '').trim() || null;
    if (!payload.category) { _vcShowError('Pick a category.'); return; }
  }
  const cardId = document.getElementById('ap-vc-card')?.value || '';
  if (cardId) {
    const card = _apCardRegistry().find(c => c.id === cardId);
    payload.paymentCardId = cardId;
    payload.paymentCardLast4 = card?.lastFourDigits ? String(card.lastFourDigits).slice(0, 4) : null;
  }

  const btn = document.getElementById('ap-vc-save');
  st.busy = true;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving…'; }
  try {
    const res = await httpsCallable(functions, 'createVendorCreditAdmin')(payload);
    const d = res?.data || {};
    st.busy = false;
    closeVendorCredit();
    const applied = Number(d.appliedToBill) || 0;
    window.showToast?.(
      d.demo ? 'Demo: refund / credit not saved'
        : itemsMode && d.status === 'pending' && Number.isFinite(Number(d.amount)) ? `Refund of $${Math.abs(Number(d.amount)).toFixed(2)} recorded for the items. It counts once another billing login approves it.`
        : applied > 0 ? `Refund / credit recorded — $${applied.toFixed(2)} taken off the bill`
          : d.status === 'pending' ? 'Refund recorded. It counts once another billing login approves it.'
            : 'Refund / credit recorded',
      'success');
    // TASK-9947 (Domain) — the customer may have been billed for it.
    (Array.isArray(d.warnings) ? d.warnings : []).forEach(w => { if (w && w.message) window.showToast?.(String(w.message), 'warning'); });
  } catch (e) {
    st.busy = false;
    st.confirmSeparate = false;
    if (btn) { btn.disabled = false; btn.textContent = 'Record refund / credit'; }
    console.error('[AP] createVendorCreditAdmin failed', e);
    _vcShowError(e?.message ? String(e.message) : 'The refund / credit was not recorded. Try again.', { needsConfirm: !!e?.details?.needsConfirm });
  }
}

// TASK-9947 — approve / reject a refund waiting for a second person.
async function decideRefund(creditId, decision) {
  let note = null;
  if (decision === 'reject') {
    note = await _promptText('Reject refund — why? (optional)', '');
    if (note === null) return;
  }
  try {
    const res = await _callExpenseFn('decideExpenseRefund', { tenantId: APState.tenantId, creditId, decision, note: note || null, expectedStatus: 'pending' });
    const d = res?.data || {};
    if (d.applied === false) { window.showToast?.(d.message || 'Already done.', 'info'); return; }
    let msg = decision === 'approve' ? 'Refund approved' : 'Refund rejected';
    if (decision === 'approve' && Number(d.appliedToBill) > 0) msg += ` — $${Number(d.appliedToBill).toFixed(2)} taken off the bill`;
    if (decision === 'approve' && Number(d.owesBack) > 0) msg += ` — recorded that the employee owes $${Number(d.owesBack).toFixed(2)} back`;
    window.showToast?.(msg, decision === 'approve' ? 'success' : 'info');
    // TASK-9952 — closed pay period / already in QuickBooks: it posts in the open period.
    if (decision === 'approve' && d.periodCorrection && d.periodCorrection.message) window.showToast?.(String(d.periodCorrection.message), 'warning');
    if (decision === 'approve' && Number(d.owesBack) > 0) _obMod?.refresh?.(); // TASK-9948
  } catch (err) {
    console.error('[AP] decideExpenseRefund', err);
    _decisionErrToast(err, decision === 'approve' ? 'Could not approve — ' : 'Could not reject — ');
  }
}

// TASK-9947 — the one-login refund rule (owner only; settings/expenseRefunds,
// written by setExpenseRefundPolicy with an audit row).
async function _loadRefundPolicyBox() {
  const box = document.getElementById('de-refund-policy');
  if (!box) return;
  const st = window.AppState || {};
  const isOwner = !!(st.user?.uid && st.tenantData?.ownerId && st.user.uid === st.tenantData.ownerId);
  let cur = 'second_person';
  try {
    const snap = await getDoc(doc(db, 'tenants', APState.tenantId, 'settings', 'expenseRefunds'));
    if (snap.exists() && snap.data()?.soleApprover === 'sole_with_audit') cur = 'sole_with_audit';
  } catch (_) { /* default stands */ }
  box.innerHTML = `
    <h4 style="margin:0;font-size:0.95rem;font-weight:700;">Refund approval</h4>
    <p style="margin:2px 0 8px;font-size:0.8rem;color:var(--text-secondary);">A refund or return counts only after a second billing login approves it. If your company has only one billing login, the owner can let that login approve its own (each one is flagged and logged).</p>
    <select id="de-refund-policy-select" ${isOwner ? '' : 'disabled'} onchange="window.APModule._saveRefundPolicy(this.value)" style="width:100%;padding:10px;border:1px solid var(--border-light);border-radius:8px;font-size:16px;min-height:44px;color:var(--text-primary);background:#fff;">
      <option value="second_person" ${cur === 'second_person' ? 'selected' : ''}>Needs a second person (recommended)</option>
      <option value="sole_with_audit" ${cur === 'sole_with_audit' ? 'selected' : ''}>Our only billing login may approve its own (flagged)</option>
    </select>
    ${isOwner ? '' : '<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">Only the owner can change this.</div>'}`;
}
async function _saveRefundPolicy(value) {
  try {
    const res = await _callExpenseFn('setExpenseRefundPolicy', { tenantId: APState.tenantId, soleApprover: value });
    window.showToast?.(res?.data?.changed === false ? 'No change.' : 'Refund approval rule saved', 'success');
  } catch (err) {
    console.error('[AP] setExpenseRefundPolicy', err);
    window.showToast?.(err?.message ? String(err.message) : 'Could not save the refund approval rule', 'error');
    _loadRefundPolicyBox();
  }
}

// ============================================
// TASK-9949 — RETURNS INBOX (employee return slips → a refund). The section
// lives in views/apReturns.js, loaded on first render (perf budget); this
// stub hands it what ap.js owns. Hidden while no slip is open.
// ============================================
let _rtMod = null;
let _rtLoading = null;
function _returnsInboxHtml() {
  if (APState.receiptsOnly) return '';
  if (_rtMod) return _rtMod.html();
  if (!_rtLoading) {
    _rtLoading = __ddChunkWrap(import('./apReturns.js?v=9949002')).then((m) => {
      m.init({ APState, escapeHtml, callFn: _callExpenseFn, promptText: _promptText });
      _rtMod = m;
      const el = document.getElementById('ap-returns-inbox');
      if (el) el.outerHTML = m.html();
    }).catch((e) => { console.warn('[AP] returns inbox did not load', e); _rtLoading = null; });
  }
  return '<div id="ap-returns-inbox"></div>';
}
// TASK-9949 (end)

// TASK-9948 — "Employee owes back" (refund to the employee on an expense
// already reimbursed). Lives in views/apOwesBack.js, loaded on first render
// (perf budget). Server-only balances, read through manageOwesBack.
let _obMod = null;
let _obLoading = null;
function _owesBackHtmlStub() {
  if (APState.receiptsOnly) return '';
  if (_obMod) return _obMod.html();
  if (!_obLoading) {
    _obLoading = __ddChunkWrap(import('./apOwesBack.js?v=9948001')).then((m) => {
      m.init({ APState, escapeHtml, money: _fmtSignedMoney, callFn: _callExpenseFn });
      _obMod = m;
      const el = document.getElementById('ap-owes-back');
      if (el) el.outerHTML = m.html();
    }).catch((e) => { console.warn('[AP] owes-back did not load', e); _obLoading = null; });
  }
  return '<div id="ap-owes-back"></div>';
}

// ============================================
// TASK-4866 — CARDS (credit / fuel card registry)
//
// WHY IT LIVES HERE. `tenants/{tid}.linesOfCredit[]` with `subtype:'credit_card'`
// is the app's one card registry. Three things read it — the driver/AP expense
// card picker (jobboard.js), reconciliation attribution, and the HR Employment
// tab's card names — and until TASK-4866 its ONLY editor was BI → Financing →
// "Debt Accounts". Measured 2026-09-10: 0 rows across all 95 tenants, while the
// dumb HR checkboxes it was meant to replace had collected 36 assignments. The
// data never got entered because it was filed under a word the person who holds
// the cards does not use. This panel is the same array, in AP, in plain English.
//
// NO SECOND STORE and NO local write path: every mutation goes through
// window.BIModule (pitfall #1 — never a cross-import with a `?v=` buster), which
// owns the transaction. Three editors now share one array field.
// ============================================

// Active-by-default view of the registry. Prefers BI's accessor (which prefers
// BI's live snapshot) and falls back to the tenant doc app.js caches at login —
// the SAME source jobboard.js and hr.js already read. NEVER throws: AP must not
// be able to go blank because the card list is unavailable.
function _apCardRegistry(includeArchived = false) {
  try {
    const viaBI = window.BIModule?.listCreditCards?.({ includeArchived });
    if (Array.isArray(viaBI)) return viaBI;
  } catch (_) { /* fall through */ }
  try {
    const arr = window.AppState?.tenantData?.linesOfCredit;
    return (Array.isArray(arr) ? arr : [])
      .filter(l => l && l.subtype === 'credit_card' && (includeArchived || !l.archived))
      .map(l => ({
        id: l.id, name: l.name || '', lastFourDigits: l.lastFourDigits || '',
        cardType: l.cardType === 'fuel' ? 'fuel' : 'credit', archived: !!l.archived,
        lender: l.lender || '', statementDate: l.statementDate || null,
        cardExpiryDate: l.cardExpiryDate || null,
      }));
  } catch (_) { return []; }
}

// Mirrors firestore.rules `isTenantAdmin` on the tenant-doc UPDATE, which is the
// REAL boundary on this array (`allow update: if isTenantAdmin(tenantId) && …`).
// The registry is READABLE by any tenant member, so a non-admin AP user still
// sees the list — they just get no Add / Edit / Retire affordances instead of a
// raw "Missing or insufficient permissions" after filling out a form.
function _apCanManageCards() {
  try {
    if (typeof window.hrEffectiveAdmin === 'function') return !!window.hrEffectiveAdmin();
    const st = window.AppState || {};
    if (st.isDemo || st.tenant === 'demo') return true;
    const ma = st.memberAccess;
    if (ma == null) return true; // legacy single-login owner — no member doc
    if (ma.status && ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
    return ma.role === 'owner' || ma.role === 'admin';
  } catch (_) { return false; }
}

function _apCardExpiryMeta(card) {
  const v = String(card?.cardExpiryDate || '');
  if (!/^\d{4}-\d{2}$/.test(v)) return null;
  const [y, m] = v.split('-').map(Number);
  // A card is good through the LAST day of its printed month.
  const end = new Date(y, m, 0, 23, 59, 59);
  const days = Math.ceil((end - new Date()) / 86400000);
  const label = end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  if (days < 0) return { label, text: `Expired ${label}`, color: '#991B1B', bg: '#FEE2E2' };
  if (days <= 60) return { label, text: `Expires ${label}`, color: '#92400E', bg: '#FEF3C7' };
  return { label, text: `Expires ${label}`, color: 'var(--text-secondary)', bg: 'transparent' };
}

function renderCardsPanel(area) {
  if (!area) return;
  const canManage = _apCanManageCards();
  const all = _apCardRegistry(true);
  const q = APState.searchQuery;
  const match = (c) => !q || (c.name || '').toLowerCase().includes(q)
    || (c.lender || '').toLowerCase().includes(q)
    || String(c.lastFourDigits || '').includes(q);
  const active = all.filter(c => !c.archived && match(c));
  const retired = all.filter(c => c.archived && match(c));

  let html = `
    <div class="ap-summary-bar">
      <div class="ap-summary-item">
        <span class="ap-summary-label">Active cards</span>
        <span class="ap-summary-value">${active.length}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Credit</span>
        <span class="ap-summary-value">${active.filter(c => c.cardType !== 'fuel').length}</span>
      </div>
      <div class="ap-summary-item">
        <span class="ap-summary-label">Fuel</span>
        <span class="ap-summary-value">${active.filter(c => c.cardType === 'fuel').length}</span>
      </div>
    </div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin:12px 0;">
      ${canManage ? `
        <button class="btn btn-primary" onclick="window.APModule.showCardModal()" style="padding:8px 14px;font-size:0.85rem;font-weight:600;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">add</span> Add Card
        </button>` : ''}
      <span style="font-size:0.78rem;color:var(--text-secondary);">Name the company's credit and fuel cards once here. The names show up on expense receipts, in Business Intelligence, and on each person's Employment tab in HR.</span>
    </div>`;

  if (!canManage) {
    html += `
      <div style="font-size:0.78rem;color:var(--text-secondary);background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:10px 12px;margin-bottom:12px;">
        <span class="material-icons" style="font-size:15px;vertical-align:middle;">lock</span>
        Only an account owner or admin can add or change cards. Ask them to set one up and it will appear here.
      </div>`;
  }

  if (!active.length && !retired.length) {
    html += `
      <div class="empty-state" style="padding:48px 20px;text-align:center;">
        <span class="material-icons empty-state-icon" style="font-size:56px;color:var(--text-muted);">credit_card</span>
        <div class="empty-state-title" style="margin-top:12px;">No cards yet</div>
        <div class="empty-state-text">${canManage
          ? 'Add the company credit card and the fuel card. Once they are named here, drivers can pick the right one on an expense and you can reconcile a statement against it.'
          : 'No company cards have been set up yet.'}</div>
      </div>`;
    area.innerHTML = html;
    return;
  }

  if (!active.length && q) {
    html += `<div style="padding:24px;text-align:center;color:var(--text-secondary);font-size:0.85rem;">No active cards match "${escapeHtml(q)}".</div>`;
  } else {
    html += active.map(c => renderCardRow(c, canManage)).join('');
  }

  if (retired.length) {
    html += `
      <div style="margin-top:20px;font-size:0.8rem;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Retired (${retired.length})</div>
      <div style="font-size:0.74rem;color:var(--text-secondary);margin:4px 0 8px;">Kept so past expenses charged to them still show the right card.</div>
      ${retired.map(c => renderCardRow(c, canManage)).join('')}`;
  }

  area.innerHTML = html;
}

function renderCardRow(card, canManage) {
  const isFuel = card.cardType === 'fuel';
  const typeLabel = isFuel ? 'Fuel Card' : 'Credit Card';
  const exp = _apCardExpiryMeta(card);
  const meta = [
    card.lastFourDigits ? '•••• ' + escapeHtml(String(card.lastFourDigits)) : 'No last 4 on file',
    card.lender ? escapeHtml(card.lender) : '',
    card.statementDate ? `Statement day ${Number(card.statementDate)}` : '',
  ].filter(Boolean).join(' · ');
  // `escJsAttr` — the card NAME is tenant-typed and these are JS string literals
  // inside an HTML attribute, where no HTML escaper is sufficient (an apostrophe
  // in "O'Brien Fuel" would kill the handler outright). Ids are UUIDs, but they
  // go through the same helper so the pattern can't rot.
  const jsId = (window.escJsAttr ? window.escJsAttr(card.id) : String(card.id || ''));
  return `
    <div style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:12px 14px;display:flex;align-items:center;gap:12px;margin-bottom:8px;${card.archived ? 'opacity:0.6;' : ''}">
      <span class="material-icons" style="font-size:26px;color:${isFuel ? '#D97706' : '#0891B2'};flex-shrink:0;">${isFuel ? 'local_gas_station' : 'credit_card'}</span>
      <div style="flex:1;min-width:0;">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
          <span style="font-weight:600;font-size:0.92rem;color:var(--text-primary);">${escapeHtml(card.name || 'Unnamed card')}</span>
          <span style="font-size:0.68rem;font-weight:700;padding:2px 8px;border-radius:999px;background:${isFuel ? '#FEF3C7' : '#CFFAFE'};color:${isFuel ? '#92400E' : '#155E75'};">${typeLabel}</span>
          ${card.archived ? '<span style="font-size:0.68rem;font-weight:700;padding:2px 8px;border-radius:999px;background:var(--bg-elevated);color:var(--text-secondary);">Retired</span>' : ''}
          ${exp && exp.bg !== 'transparent' ? `<span style="font-size:0.68rem;font-weight:700;padding:2px 8px;border-radius:999px;background:${exp.bg};color:${exp.color};">${escapeHtml(exp.text)}</span>` : ''}
        </div>
        <div style="font-size:0.76rem;color:var(--text-secondary);margin-top:3px;">${meta}${exp && exp.bg === 'transparent' ? ' · ' + escapeHtml(exp.text) : ''}</div>
      </div>
      ${canManage ? `
        <div style="display:flex;gap:6px;flex-shrink:0;">
          <button class="btn btn-sm btn-secondary" onclick="window.APModule.showCardModal('${jsId}')" title="Edit this card" style="padding:6px 10px;font-size:0.78rem;">Edit</button>
          ${card.archived
            ? `<button class="btn btn-sm btn-secondary" onclick="window.APModule.restoreCard('${jsId}')" title="Put this card back in use" style="padding:6px 10px;font-size:0.78rem;">Restore</button>`
            : `<button class="btn btn-sm btn-secondary" onclick="window.APModule.retireCard('${jsId}')" title="Stop offering this card on new expenses" style="padding:6px 10px;font-size:0.78rem;">Retire</button>`}
        </div>` : ''}
    </div>`;
}

function showCardModal(id) {
  if (!_apCanManageCards()) {
    window.showToast?.('Only an account owner or admin can add or change cards', 'error');
    return;
  }
  const card = id ? _apCardRegistry(true).find(c => c.id === id) : null;
  if (id && !card) { window.showToast?.('That card is no longer available', 'error'); return; }
  const c = card || { id: '', name: '', cardType: 'credit', lastFourDigits: '', lender: '', statementDate: '', cardExpiryDate: '' };

  document.getElementById('ap-card-modal')?.remove();
  const modal = document.createElement('div');
  modal.id = 'ap-card-modal';
  modal.className = 'modal';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  const jsId = (window.escJsAttr ? window.escJsAttr(c.id || '') : String(c.id || ''));
  modal.innerHTML = `
    <div class="modal-content" style="max-width:460px;">
      <div class="modal-header">
        <h3 style="margin:0;">${card ? 'Edit' : 'Add'} Card</h3>
        <button class="btn-icon" onclick="document.getElementById('ap-card-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
        <div class="form-group">
          <label>Card name *</label>
          <input type="text" id="ap-card-name" class="form-control" maxlength="60" value="${escapeHtml(c.name)}" placeholder="Chase Visa, Sunoco Fleet…">
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">What people here call it. This is the name drivers see when they pick a card on an expense.</div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label>Used for</label>
            <select id="ap-card-type" class="form-control">
              <option value="credit" ${c.cardType !== 'fuel' ? 'selected' : ''}>Company Credit Card</option>
              <option value="fuel" ${c.cardType === 'fuel' ? 'selected' : ''}>Fuel Card</option>
            </select>
          </div>
          <div class="form-group">
            <label>Last 4 digits</label>
            <input type="text" id="ap-card-last4" class="form-control" maxlength="4" inputmode="numeric" value="${escapeHtml(String(c.lastFourDigits || ''))}" placeholder="1234">
          </div>
        </div>
        <div class="form-group">
          <label>Issuer</label>
          <input type="text" id="ap-card-lender" class="form-control" maxlength="60" value="${escapeHtml(c.lender || '')}" placeholder="Chase, Sunoco, WEX…">
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label>Statement day</label>
            ${/* TASK-4866 (COD Code Reviewer 🔴) — Number(), not the raw value.
                 `statementDate` is read back off the tenant doc, and the fallback
                 registry reader hands it through untouched; a raw-SDK write could
                 put a string carrying a quote there and break out of this
                 attribute. A day-of-month is a number or it is nothing. */''}
            <input type="number" id="ap-card-stmt" class="form-control" min="1" max="31" value="${Number(c.statementDate) || ''}" placeholder="15">
          </div>
          <div class="form-group">
            <label>Card expires</label>
            <input type="month" id="ap-card-expiry" class="form-control" value="${escapeHtml(c.cardExpiryDate || '')}">
          </div>
        </div>
        <div style="font-size:0.72rem;color:var(--text-secondary);border-top:1px solid var(--border-light);padding-top:10px;margin-top:4px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">shield</span>
          Never enter a full card number here. Only the last 4 digits are stored, and only the last 4 are ever kept.
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="document.getElementById('ap-card-modal').remove()">Cancel</button>
        <button class="btn btn-primary" id="ap-card-save" onclick="window.APModule.saveCardFromModal('${jsId}')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">save</span> Save
        </button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  setTimeout(() => document.getElementById('ap-card-name')?.focus(), 40);
}

async function saveCardFromModal(existingId) {
  const nameEl = document.getElementById('ap-card-name');
  const name = (nameEl?.value || '').trim();
  if (!name) { window.showToast?.('Give the card a name', 'error'); nameEl?.focus(); return; }

  const raw4 = String(document.getElementById('ap-card-last4')?.value || '');
  // Refuse a full PAN outright rather than silently truncating it. Truncation is
  // the right STORAGE rule (BIModule.saveCard enforces it regardless), but a
  // person who pasted 16 digits needs to be told the app does not want them.
  if (raw4.replace(/\D/g, '').length > 4) {
    window.showToast?.('Enter only the last 4 digits, not the full card number', 'error');
    return;
  }

  const btn = document.getElementById('ap-card-save');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.6'; }

  const res = await (window.BIModule?.saveCard?.(APState.tenantId, {
    id: existingId || null,
    name,
    cardType: document.getElementById('ap-card-type')?.value === 'fuel' ? 'fuel' : 'credit',
    lastFourDigits: raw4,
    lender: document.getElementById('ap-card-lender')?.value || '',
    statementDate: document.getElementById('ap-card-stmt')?.value || '',
    cardExpiryDate: document.getElementById('ap-card-expiry')?.value || '',
  }) ?? { ok: false, code: 'accessor-missing' });

  if (btn) { btn.disabled = false; btn.style.opacity = ''; }
  if (!res.ok) {
    // TASK-4866 (COD Code Reviewer 🔴) — `pan-in-text` is the refusal for a full
    // card number typed into Card name or Issuer. Those are free-text fields on
    // the driver-readable tenant doc, so the save is refused outright rather
    // than scrubbed; say which box to fix or the person just retypes it.
    window.showToast?.(
      res.code === 'pan-in-text' ? 'Remove the full card number from the name or issuer — only the last 4 digits are stored'
      : res.code === 'permission-denied' ? 'Only an account owner or admin can change cards'
      : res.code === 'not-found' ? 'That card was removed by someone else — close this and add it again'
      : 'Could not save the card — please try again', 'error');
    // Someone else changed the registry under us; re-render behind the modal so
    // the list stops disagreeing with what just happened.
    if (res.code === 'not-found') renderAPView();
    return;
  }
  document.getElementById('ap-card-modal')?.remove();
  window.showToast?.(res.created ? 'Card added' : 'Card saved', 'success');
  renderAPView();
}

async function retireCard(id) {
  const card = _apCardRegistry(true).find(c => c.id === id);
  if (!card) return;
  if (!confirm(`Retire "${card.name || 'this card'}"?\n\nIt stops showing up on new expenses. Past expenses charged to it are not touched, and you can restore it any time.`)) return;
  await _setCardArchived(id, true, 'Card retired');
}

async function restoreCard(id) {
  await _setCardArchived(id, false, 'Card restored');
}

async function _setCardArchived(id, archived, okMsg) {
  const res = await (window.BIModule?.setCardArchived?.(APState.tenantId, id, archived)
    ?? { ok: false, code: 'accessor-missing' });
  if (!res.ok) {
    window.showToast?.(
      res.code === 'permission-denied' ? 'Only an account owner or admin can change cards'
      : res.code === 'not-found' ? 'That card is no longer in the list — refreshing'
      : 'Could not update the card — please try again', 'error');
    // A not-found means someone else changed the registry under us; re-render so
    // the list stops disagreeing with what just happened.
    if (res.code === 'not-found') renderAPView();
    return;
  }
  window.showToast?.(okMsg, 'success');
  renderAPView();
}

window.APModule = {
  renderAPView: renderAPViewInContainer,
  openStockIn, // TASK-9724 S3

  openAdminExpense,
  // TASK-9367 — Refund / Credit
  toggleRecordMenu,
  openReceiptInbox, // TASK-9771
  openVendorCredit,
  closeVendorCredit,
  saveVendorCredit,
  // TASK-9947 — refund approvals + where the money went
  decideRefund,
  pushRefundToQbo, // TASK-9952 slice B
  _vcRefundToChanged,
  _vcConfirmSeparate,
  _vcItemsChanged, // TASK-9950
  _saveRefundPolicy,
  showNewInvoice,
  editInvoice,
  closeInvoiceModal,
  saveInvoice,
  deleteInvoice,
  switchFilter,
  onSearch,
  toggleVendor,
  // Line items
  addLineItem,
  removeLineItem,
  updateLineItem,
  // Vendor
  showAddVendorInline,
  saveNewVendor,
  editVendor, // TASK-9793
  onVendorChange,
  // TASK-9684 — "Other payees" finder in the bill's vendor picker
  togglePayeeFinder,
  renderPayeeResults,
  pickPayee,
  onInvoiceDateChange,
  // PO
  autoGenPO,
  // Payment
  setPaymentStatus,
  setPaymentMethod,
  // Documents
  handleDocUpload,
  removeDoc,
  // Check printing
  printCheck,
  batchPrintCheck,
  voidCheck,
  exportCheckRegister,
  // Bank setup
  showBankSetup,
  closeBankSetup,
  saveBankSetup,
  hasCheckPrintingSetup,
  // QB IIF Export
  exportAPBillsIIF,
  exportAPPaymentIIF,
  // Categories
  addCategory,
  // Driver Expenses
  toggleDriverGroup,
  toggleDriverExpenseSelect,
  selectAllPendingDriver,
  clearDriverSelection,
  approveDriverExpense,
  rejectDriverExpense,
  askDriverClarify,
  markReimbursed,
  correctReimbursement, // TASK-9778 (approve-txn follow-up)
  markExpensePersonal, // TASK-9917
  clearExpensePersonal, // TASK-9917
  answerOwnerDraw, // TASK-9917 — owner path
  recordPersonalRepayment, // TASK-9917 slice 2
  undoPersonalRepayment, // TASK-9917 slice 2
  // TASK-9592-FU — Re-allocate
  openReallocateExpense,
  closeReallocateExpense,
  saveReallocateExpense,
  _reallocSync,
  bulkApproveDriver,
  consolidateDriverExpenses,
  openDriverExpenseSettings,
  _showDriverExpenseSettingsModal,
  _saveDriverExpenseSettings,
  _addCostAllocation,
  _toggleCostAllocation,
  _renameCostAllocation,
  _seedCostAllocations,
  // TASK-4866 — Cards (credit / fuel card registry, shared with BI + HR)
  showCardModal,
  saveCardFromModal,
  retireCard,
  restoreCard,
  // Categories CRUD (tenant-scoped)
  _addExpenseCategory,
  _removeExpenseCategory,
  _resetExpenseCategories,
};

export default { initAP };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
